import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "@google/generative-ai";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";
import type { HFAnalysisResult } from "./backendAIService";
import { checkContentSafety, type SafetyCheckResult } from "@/utils/contentSafetyFilter";
import {
  createGoogleSearchService,
  getGoogleSearchService,
  type GoogleSearchService,
} from "./googleSearchService";

// Lazy getter for backendAIService to avoid circular dependency
let _backendAIServiceCache: any = null;
async function getBackendAIService() {
  if (!_backendAIServiceCache) {
    const { backendAIService } = await import("./backendAIService");
    _backendAIServiceCache = backendAIService;
  }
  return _backendAIServiceCache;
}

// API key configuration - MUST be set in environment variables
// ⚠️ DO NOT hardcode API keys here - they will be exposed in the browser!
const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

if (!API_KEY) {
  console.error(
    "❌ VITE_GEMINI_API_KEY not set - Gemini features (summaries, flashcards, post-tests) will not work."
  );
  console.info("💡 For local development: Add VITE_GEMINI_API_KEY to .env.local");
  console.info(
    "🔒 For production: This service should be migrated to use geminiProxyService for secure API access"
  );
  console.info("📚 Migration guide: AI_SECURITY_MIGRATION.md");
}

// Lazy initialization to avoid circular dependency issues
let genAI: GoogleGenerativeAI | null = null;

function getGenAI(): GoogleGenerativeAI {
  if (!genAI) {
    genAI = new GoogleGenerativeAI(API_KEY);
  }
  return genAI;
}

// Google Search API configuration (used by backend proxy)
const GOOGLE_SEARCH_API_KEY = import.meta.env.VITE_GOOGLE_SEARCH_API_KEY;
const GOOGLE_SEARCH_ENGINE_ID = import.meta.env.VITE_GOOGLE_SEARCH_ENGINE_ID;

export interface SummaryResult {
  summary: string;
  keyPoints: string[];
  topic: string;
  subject: string;
  sources: string[];
  correctionsApplied?: Array<{
    userClaim: string;
    correction: string;
    sourceUrl: string;
    severity: string;
  }>;
}

export interface ProgressiveResult {
  phase: "basic" | "enhanced";
  result: SummaryResult | EnhancedSummaryResult;
  processingTime: number;
  confidence: "low" | "medium" | "high";
  improvements?: {
    sourcesAdded: number;
    accuracyBoost: string;
    hfInsights: boolean;
  };
}

export interface FlashcardResult {
  flashcards: Flashcard[];
  topic: string;
  subject: string;
  sources: string[];
  correctionsApplied?: Array<{
    userClaim: string;
    correction: string;
    sourceUrl: string;
    severity: string;
  }>;
}

export interface Flashcard {
  front: string;
  back: string;
}

export interface PostTestQuestion {
  question: string;
  options: string[];
  correctAnswer: number; // Index of correct answer (0-3)
  explanation: string;
}

export interface PostTestResult {
  questions: PostTestQuestion[];
  topic: string;
  subject: string;
  sources: string[];
  correctionsApplied?: Array<{
    userClaim: string;
    correction: string;
    sourceUrl: string;
    severity: string;
  }>;
}

export interface EnhancedSummaryResult extends SummaryResult {
  hfAnalysis?: HFAnalysisResult;
  processingMetrics?: {
    totalTime: number;
    hfTime: number;
    geminiTime: number;
    searchTime: number;
  };
  enhancementStatus: "standard" | "hf_enhanced" | "hf_fallback";
}

export interface LanguageValidationResult {
  isValid: boolean;
  detectedLanguages: {
    english: number;
    filipino: number;
  };
  primaryLanguage: "english" | "filipino" | "mixed" | "unknown";
  requiresUserChoice: boolean;
  message?: string;
}

export interface SubjectRelevanceResult {
  isRelevant: boolean;
  relevanceScore: number; // 0-100 percentage
  detectedSubject: string;
  expectedSubject: string;
  confidence: "low" | "medium" | "high";
  message?: string;
}

export class GeminiService {
  private _model: ReturnType<GoogleGenerativeAI["getGenerativeModel"]> | null = null;
  private _flashcardModel: ReturnType<GoogleGenerativeAI["getGenerativeModel"]> | null = null;

  private get model() {
    if (!this._model) {
      this._model = getGenAI().getGenerativeModel({
        model: "gemini-2.5-pro",
        generationConfig: {
          temperature: 0.3,
          topP: 0.95,
          topK: 64,
          maxOutputTokens: 8192,
        },
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
      });
    }
    return this._model;
  }

  private get flashcardModel() {
    if (!this._flashcardModel) {
      this._flashcardModel = getGenAI().getGenerativeModel({
        model: "gemini-2.5-flash",
        generationConfig: {
          temperature: 0.4, // Balanced temperature for varied yet accurate content
          topP: 0.9,
          topK: 50,
          maxOutputTokens: 6144, // Increased for more detailed answers
          responseMimeType: "application/json", // ✅ Force valid JSON output
        },
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
      });
    }
    return this._flashcardModel;
  }

  private googleSearchService?: GoogleSearchService;

  // Simple cache to avoid repeated searches (saves money!)
  private searchCache = new Map<string, string[]>();

  constructor() {
    // Initialize Google Search Service asynchronously (don't await in constructor)
    this.initializeGoogleSearch().catch((error) => {
      logError("Failed to initialize Google Search Service in constructor", error);
    });
  }

  /**
   * Timeout wrapper for external API calls
   * Prevents indefinite hanging on API failures
   */
  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operationName: string
  ): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${operationName} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]);
  }

  /**
   * Check if a URL is a duplicate based on normalized hostname and pathname
   * Ignores query parameters and fragments for more accurate deduplication
   */
  private isDuplicateUrl(newUrl: string, existingUrls: string[]): boolean {
    try {
      // Normalize the new URL
      const normalizedNew = this.normalizeUrl(newUrl);

      // Check against all existing URLs
      return existingUrls.some((existingUrl) => {
        const normalizedExisting = this.normalizeUrl(existingUrl);
        return normalizedNew === normalizedExisting;
      });
    } catch (error) {
      // If URL parsing fails, fall back to simple string comparison
      return existingUrls.some((url) => url.includes(newUrl) || newUrl.includes(url));
    }
  }

  /**
   * Validate language of content and determine if user input is required
   * Returns validation result with language percentages and user choice requirement
   */
  async validateContentLanguage(content: string): Promise<LanguageValidationResult> {
    try {
      logDebug("Starting language validation", { contentLength: content.length });

      // Extract only text content (remove formulas, symbols, special characters)
      const textOnlyContent = this.extractTextForLanguageAnalysis(content);

      logDebug("Text extraction for language analysis", {
        originalLength: content.length,
        textOnlyLength: textOnlyContent.length,
      });

      // If content is mostly non-text (formulas, symbols), accept it as English by default
      if (textOnlyContent.trim().length < 20) {
        logInfo(
          "Content is mostly non-text (formulas/symbols/numbers), accepting as valid English content"
        );
        return {
          isValid: true,
          detectedLanguages: { english: 100, filipino: 0 },
          primaryLanguage: "english",
          requiresUserChoice: false,
        };
      }

      // Use Backend HF Analysis for accurate language detection on text-only content
      // Skip subject detection here as we only need language validation
      const backendAIService = await getBackendAIService();
      const hfAnalysis: HFAnalysisResult = await backendAIService.analyzeContent(
        textOnlyContent,
        true
      );

      logDebug("Backend HF language analysis completed", {
        language: hfAnalysis.language,
        confidence: hfAnalysis.languageConfidence,
      });

      // ✅ CRITICAL: Trust HF when confidence is high (>= 80%)
      // High confidence from HF means we should skip keyword analysis
      if (hfAnalysis.languageConfidence >= 80) {
        const hfLang = hfAnalysis.language.toLowerCase();

        if (hfLang === "english") {
          logSuccess("HF detected pure English with high confidence - no user choice needed", {
            language: hfAnalysis.language,
            confidence: hfAnalysis.languageConfidence,
          });
          return {
            isValid: true,
            detectedLanguages: { english: 100, filipino: 0 },
            primaryLanguage: "english",
            requiresUserChoice: false,
          };
        } else if (hfLang === "filipino") {
          logSuccess("HF detected pure Filipino with high confidence - no user choice needed", {
            language: hfAnalysis.language,
            confidence: hfAnalysis.languageConfidence,
          });
          return {
            isValid: true,
            detectedLanguages: { english: 0, filipino: 100 },
            primaryLanguage: "filipino",
            requiresUserChoice: false,
          };
        } else if (hfLang === "taglish") {
          logInfo("HF detected Taglish with high confidence - user choice required", {
            language: hfAnalysis.language,
            confidence: hfAnalysis.languageConfidence,
          });
          // Calculate percentages for display purposes
          const languagePercentages = this.calculateLanguagePercentages(
            textOnlyContent,
            hfAnalysis
          );
          return {
            isValid: true,
            detectedLanguages: languagePercentages,
            primaryLanguage: "mixed",
            requiresUserChoice: true,
            message: `Your content is detected as Taglish (mixed English and Filipino). Please choose your preferred output language:`,
          };
        }
      }

      // Calculate language percentages using more sophisticated analysis
      // ✅ CRITICAL: Always calculate percentages first, even if HF says "Taglish"
      // This prevents false positives where HF incorrectly detects Taglish for pure Filipino/English
      const languagePercentages = this.calculateLanguagePercentages(textOnlyContent, hfAnalysis);

      logDebug("Language percentages calculated", languagePercentages);

      // Determine primary language and validation status
      const englishPercent = languagePercentages.english;
      const filipinoPercent = languagePercentages.filipino;

      // Check for unsupported languages - BUT be lenient with low percentages
      // Low percentages could mean content has formulas/symbols mixed with text
      if (
        hfAnalysis.language === "unknown" &&
        englishPercent < 5 &&
        filipinoPercent < 5 &&
        hfAnalysis.languageConfidence < 30
      ) {
        logWarn("Truly unsupported language detected with high confidence", {
          hfLanguage: hfAnalysis.language,
          confidence: hfAnalysis.languageConfidence,
        });
        return {
          isValid: false,
          detectedLanguages: languagePercentages,
          primaryLanguage: "unknown",
          requiresUserChoice: false,
          message:
            "Invalid language detected. This AI note processor can only handle English, Filipino, or both. Please try again with supported languages.",
        };
      }

      // If percentages are low, assume it's technical content with English context
      if (englishPercent < 10 && filipinoPercent < 10) {
        logInfo(
          "Low language percentages detected - likely technical content with formulas/symbols, defaulting to English"
        );
        return {
          isValid: true,
          detectedLanguages: { english: 100, filipino: 0 },
          primaryLanguage: "english",
          requiresUserChoice: false,
        };
      }

      // ✅ NEW LOGIC: Detect Taglish (mixed language) with more lenient thresholds
      // Pure English: >= 90% English (dominant)
      // Pure Filipino: >= 90% Filipino (dominant)
      // Anything else = Taglish (requires user choice)
      //
      // ✅ FIX: Use simple dominance check (>= 90%) instead of checking both percentages
      // This prevents issues where one language is 100% but still fails the dual condition

      const isPureEnglish = englishPercent >= 90;
      const isPureFilipino = filipinoPercent >= 90;

      if (isPureEnglish) {
        logSuccess("Pure English detected - output will be in English", {
          english: `${englishPercent}%`,
          filipino: `${filipinoPercent}%`,
        });
        return {
          isValid: true,
          detectedLanguages: languagePercentages,
          primaryLanguage: "english",
          requiresUserChoice: false,
        };
      }

      if (isPureFilipino) {
        logSuccess("Pure Filipino detected - output will be in Filipino", {
          english: `${englishPercent}%`,
          filipino: `${filipinoPercent}%`,
        });
        return {
          isValid: true,
          detectedLanguages: languagePercentages,
          primaryLanguage: "filipino",
          requiresUserChoice: false,
        };
      }

      // If we reach here, it's mixed language (Taglish) - user must choose
      logInfo("Taglish (mixed language) detected - user choice required", {
        english: `${englishPercent}%`,
        filipino: `${filipinoPercent}%`,
      });
      return {
        isValid: true,
        detectedLanguages: languagePercentages,
        primaryLanguage: "mixed",
        requiresUserChoice: true,
        message: `Your content contains both English (${englishPercent}%) and Filipino (${filipinoPercent}%). Please choose your preferred output language:`,
      };
    } catch (error) {
      logError("Language validation error", error);
      // Fallback to English
      return {
        isValid: true,
        detectedLanguages: { english: 100, filipino: 0 },
        primaryLanguage: "english",
        requiresUserChoice: false,
      };
    }
  }

  /**
   * Calculate language percentages using keyword analysis + HF insights
   */
  private calculateLanguagePercentages(
    content: string,
    hfAnalysis: HFAnalysisResult
  ): { english: number; filipino: number } {
    const text = content.toLowerCase();
    const words = text.split(/\s+/).filter((w) => w.length > 0);
    const totalWords = words.length;

    if (totalWords === 0) {
      return { english: 0, filipino: 0 };
    }

    // Filipino/Tagalog markers (STRICT - only uniquely Filipino words, 3+ characters)
    const filipinoMarkers = [
      // Common words (3+ chars, uniquely Filipino)
      "ang",
      "mga",
      "hindi",
      "ako",
      "ikaw",
      "siya",
      "kami",
      "kayo",
      "sila",
      // Question words
      "ano",
      "sino",
      "saan",
      "kailan",
      "paano",
      "bakit",
      // Verbs
      "dapat",
      "kailangan",
      "gusto",
      "wala",
      // Common Filipino words
      "tayo",
      "natin",
      "namin",
      "ninyo",
      "nila",
      "yung",
      "yun",
      "yan",
      "ito",
      "iyan",
      "iyon",
      "kung",
      "pero",
      "kasi",
      "lang",
      "din",
      "rin",
    ];

    // English markers (common words that are uniquely English)
    const englishMarkers = [
      "the",
      "is",
      "are",
      "was",
      "were",
      "have",
      "has",
      "had",
      "will",
      "would",
      "should",
      "could",
      "can",
      "might",
      "what",
      "where",
      "when",
      "why",
      "how",
      "who",
      "which",
      "this",
      "that",
      "these",
      "those",
      "with",
      "from",
      "about",
      "into",
      "their",
      "there",
      "they",
      "them",
      "been",
      "being",
    ];

    let filipinoCount = 0;
    let englishCount = 0;

    words.forEach((word) => {
      const cleanWord = word.replace(/[^\w]/g, "");
      if (filipinoMarkers.includes(cleanWord)) {
        filipinoCount++;
      }
      if (englishMarkers.includes(cleanWord)) {
        englishCount++;
      }
    });

    // Calculate raw percentages
    let filipinoPercent = Math.round((filipinoCount / totalWords) * 100);
    let englishPercent = Math.round((englishCount / totalWords) * 100);

    // Use HF analysis to boost confidence in detected language
    if (hfAnalysis.language === "filipino" && hfAnalysis.languageConfidence > 70) {
      filipinoPercent = Math.max(filipinoPercent, 60);
    } else if (hfAnalysis.language === "english" && hfAnalysis.languageConfidence > 70) {
      englishPercent = Math.max(englishPercent, 60);
    }

    // Normalize to ensure they add up reasonably
    const total = filipinoPercent + englishPercent;
    if (total > 100) {
      filipinoPercent = Math.round((filipinoPercent / total) * 100);
      englishPercent = Math.round((englishPercent / total) * 100);
    }

    // Ensure at least 100% total for display purposes
    if (total < 100) {
      const remaining = 100 - total;
      if (filipinoPercent > englishPercent) {
        filipinoPercent += remaining;
      } else {
        englishPercent += remaining;
      }
    }

    return { english: englishPercent, filipino: filipinoPercent };
  }

  /**
   * Extract only text content for language analysis
   * Filters out formulas, symbols, numbers, and special characters
   * This allows language detection to focus on actual words, not mathematical/scientific notation
   */
  private extractTextForLanguageAnalysis(content: string): string {
    // Split into words and filter to keep only words with letters
    const words = content.split(/\s+/);

    const textWords = words.filter((word) => {
      // Remove leading/trailing punctuation and special characters
      const cleaned = word.replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");

      // Keep words that have at least 2 letters (case-insensitive Unicode)
      // This filters out pure numbers, symbols, and single-character variables
      const hasLetters = /\p{L}{2,}/u.test(cleaned);

      return hasLetters;
    });

    const extractedText = textWords.join(" ");

    logDebug("Text extraction for language analysis", {
      originalWordCount: words.length,
      textWordCount: textWords.length,
      filterRatio: `${Math.round((textWords.length / words.length) * 100)}%`,
    });

    return extractedText;
  }

  /**
   * Helper function to check if sources contain real URLs
   */
  private hasRealUrls(sources: string[]): boolean {
    if (!sources || sources.length === 0) return false;

    const urlPattern = /^https?:\/\//i;
    const genericFallbacks = [
      "AI-generated",
      "verify with",
      "authoritative sources",
      "content - verify",
      "flashcards - verify",
    ];

    const realUrlCount = sources.filter((source) => {
      // Check if it's a URL
      if (!urlPattern.test(source)) return false;

      // Check if it contains generic fallback text
      const hasGenericText = genericFallbacks.some((fallback) =>
        source.toLowerCase().includes(fallback.toLowerCase())
      );

      return !hasGenericText;
    }).length;

    logDebug("Real URL check", {
      totalSources: sources.length,
      realUrls: realUrlCount,
      hasRealUrls: realUrlCount > 0,
    });

    return realUrlCount > 0;
  }

  private async initializeGoogleSearch() {
    try {
      // Initialize Google Search Service (uses backend proxy for security)
      // NOTE: SafeSearch must be configured in Google Custom Search Engine settings
      // at https://programmablesearchengine.google.com/
      if (GOOGLE_SEARCH_API_KEY && GOOGLE_SEARCH_ENGINE_ID) {
        this.googleSearchService = createGoogleSearchService({
          apiKey: GOOGLE_SEARCH_API_KEY,
          searchEngineId: GOOGLE_SEARCH_ENGINE_ID,
        });

        // Initialize asynchronously (non-blocking)
        this.googleSearchService
          .initialize()
          .then(() => {
            logSuccess(
              "✅ Google Search Service initialized successfully - SafeSearch configured",
              {
                hasApiKey: !!GOOGLE_SEARCH_API_KEY,
                hasEngineId: !!GOOGLE_SEARCH_ENGINE_ID,
                safeSearchNote:
                  "SafeSearch must be enabled in Google CSE settings (programmablesearchengine.google.com)",
                searchQualityNote:
                  "All search queries include negative keywords to filter inappropriate content",
              }
            );
          })
          .catch((error: any) => {
            logError("Failed to initialize Google Search Service", error);
          });
      } else {
        logInfo("ℹ️ Google Search API keys in backend only (secure mode)", {
          frontendKeys: "Not exposed to browser (for security)",
          backendProxy: "Google Search handled server-side",
          status: "✅ Search functionality still works through backend",
          note: "This is the CORRECT configuration - API keys secured on backend",
        });
        // Note: Google Search will be handled by backend when needed
        // Frontend doesn't need direct access to Google Search API
      }
    } catch (error) {
      logError("Error initializing Google Search Service", error);
    }
  }

  /**
   * Search credible sources using Google Search Service (backend proxy)
   * Delegates to the extracted GoogleSearchService for better performance
   */
  private async searchCredibleSources(topics: string[], subject?: string): Promise<string[]> {
    if (!this.googleSearchService) {
      logDebug("Google Search Service not available, skipping source search");
      return [];
    }

    try {
      return await this.googleSearchService.searchCredibleSources(topics, subject);
    } catch (error) {
      logError("Error searching credible sources", { topics, subject, error });
      return [];
    }
  }

  /**
   * CRITICAL: Validate that uploaded content is relevant to the session's subject
   * Prevents mismatched content (e.g., Math notes for Science session) from entering the pipeline
   * Uses Gemini 2.5-Pro to analyze content and compare with expected subject
   *
   * @param content - The text content to validate
   * @param expectedSubject - The subject from the completed session (e.g., "Mathematics", "Science")
   * @returns SubjectRelevanceResult with relevance score and validation details
   */
  async validateSubjectRelevance(
    content: string,
    expectedSubject: string
  ): Promise<SubjectRelevanceResult> {
    try {
      logInfo("Starting subject relevance validation", {
        contentLength: content.length,
        expectedSubject,
      });

      // Extract a sample of the content for analysis (first 3000 characters for efficiency)
      const contentSample = content.substring(0, 3000);

      const prompt = `You are an educational content analyzer. Analyze the following content and determine its academic subject.

EXPECTED SUBJECT: ${expectedSubject}

CONTENT TO ANALYZE:
${contentSample}

Carefully analyze the content and determine:
1. What is the PRIMARY academic subject of this content? (e.g., Mathematics, Science, Physics, Chemistry, Biology, English, History, etc.)
2. How confident are you that this content matches the expected subject "${expectedSubject}"?
3. Calculate a relevance score (0-100) where:
   - 90-100: Perfect match (content is clearly about ${expectedSubject})
   - 70-89: Good match (content is related to ${expectedSubject} with some overlap)
   - 50-69: Partial match (content touches on ${expectedSubject} but includes other subjects)
   - 30-49: Weak match (content is vaguely related but mostly about other topics)
   - 0-29: No match (content is about a completely different subject)

IMPORTANT CONSIDERATIONS:
- If the content contains formulas, equations, or technical terms, consider their context
- Word dumps or random text should be flagged as irrelevant (score 0-20)
- Cross-subject content (e.g., Math in Physics) should be evaluated based on primary focus
- Generic study materials without clear subject focus should score 40-60

Respond in JSON format:
{
  "detectedSubject": "The primary subject detected",
  "relevanceScore": 0-100,
  "confidence": "low" | "medium" | "high",
  "reasoning": "Brief explanation of why this score was given",
  "isRelevant": true if score >= 60, false otherwise
}`;

      const result = await this.model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      logDebug("Gemini subject relevance analysis response", {
        rawResponse: text.substring(0, 500),
      });

      // Parse JSON response (handle both raw JSON and markdown code blocks)
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logError("Failed to parse subject relevance response - no JSON found");
        // Return permissive result if parsing fails
        return {
          isRelevant: true,
          relevanceScore: 50,
          detectedSubject: "Unknown",
          expectedSubject,
          confidence: "low",
          message: "Unable to validate subject relevance - proceeding with caution",
        };
      }

      const analysis = JSON.parse(jsonMatch[0]);

      logInfo("Subject relevance validation completed", {
        detectedSubject: analysis.detectedSubject,
        expectedSubject,
        relevanceScore: analysis.relevanceScore,
        confidence: analysis.confidence,
        isRelevant: analysis.isRelevant,
        reasoning: analysis.reasoning,
      });

      // Construct detailed message based on relevance score
      let message = "";
      if (!analysis.isRelevant) {
        if (analysis.relevanceScore < 30) {
          message = `Content mismatch detected! Your uploaded content appears to be about "${analysis.detectedSubject}", but this session is for "${expectedSubject}". Please upload notes that match the session subject.`;
        } else if (analysis.relevanceScore < 50) {
          message = `Weak subject match. The content seems to focus more on "${analysis.detectedSubject}" rather than "${expectedSubject}". Consider uploading more relevant notes.`;
        } else {
          message = `Partial subject match. The content includes some "${expectedSubject}" material but also covers other topics like "${analysis.detectedSubject}". For best results, use notes focused on "${expectedSubject}".`;
        }
      }

      return {
        isRelevant: analysis.isRelevant,
        relevanceScore: analysis.relevanceScore,
        detectedSubject: analysis.detectedSubject,
        expectedSubject,
        confidence: analysis.confidence,
        message: message || undefined,
      };
    } catch (error) {
      logError("Error validating subject relevance:", error);

      // Return permissive result on error to avoid blocking valid content
      return {
        isRelevant: true,
        relevanceScore: 50,
        detectedSubject: "Unknown",
        expectedSubject,
        confidence: "low",
        message: "Unable to validate subject relevance - proceeding with caution",
      };
    }
  }

  /**
   * NEW: Validate if uploaded materials match user's learning goals/context
   * Detects mismatches like "business math PDFs" uploaded when user wants to learn "intermolecular forces"
   *
   * @param userContext - What the user wants to learn (e.g., "di ko naintindihan yung intermolecular forces")
   * @param materialContent - Extracted text from uploaded files
   * @param sessionSubject - The session subject for additional context
   * @returns ContextMaterialRelevanceResult with match score and validation details
   */
  async validateContextMaterialRelevance(
    userContext: string,
    materialContent: string,
    sessionSubject: string
  ): Promise<{
    isRelevant: boolean;
    matchScore: number;
    detectedMaterialTopic: string;
    userRequestedTopic: string;
    confidence: "low" | "medium" | "high";
    message?: string;
  }> {
    try {
      logInfo("Starting context-material relevance validation", {
        userContextLength: userContext.length,
        materialContentLength: materialContent.length,
        sessionSubject,
      });

      // Extract samples for analysis (first 2000 chars of each)
      const contextSample = userContext.substring(0, 2000);
      const materialSample = materialContent.substring(0, 3000);

      const prompt = `You are an educational content analyzer. Your task is to determine if uploaded study materials match what the student wants to learn.

SESSION SUBJECT: ${sessionSubject}

WHAT THE STUDENT WANTS TO LEARN:
${contextSample}

CONTENT OF UPLOADED MATERIALS:
${materialSample}

Carefully analyze BOTH pieces of information and determine:
1. What specific TOPIC does the student want help with? (extract from their description)
2. What TOPIC is the uploaded material about?
3. Do these topics MATCH? Calculate a match score (0-100):
   - 90-100: Perfect match (materials directly cover what student wants to learn)
   - 70-89: Good match (materials are related and will be helpful)
   - 50-69: Partial match (materials touch on some concepts but not the main topic)
   - 30-49: Weak match (materials are loosely related but mostly irrelevant)
   - 0-29: No match (materials are about a completely different topic)

EXAMPLES OF MISMATCHES TO DETECT:
- Student wants to learn "intermolecular forces" but uploads "business math" PDFs
- Student wants help with "Shakespeare sonnets" but uploads "calculus" notes
- Student wants "chemical reactions" but uploads "World War 2 history"

IMPORTANT:
- Focus on the MAIN TOPIC, not just the subject area
- "Math" is too broad - look for specific topics like "algebra", "calculus", "statistics"
- Even within same subject, topics can mismatch (e.g., "geometry" notes won't help with "calculus")

Respond in JSON format:
{
  "userRequestedTopic": "What the student wants to learn (specific topic)",
  "detectedMaterialTopic": "What the uploaded material is about (specific topic)",
  "matchScore": 0-100,
  "confidence": "low" | "medium" | "high",
  "reasoning": "Why this score was given",
  "isRelevant": true if score >= 60, false otherwise
}`;

      const result = await this.model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      logDebug("Gemini context-material relevance response", {
        rawResponse: text.substring(0, 500),
      });

      // Parse JSON response
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        logWarn("Failed to parse context-material relevance - no JSON found");
        // Return permissive result if parsing fails
        return {
          isRelevant: true,
          matchScore: 50,
          detectedMaterialTopic: "Unknown",
          userRequestedTopic: "Unknown",
          confidence: "low",
          message: "Unable to validate material relevance - proceeding with caution",
        };
      }

      const analysis = JSON.parse(jsonMatch[0]);

      logInfo("Context-material validation completed", {
        userRequestedTopic: analysis.userRequestedTopic,
        detectedMaterialTopic: analysis.detectedMaterialTopic,
        matchScore: analysis.matchScore,
        confidence: analysis.confidence,
        isRelevant: analysis.isRelevant,
      });

      // Construct detailed message if mismatch detected
      let message = "";
      if (!analysis.isRelevant) {
        if (analysis.matchScore < 30) {
          message = `⚠️ Material Mismatch Detected!\n\nYou want to learn about "${analysis.userRequestedTopic}", but your uploaded materials appear to be about "${analysis.detectedMaterialTopic}".\n\nPlease upload materials that match what you want to learn, or we'll rely only on your description to generate questions.`;
        } else if (analysis.matchScore < 50) {
          message = `⚠️ Weak Material Match\n\nYour uploaded materials focus on "${analysis.detectedMaterialTopic}", which may not fully address your request about "${analysis.userRequestedTopic}".\n\nConsider uploading more relevant materials for better question generation.`;
        } else {
          message = `ℹ️ Partial Material Match\n\nYour materials cover "${analysis.detectedMaterialTopic}", which partially relates to "${analysis.userRequestedTopic}".\n\nWe'll use both your description and the materials, but more focused materials would help.`;
        }
      }

      return {
        isRelevant: analysis.isRelevant,
        matchScore: analysis.matchScore,
        detectedMaterialTopic: analysis.detectedMaterialTopic,
        userRequestedTopic: analysis.userRequestedTopic,
        confidence: analysis.confidence,
        message: message || undefined,
      };
    } catch (error) {
      logError("Error validating context-material relevance:", error);

      // Return permissive result on error
      return {
        isRelevant: true,
        matchScore: 50,
        detectedMaterialTopic: "Unknown",
        userRequestedTopic: "Unknown",
        confidence: "low",
        message: "Unable to validate material relevance - proceeding with caution",
      };
    }
  }

  /**
   * Build multiple educational search queries for a topic with SEMANTIC FOCUS
   * Uses full topic context instead of keyword extraction to prevent irrelevant matches
   * Example: "electromagnetic waves" stays together, not split into "waves" (which could match boat waves)
   */
  private buildEducationalSearchQueries(topic: string): string[] {
    const queries: string[] = [];

    // SEMANTIC APPROACH: Keep topic as a coherent phrase for better context
    const fullTopicPhrase = topic.trim();

    // Build context-aware exclusions based on the FULL topic, not fragmented keywords
    const contextAwareExclusions = this.buildContextAwareExclusions(topic, fullTopicPhrase);

    // Strategy 1: Full topic phrase with educational context
    // This ensures "electromagnetic waves" is searched as a unit, not individual words
    queries.push(
      `"${fullTopicPhrase}" educational explanation definition -enrollment -admission -course -program -login -error -404 ${contextAwareExclusions}`
    );

    // Strategy 2: Subject-specific search maintaining full topic integrity
    const subjectSpecificQuery = this.buildEnhancedSubjectSpecificQuery(topic, fullTopicPhrase);
    if (subjectSpecificQuery) {
      queries.push(subjectSpecificQuery);
    }

    // Strategy 3: Academic/reference search with full topic context
    queries.push(
      `"${fullTopicPhrase}" academic reference study research -enrollment -admission ${contextAwareExclusions}`
    );

    logDebug("Built educational search queries (semantic mode)", {
      topic,
      fullTopicPhrase,
      queriesCount: queries.length,
      approach: "Semantic search using full topic phrases instead of keyword fragmentation",
    });

    // Return top 2 most relevant queries
    return queries.slice(0, 2);
  }

  /**
   * Extract key terms from a topic for more targeted searching
   */
  private extractTopicKeyTerms(topic: string): string[] {
    // Split topic into meaningful terms, removing common words
    const stopWords = new Set(["the", "of", "and", "in", "on", "at", "to", "for", "with", "by"]);

    // Generic educational terms that appear everywhere - filter these out
    const genericEducationalTerms = new Set([
      "cycle",
      "process",
      "system",
      "method",
      "theory",
      "concept",
      "data",
      "information",
      "example",
      "study",
      "research",
      "analysis",
      "lesson",
      "chapter",
      "unit",
      "activity",
      "learning",
      "education",
    ]);

    const terms = topic
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((term) => term.length > 2 && !stopWords.has(term))
      .filter((term) => !genericEducationalTerms.has(term)) // Remove generic educational terms
      .slice(0, 6); // ✅ FIX #4: INCREASED from 4 to 6 terms for better coverage

    // ✅ FIX #4: NEW - Extract multi-word phrases for better semantic matching
    const multiWordPhrases: string[] = [];
    const words = topic.toLowerCase().split(/\s+/);

    // Extract 2-word combinations that aren't stop words
    for (let i = 0; i < words.length - 1; i++) {
      if (!stopWords.has(words[i]) && !stopWords.has(words[i + 1])) {
        const phrase = `${words[i]} ${words[i + 1]}`;
        if (phrase.length > 8) {
          multiWordPhrases.push(phrase.replace(/\s+/g, "-")); // URL-friendly
        }
      }
    }

    // Prioritize financial mathematics terms (HIGHEST PRIORITY - prevents formula feeding results)
    const financialMathTerms = terms.filter((term) =>
      [
        "interest",
        "compound",
        "simple",
        "principal",
        "rate",
        "investment",
        "loan",
        "annuity",
        "amortization",
      ].includes(term)
    );

    // Prioritize scientific/academic terms
    const scientificTerms = terms.filter((term) =>
      [
        "rock",
        "mineral",
        "sediment",
        "igneous",
        "metamorphic",
        "sedimentary",
        "weathering",
        "erosion",
        "magma",
        "lava",
        "geology",
        "earth",
      ].includes(term)
    );

    // ✅ FIX #4: IMPROVED - Combine multi-word phrases + prioritized terms
    const prioritizedTerms = [
      ...multiWordPhrases.slice(0, 2), // Top 2 multi-word phrases first
      ...financialMathTerms,
      ...scientificTerms,
      ...terms.filter(
        (term) => !financialMathTerms.includes(term) && !scientificTerms.includes(term)
      ),
    ];

    // ✅ FIX #4: Return top 5 terms (was 3) for better topic coverage
    return prioritizedTerms.slice(0, 5);
  }

  /**
   * Build enhanced subject-specific search query using FULL TOPIC PHRASE
   * Maintains semantic integrity instead of fragmenting into keywords
   */
  private buildEnhancedSubjectSpecificQuery(topic: string, fullTopicPhrase: string): string | null {
    const topicLower = topic.toLowerCase();

    // Philippine Curriculum Subject-Specific Queries - Aligned with Firebase subject names

    // Sciences: Earth and Life Science, Physical Science, Earth Science
    if (
      [
        "rock",
        "mineral",
        "geology",
        "earth",
        "sediment",
        "igneous",
        "metamorphic",
        "weathering",
        "erosion",
        "plate tectonics",
        "volcano",
        "earthquake",
        "fossil",
        "carbon cycle",
        "water cycle",
        "earth science",
        "earth and life science",
      ].some((term) => topicLower.includes(term))
    ) {
      // Focus on scientific content, not educational programs
      return `"${fullTopicPhrase}" geology earth science definition -program -course -degree site:usgs.gov OR site:britannica.com`;
    }

    if (
      [
        "cell",
        "biology",
        "organism",
        "ecosystem",
        "evolution",
        "photosynthesis",
        "respiration",
        "mitosis",
        "meiosis",
        "dna",
        "genetics",
        "biodiversity",
        "food chain",
        "food web",
        "life science",
        "earth and life science",
      ].some((term) => topicLower.includes(term))
    ) {
      return `"${fullTopicPhrase}" biology life science process -program -course -degree site:nih.gov OR site:britannica.com`;
    }

    if (
      [
        "physics",
        "force",
        "energy",
        "wave",
        "motion",
        "momentum",
        "velocity",
        "acceleration",
        "electromagnetic",
        "light",
        "sound",
        "heat",
        "electricity",
        "magnetism",
        "physical science",
      ].some((term) => topicLower.includes(term))
    ) {
      return `"${fullTopicPhrase}" physics principle explanation -program -course -degree site:nist.gov OR site:britannica.com OR site:khanacademy.org`;
    }

    if (
      [
        "chemistry",
        "chemical",
        "reaction",
        "molecule",
        "atom",
        "periodic table",
        "bonding",
        "acid",
        "base",
        "solution",
        "compound",
        "element",
        "ionic",
        "covalent",
        "physical science",
      ].some((term) => topicLower.includes(term))
    ) {
      return `"${fullTopicPhrase}" chemistry explanation -program -course -degree site:nist.gov OR site:britannica.com`;
    }

    // Mathematics: General Mathematics, Statistics and Probability
    if (
      [
        "math",
        "mathematics",
        "algebra",
        "geometry",
        "calculus",
        "equation",
        "function",
        "graph",
        "statistics",
        "probability",
        "trigonometry",
        "polynomial",
        "matrix",
        "derivative",
        "integral",
        "general mathematics",
        "statistics and probability",
        // Financial Mathematics - CRITICAL FOR PREVENTING "FORMULA FEEDING" RESULTS
        "interest",
        "compound interest",
        "simple interest",
        "principal",
        "rate",
        "loan",
        "investment",
        "annuity",
        "amortization",
        "future value",
        "present value",
      ].some((term) => topicLower.includes(term))
    ) {
      // Financial Mathematics - Add specific exclusions to prevent baby formula/feeding results
      if (
        [
          "interest",
          "compound interest",
          "simple interest",
          "principal",
          "rate",
          "loan",
          "investment",
          "annuity",
        ].some((term) => topicLower.includes(term))
      ) {
        return `"${fullTopicPhrase}" mathematics finance calculation -infant -baby -feeding -breast site:britannica.com OR site:khanacademy.org OR site:investopedia.com`;
      } else if (
        [
          "statistics",
          "probability",
          "statistics and probability",
          "data",
          "distribution",
          "hypothesis",
          "correlation",
          "regression",
        ].some((term) => topicLower.includes(term))
      ) {
        return `"${fullTopicPhrase}" statistics probability -program -course -degree site:britannica.com OR site:khanacademy.org`;
      } else {
        return `"${fullTopicPhrase}" mathematics explanation -program -course -degree site:britannica.com OR site:khanacademy.org`;
      }
    }

    // English: Oral Communication, Reading and Writing, 21st Century
    if (
      [
        "communication",
        "oral",
        "oral communication",
        "speaking",
        "listening",
        "reading",
        "writing",
        "reading and writing",
        "literature",
        "grammar",
        "composition",
        "essay",
        "rhetoric",
        "21st century",
        "contemporary",
      ].some((term) => topicLower.includes(term))
    ) {
      if (
        ["oral", "oral communication", "speaking", "listening", "communication skills"].some(
          (term) => topicLower.includes(term)
        )
      ) {
        return `"${fullTopicPhrase}" communication technique -program -course -degree site:britannica.com`;
      } else if (
        ["21st century", "contemporary", "modern literature"].some((term) =>
          topicLower.includes(term)
        )
      ) {
        return `"${fullTopicPhrase}" literature analysis -program -course -degree site:britannica.com`;
      } else if (
        ["reading", "writing", "reading and writing", "composition", "essay"].some((term) =>
          topicLower.includes(term)
        )
      ) {
        return `"${fullTopicPhrase}" writing technique -program -course -degree site:britannica.com`;
      }
    }

    // Filipino: Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri
    if (
      [
        "filipino",
        "pilipino",
        "komunikasyon",
        "komunikasyon at pananaliksik",
        "pananaliksik",
        "pagbasa",
        "pagsusuri",
        "pagbasa at pagsusuri",
        "panitikan",
        "gramatika",
        "talumpati",
        "sanaysay",
        "wika",
        "literatura",
      ].some((term) => topicLower.includes(term))
    ) {
      if (
        ["komunikasyon", "komunikasyon at pananaliksik", "pananaliksik", "research"].some((term) =>
          topicLower.includes(term)
        )
      ) {
        return `"${fullTopicPhrase}" filipino research communication -program -course -degree`;
      } else if (
        ["pagbasa", "pagsusuri", "pagbasa at pagsusuri"].some((term) => topicLower.includes(term))
      ) {
        return `"${fullTopicPhrase}" filipino reading analysis -program -course -degree`;
      } else {
        return `"${fullTopicPhrase}" filipino literature -program -course -degree`;
      }
    }

    return null;
  }

  /**
   * Build context-aware negative keywords to prevent ambiguous term mismatches
   * CRITICAL: Prevents "formula" (math) from matching "formula feeding" (baby care)
   * CRITICAL: Prevents "compound" (interest) from matching "chemical compound" (chemistry)
   * CRITICAL: Prevents "interest" (financial) from matching "interest groups" (politics)
   * SAFESEARCH: Blocks explicit content, adult material, violence, and inappropriate content
   */
  private buildContextAwareExclusions(topic: string, primaryTerm: string): string {
    const topicLower = topic.toLowerCase();
    const primaryLower = primaryTerm.toLowerCase();
    const exclusions: string[] = [];

    // SAFESEARCH FILTERS - ALWAYS ACTIVE (blocks explicit content)
    // CRITICAL: Block adult content, pornography, explicit material
    exclusions.push(
      "-porn",
      "-pornography",
      "-xxx",
      "-nsfw",
      "-nude",
      "-naked",
      "-adult-content",
      "-explicit",
      "-sex",
      "-sexual",
      "-erotic"
    );
    // CRITICAL: Block violence, gore, disturbing content
    exclusions.push(
      "-violence",
      "-gore",
      "-brutal",
      "-graphic",
      "-disturbing",
      "-murder",
      "-killing",
      "-death"
    );
    // CRITICAL: Block profanity and inappropriate language
    exclusions.push("-profanity", "-vulgar", "-obscene", "-offensive");
    // CRITICAL: Block drugs, alcohol, illegal content
    exclusions.push("-drugs", "-cocaine", "-heroin", "-illegal", "-trafficking");

    // FINANCIAL MATHEMATICS - Prevent baby/health/medical/transportation results
    if (
      [
        "interest",
        "compound",
        "simple",
        "principal",
        "rate",
        "investment",
        "loan",
        "annuity",
        "amortization",
      ].some((term) => topicLower.includes(term) || primaryLower.includes(term))
    ) {
      // CRITICAL: Exclude baby formula, breast feeding, infant nutrition
      exclusions.push(
        "-infant",
        "-baby",
        "-feeding",
        "-breast",
        "-breastfeeding",
        "-nutrition",
        "-health",
        "-medical",
        "-formula-feeding",
        "-infant-formula"
      );
      // CRITICAL: Exclude transportation/travel (fare adjustments)
      exclusions.push("-fare", "-transportation", "-travel", "-bus", "-train", "-ticket");
      // CRITICAL: Exclude political/social interest groups
      exclusions.push("-politics", "-government", "-policy", "-lobby");
    }

    // CHEMISTRY - Prevent confusion with financial "compound"
    if (
      ["chemical", "chemistry", "molecule", "atom", "reaction", "bond"].some(
        (term) => topicLower.includes(term) || primaryLower.includes(term)
      )
    ) {
      // Exclude financial mathematics when searching chemistry
      exclusions.push("-loan", "-investment", "-bank", "-finance", "-interest-rate");
    }

    // GENERAL EXCLUSIONS - Always exclude error pages and non-educational content
    exclusions.push("-shopping", "-store", "-buy", "-sale", "-price", "-product");

    return exclusions.join(" ");
  }

  /**
   * Check if topic is relevant to Philippine curriculum
   */
  private isPhilippineCurriculumRelevant(topic: string): boolean {
    const philippineTopics = [
      "filipino",
      "pilipino",
      "jose rizal",
      "philippine",
      "philippines",
      "bagong lipunan",
      "edsa",
      "marcos",
      "aquino",
      "duterte",
      "mindanao",
      "luzon",
      "visayas",
    ];

    return philippineTopics.some((pt) => topic.toLowerCase().includes(pt));
  }

  /**
   * Build subject-specific search query
   */
  private buildSubjectSpecificQuery(topic: string): string | null {
    const subjectMappings = {
      mathematics: ["math", "algebra", "geometry", "calculus", "statistics"],
      science: ["physics", "chemistry", "biology", "scientific"],
      english: ["communication", "reading", "writing", "grammar", "literature"],
      filipino: ["komunikasyon", "pananaliksik", "pagbasa", "pagsusuri", "wika"],
    };

    for (const [subject, keywords] of Object.entries(subjectMappings)) {
      if (keywords.some((keyword) => topic.toLowerCase().includes(keyword))) {
        return `"${topic}" ${subject} definition explanation -program -course -degree`;
      }
    }

    return null;
  }

  /**
   * Check if a URL is relevant to the given topic with SEMANTIC validation
   * Uses full topic context instead of keyword fragmentation
   */
  private isTopicRelevant(url: string, topic: string): boolean {
    const urlLower = url.toLowerCase();
    const topicLower = topic.toLowerCase();
    const fullTopicPhrase = topic.trim().toLowerCase();

    // Strategy 1: Direct full topic phrase match in URL (highest confidence)
    // Example: "electromagnetic waves" as a complete phrase, not "waves" alone
    const topicSlug = fullTopicPhrase.replace(/\s+/g, "-");
    const topicNospace = fullTopicPhrase.replace(/\s+/g, "");

    if (
      urlLower.includes(topicSlug) ||
      urlLower.includes(topicNospace) ||
      urlLower.includes(fullTopicPhrase)
    ) {
      logDebug("Topic relevance: Direct full phrase match", { url: url.substring(0, 50), topic });
      return true;
    }

    // Strategy 2: Semantic similarity - check if URL context matches topic meaning
    // This prevents "waves" from matching "boat waves" when topic is "electromagnetic waves"
    if (this.hasSemanticTopicRelevance(url, fullTopicPhrase)) {
      logDebug("Topic relevance: Semantic similarity match", {
        url: url.substring(0, 50),
        topic,
      });
      return true;
    }

    // Strategy 3: Subject-specific domain relevance (as fallback)
    const topicKeyTerms = this.extractEducationalConcepts(fullTopicPhrase);
    if (this.hasEnhancedSubjectSpecificRelevance(url, topic, topicKeyTerms)) {
      logDebug("Topic relevance: Subject-specific match", {
        url: url.substring(0, 50),
        topic,
      });
      return true;
    }

    // Strategy 4: Reject if URL seems too general for the specific topic
    if (this.isUrlTooGeneral(url, topic)) {
      logDebug("Topic relevance: URL too general (rejected)", { url: url.substring(0, 50), topic });
      return false;
    }

    logDebug("Topic relevance: No semantic match found (rejected)", {
      url: url.substring(0, 50),
      topic,
    });
    return false;
  }

  /**
   * Check semantic relevance of URL to topic using contextual analysis
   * Prevents false matches like "waves" (ocean) vs "electromagnetic waves"
   */
  private hasSemanticTopicRelevance(url: string, topicPhrase: string): boolean {
    const urlLower = url.toLowerCase();
    const topicLower = topicPhrase.toLowerCase();

    // Multi-word topics: Check if all significant words appear in close proximity
    const topicWords = topicLower.split(/\s+/).filter((word) => word.length > 2);

    if (topicWords.length === 1) {
      // Single word topic: just check if it appears
      return urlLower.includes(topicWords[0]);
    }

    // For multi-word topics (e.g., "electromagnetic waves"):
    // Require ALL words to appear, ideally with related domain context
    const allWordsPresent = topicWords.every((word) => urlLower.includes(word));

    if (!allWordsPresent) {
      return false;
    }

    // Additional semantic check: ensure related domain terms are present
    // This prevents "waves" from matching "ocean waves" when topic is "electromagnetic waves"
    const domainContexts = this.getTopicDomainContext(topicPhrase);
    const hasDomainContext = domainContexts.some((context) => urlLower.includes(context));

    logDebug("Semantic relevance check", {
      topic: topicPhrase,
      topicWords,
      allWordsPresent,
      hasDomainContext,
      domainContexts,
    });

    return allWordsPresent && hasDomainContext;
  }

  /**
   * Get domain-specific context words for a topic to validate semantic relevance
   * Example: "electromagnetic waves" → ["electromagnetic", "physics", "spectrum", "radiation", "frequency"]
   */
  private getTopicDomainContext(topicPhrase: string): string[] {
    const topicLower = topicPhrase.toLowerCase();

    // Physics/Physical Science contexts
    if (
      topicLower.includes("electromagnetic") ||
      (topicLower.includes("wave") &&
        (topicLower.includes("light") || topicLower.includes("sound")))
    ) {
      return [
        "electromagnetic",
        "physics",
        "spectrum",
        "radiation",
        "frequency",
        "wavelength",
        "photon",
      ];
    }
    if (
      topicLower.includes("force") ||
      topicLower.includes("motion") ||
      topicLower.includes("energy")
    ) {
      return ["physics", "mechanics", "newton", "kinetic", "potential", "work"];
    }

    // Chemistry/Physical Science contexts
    if (
      topicLower.includes("atom") ||
      topicLower.includes("molecule") ||
      topicLower.includes("chemical")
    ) {
      return ["chemistry", "chemical", "atomic", "molecular", "reaction", "compound", "element"];
    }

    // Earth Science contexts
    if (
      topicLower.includes("rock") ||
      topicLower.includes("mineral") ||
      topicLower.includes("geology")
    ) {
      return ["geology", "earth", "geological", "mineral", "sediment", "igneous", "metamorphic"];
    }
    if (
      topicLower.includes("weather") ||
      topicLower.includes("climate") ||
      topicLower.includes("atmosphere")
    ) {
      return ["meteorology", "atmospheric", "climate", "weather", "precipitation"];
    }

    // Life Science/Biology contexts
    if (
      topicLower.includes("cell") ||
      topicLower.includes("organism") ||
      topicLower.includes("biology")
    ) {
      return ["biology", "biological", "cellular", "organism", "life", "living"];
    }

    // Mathematics contexts
    if (
      topicLower.includes("equation") ||
      topicLower.includes("formula") ||
      topicLower.includes("algebra")
    ) {
      return ["mathematics", "mathematical", "algebra", "equation", "calculation"];
    }
    if (
      topicLower.includes("interest") &&
      (topicLower.includes("compound") || topicLower.includes("simple"))
    ) {
      return ["finance", "financial", "mathematics", "investment", "principal", "rate"];
    }
    if (topicLower.includes("statistics") || topicLower.includes("probability")) {
      return ["statistics", "statistical", "probability", "data", "analysis"];
    }

    // English/Communication contexts
    if (
      topicLower.includes("communication") ||
      topicLower.includes("speech") ||
      topicLower.includes("oral")
    ) {
      return ["communication", "speaking", "rhetoric", "presentation", "verbal"];
    }
    if (
      topicLower.includes("literature") ||
      topicLower.includes("poetry") ||
      topicLower.includes("novel")
    ) {
      return ["literature", "literary", "author", "text", "narrative"];
    }

    // Filipino contexts
    if (topicLower.includes("wika") || topicLower.includes("panitikan")) {
      return ["filipino", "pilipino", "tagalog", "wika", "panitikan"];
    }

    // Default: extract the multi-word phrase itself as context
    return topicPhrase.split(/\s+/).filter((word) => word.length > 3);
  }

  /**
   * Enhanced subject-specific domain relevance with content validation
   * STRICT MODE: Requires BOTH specific terms AND domain relevance
   * Accommodates Firebase subject names: Oral Communication, Reading and Writing, 21st Century,
   * Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri, General Mathematics, Statistics and Probability,
   * Earth and Life Science, Physical Science, Earth Science
   */
  private hasEnhancedSubjectSpecificRelevance(
    url: string,
    topic: string,
    topicKeyTerms: string[]
  ): boolean {
    const urlLower = url.toLowerCase();
    const topicLower = topic.toLowerCase();

    // Earth Science / Earth and Life Science - STRICT: Require multiple earth science terms AND geology domain
    if (topicKeyTerms.some((term) => ["rock", "mineral", "geology", "earth"].includes(term))) {
      const earthScienceTerms = [
        "rock",
        "mineral",
        "sediment",
        "igneous",
        "metamorphic",
        "weathering",
        "erosion",
        "earth",
        "geology",
      ];
      const earthTermMatches = earthScienceTerms.filter((term) => urlLower.includes(term));
      const isGeologyDomain =
        urlLower.includes("geology") || urlLower.includes("usgs") || urlLower.includes("earth");

      // STRICT: Require at least 2 earth science terms AND geology domain
      const isRelevant = earthTermMatches.length >= 2 && isGeologyDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Earth Science match (strict)", {
          url: url.substring(0, 50),
          earthTermMatches,
          isGeologyDomain,
        });
      }
      return isRelevant;
    }

    // Life Science / Earth and Life Science - STRICT: Multiple bio terms AND biology domain
    if (topicKeyTerms.some((term) => ["cell", "biology", "organism", "life"].includes(term))) {
      const lifeScienceTerms = ["biology", "cell", "organism", "ecosystem", "evolution", "life"];
      const bioTermMatches = lifeScienceTerms.filter((term) => urlLower.includes(term));
      const isBiologyDomain =
        urlLower.includes("biology") || urlLower.includes("ncbi") || urlLower.includes("life");

      // STRICT: Require at least 2 biology terms AND biology domain
      const isRelevant = bioTermMatches.length >= 2 && isBiologyDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Life Science match (strict)", {
          url: url.substring(0, 50),
          bioTermMatches,
          isBiologyDomain,
        });
      }
      return isRelevant;
    }

    // Physical Science - STRICT: Multiple physics terms AND physics domain
    if (topicKeyTerms.some((term) => ["physics", "force", "energy", "physical"].includes(term))) {
      const physicsTerms = ["physics", "force", "energy", "motion", "wave", "physical"];
      const physicsTermMatches = physicsTerms.filter((term) => urlLower.includes(term));
      const isPhysicsDomain =
        urlLower.includes("physics") || urlLower.includes("phys") || urlLower.includes("force");

      // STRICT: Require at least 2 physics terms AND physics domain
      const isRelevant = physicsTermMatches.length >= 2 && isPhysicsDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Physical Science match (strict)", {
          url: url.substring(0, 50),
          physicsTermMatches,
          isPhysicsDomain,
        });
      }
      return isRelevant;
    }

    // Physical Science (Chemistry) - STRICT: Multiple chemistry terms AND chemistry domain
    if (topicKeyTerms.some((term) => ["chemistry", "chemical", "reaction"].includes(term))) {
      const chemistryTerms = ["chemistry", "chemical", "reaction", "molecule", "compound"];
      const chemTermMatches = chemistryTerms.filter((term) => urlLower.includes(term));
      const isChemistryDomain =
        urlLower.includes("chemistry") ||
        urlLower.includes("chem") ||
        urlLower.includes("molecule");

      // STRICT: Require at least 2 chemistry terms AND chemistry domain
      const isRelevant = chemTermMatches.length >= 2 && isChemistryDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Chemistry (Physical Science) match (strict)", {
          url: url.substring(0, 50),
          chemTermMatches,
          isChemistryDomain,
        });
      }
      return isRelevant;
    }

    // General Mathematics - STRICT: Multiple math terms AND math domain
    if (
      topicKeyTerms.some((term) => ["math", "mathematics", "algebra", "geometry"].includes(term))
    ) {
      const mathTerms = ["math", "mathematics", "algebra", "geometry", "equation", "formula"];
      const mathTermMatches = mathTerms.filter((term) => urlLower.includes(term));
      const isMathDomain =
        urlLower.includes("math") || urlLower.includes("wolfram") || urlLower.includes("algebra");

      // STRICT: Require at least 2 math terms AND math domain
      const isRelevant = mathTermMatches.length >= 2 && isMathDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: General Mathematics match (strict)", {
          url: url.substring(0, 50),
          mathTermMatches,
          isMathDomain,
        });
      }
      return isRelevant;
    }

    // Statistics and Probability - STRICT: Multiple statistics terms AND math domain
    if (topicKeyTerms.some((term) => ["statistics", "probability", "data"].includes(term))) {
      const statsTerms = ["statistics", "probability", "data", "distribution", "hypothesis"];
      const statsTermMatches = statsTerms.filter((term) => urlLower.includes(term));
      const isStatsDomain =
        urlLower.includes("statistics") || urlLower.includes("math") || urlLower.includes("data");

      // STRICT: Require at least 2 statistics terms AND math domain
      const isRelevant = statsTermMatches.length >= 2 && isStatsDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Statistics and Probability match (strict)", {
          url: url.substring(0, 50),
          statsTermMatches,
          isStatsDomain,
        });
      }
      return isRelevant;
    }

    // Oral Communication - STRICT: Multiple communication terms AND communication domain
    if (topicKeyTerms.some((term) => ["oral", "communication", "speaking"].includes(term))) {
      const commTerms = ["oral", "communication", "speaking", "listening", "presentation"];
      const commTermMatches = commTerms.filter((term) => urlLower.includes(term));
      const isCommDomain =
        urlLower.includes("communication") ||
        urlLower.includes("speech") ||
        urlLower.includes("oral");

      // STRICT: Require at least 2 communication terms AND communication domain
      const isRelevant = commTermMatches.length >= 2 && isCommDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Oral Communication match (strict)", {
          url: url.substring(0, 50),
          commTermMatches,
          isCommDomain,
        });
      }
      return isRelevant;
    }

    // Reading and Writing - STRICT: Multiple reading/writing terms AND literacy domain
    if (topicKeyTerms.some((term) => ["reading", "writing", "composition"].includes(term))) {
      const literacyTerms = ["reading", "writing", "composition", "essay", "comprehension"];
      const literacyTermMatches = literacyTerms.filter((term) => urlLower.includes(term));
      const isLiteracyDomain =
        urlLower.includes("reading") ||
        urlLower.includes("writing") ||
        urlLower.includes("literacy");

      // STRICT: Require at least 2 literacy terms AND literacy domain
      const isRelevant = literacyTermMatches.length >= 2 && isLiteracyDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Reading and Writing match (strict)", {
          url: url.substring(0, 50),
          literacyTermMatches,
          isLiteracyDomain,
        });
      }
      return isRelevant;
    }

    // 21st Century (Literature) - STRICT: Multiple literature terms AND literature domain
    if (
      topicKeyTerms.some((term) => ["21st", "century", "literature", "contemporary"].includes(term))
    ) {
      const litTerms = ["literature", "21st", "century", "contemporary", "modern"];
      const litTermMatches = litTerms.filter((term) => urlLower.includes(term));
      const isLitDomain =
        urlLower.includes("literature") ||
        urlLower.includes("literary") ||
        urlLower.includes("contemporary");

      // STRICT: Require at least 2 literature terms AND literature domain
      const isRelevant = litTermMatches.length >= 2 && isLitDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: 21st Century Literature match (strict)", {
          url: url.substring(0, 50),
          litTermMatches,
          isLitDomain,
        });
      }
      return isRelevant;
    }

    // Komunikasyon at Pananaliksik - STRICT: Multiple Filipino communication/research terms
    if (topicKeyTerms.some((term) => ["komunikasyon", "pananaliksik", "filipino"].includes(term))) {
      const filipinoCommTerms = ["komunikasyon", "pananaliksik", "filipino", "wika", "research"];
      const filipinoCommMatches = filipinoCommTerms.filter((term) => urlLower.includes(term));
      const isFilipinoCommDomain =
        urlLower.includes("filipino") ||
        urlLower.includes("komunikasyon") ||
        urlLower.includes("wika");

      // STRICT: Require at least 2 Filipino communication terms AND Filipino domain
      const isRelevant = filipinoCommMatches.length >= 2 && isFilipinoCommDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Komunikasyon at Pananaliksik match (strict)", {
          url: url.substring(0, 50),
          filipinoCommMatches,
          isFilipinoCommDomain,
        });
      }
      return isRelevant;
    }

    // Pagbasa at Pagsusuri - STRICT: Multiple Filipino reading/analysis terms
    if (topicKeyTerms.some((term) => ["pagbasa", "pagsusuri", "panitikan"].includes(term))) {
      const filipinoReadTerms = ["pagbasa", "pagsusuri", "panitikan", "literatura", "filipino"];
      const filipinoReadMatches = filipinoReadTerms.filter((term) => urlLower.includes(term));
      const isFilipinoReadDomain =
        urlLower.includes("filipino") ||
        urlLower.includes("pagbasa") ||
        urlLower.includes("panitikan");

      // STRICT: Require at least 2 Filipino reading terms AND Filipino domain
      const isRelevant = filipinoReadMatches.length >= 2 && isFilipinoReadDomain;
      if (isRelevant) {
        logDebug("Enhanced subject relevance: Pagbasa at Pagsusuri match (strict)", {
          url: url.substring(0, 50),
          filipinoReadMatches,
          isFilipinoReadDomain,
        });
      }
      return isRelevant;
    }

    return false;
  }

  /**
   * Check if URL is too general for the specific topic
   */
  private isUrlTooGeneral(url: string, topic: string): boolean {
    const urlLower = url.toLowerCase();

    // URLs that are too general for specific topics
    const generalPatterns = [
      "overview",
      "introduction",
      "basics",
      "general",
      "about",
      "home",
      "index",
      "main",
      "welcome",
      "portal",
      "guide-to",
      "what-is",
    ];

    // Homepage and generic site patterns that should be rejected
    const homepagePatterns = [
      // Exact homepage matches
      "deped.gov.ph/",
      "deped.gov.ph",
      "nctm.org/",
      "nctm.org",
      "britannica.com/",
      "britannica.com",
      "nasa.gov/",
      "nasa.gov",
      "ed.gov/",
      "ed.gov",
      "up.edu.ph/",
      "up.edu.ph",
      "ateneo.edu/",
      "ateneo.edu",
      // Generic directory pages
      "/about",
      "/contact",
      "/home",
      "/index",
      "/main",
      "/search",
      "/browse",
      "/categories",
      "/resources",
      "/links",
      "/sitemap",
      // Very short paths indicating homepages
    ];

    // Educational program/institutional URLs that should be rejected for content searches
    const programPatterns = [
      "program",
      "course",
      "degree",
      "curriculum",
      "admission",
      "enrollment",
      "tuition",
      "university",
      "college",
      "school",
      "institute",
      "academy",
      "campus",
      "student",
      "apply",
      "application",
      "requirements",
      "scholarships",
      "academic-programs",
      "bachelor",
      "master",
      "phd",
      "undergraduate",
      "graduate",
      "major",
      "minor",
      "department",
    ];

    // Check for homepage patterns - reject if it's just a domain or very short path
    const url_obj = new URL(urlLower.startsWith("http") ? urlLower : `https://${urlLower}`);
    const path = url_obj.pathname;

    // Reject URLs that are just domains or have very short, generic paths
    if (path === "/" || path === "" || path.length <= 3) {
      logDebug("Rejecting homepage URL", {
        url: url.substring(0, 80),
        reason: "Homepage or very short path",
        path: path,
      });
      return true;
    }

    // Check for explicit homepage patterns
    const hasHomepagePattern = homepagePatterns.some((pattern) => urlLower.includes(pattern));
    if (hasHomepagePattern) {
      logDebug("Rejecting homepage URL", {
        url: url.substring(0, 80),
        reason: "Homepage pattern detected",
      });
      return true;
    }

    // If topic is specific (like "rock cycle") but URL is very general
    if (topic.includes(" ") && topic.length > 8) {
      const hasGeneralPattern = generalPatterns.some((pattern) => urlLower.includes(pattern));
      if (hasGeneralPattern) {
        return true;
      }
    }

    // Reject educational program/institutional URLs
    const hasProgramPattern = programPatterns.some((pattern) => urlLower.includes(pattern));
    if (hasProgramPattern) {
      return true;
    }

    // Reject overly broad subject area URLs when topic is specific
    const broadSubjectPatterns = [
      "earth-and-life-science",
      "earth-life-science",
      "general-science",
      "science-education",
      "earth-science-overview",
    ];

    return broadSubjectPatterns.some((pattern) => urlLower.includes(pattern));
  }

  /**
   * Check if URL contains specific content rather than general/homepage content
   */
  private isSpecificContentUrl(url: string, topic: string): boolean {
    const urlLower = url.toLowerCase();
    const path = new URL(urlLower.startsWith("http") ? urlLower : `https://${urlLower}`).pathname;

    // URLs with substantial paths are more likely to be specific content
    if (path.length < 10 && !path.includes(topic.toLowerCase().replace(/\s+/g, "-"))) {
      return false;
    }

    // Look for indicators of specific content
    const specificContentIndicators = [
      topic.toLowerCase().replace(/\s+/g, "-"),
      topic.toLowerCase().replace(/\s+/g, ""),
      "article",
      "entry",
      "topic",
      "definition",
      "explanation",
      "guide",
      "tutorial",
      "resource",
      "content",
      "page",
      "document",
    ];

    // URL should contain topic-related terms or content indicators
    const hasSpecificIndicators = specificContentIndicators.some((indicator) =>
      urlLower.includes(indicator)
    );

    // Additional check: URL should not be a directory listing or search page
    const isDirectoryOrSearch = [
      "/search",
      "/browse",
      "/categories",
      "/directory",
      "/list",
      "/index.html",
      "/index.php",
    ].some((pattern) => urlLower.includes(pattern));

    return hasSpecificIndicators && !isDirectoryOrSearch;
  }

  /**
   * Check for subject-specific domain relevance
   */
  private hasSubjectSpecificRelevance(url: string, topic: string): boolean {
    const urlLower = url.toLowerCase();
    const topicLower = topic.toLowerCase();

    // Math topics on math sites
    if (["math", "algebra", "geometry", "calculus"].some((term) => topicLower.includes(term))) {
      return urlLower.includes("math") || urlLower.includes("nctm") || urlLower.includes("ams");
    }

    // Science topics on science sites
    if (["physics", "chemistry", "biology", "science"].some((term) => topicLower.includes(term))) {
      return urlLower.includes("science") || urlLower.includes("nsta") || urlLower.includes("nasa");
    }

    // History topics on history sites
    if (["history", "historical", "civilization"].some((term) => topicLower.includes(term))) {
      return urlLower.includes("history") || urlLower.includes("historical");
    }

    return false;
  }

  /**
   * CRITICAL: Detect obviously mismatched sources (e.g., math sources for Filipino content)
   * This prevents irrelevant sources from appearing in final output
   */
  private isObviouslyOffTopic(
    url: string,
    contentSubject: string,
    contentTopics: string[]
  ): boolean {
    const urlLower = url.toLowerCase();
    const subjectLower = contentSubject.toLowerCase();
    const topicsLower = contentTopics.join(" ").toLowerCase();

    // ✅ ENHANCED: Math/Calculator/Homework sites for non-math content
    // Check both domain AND path to catch sites like gauthmath.com/solution/...
    const isMathSite = [
      "math",
      "calculator",
      "equation",
      "gauth", // Catches gauthmath
      "mathway",
      "wolfram",
      "symbolab",
      "photomath",
      "solution", // Homework solution sites
      "tutor", // Math tutoring sites
    ].some((term) => urlLower.includes(term));

    const isMathContent = [
      "math",
      "algebra",
      "geometry",
      "calculus",
      "equation",
      "formula",
      "interest",
      "compound",
      "statistics",
      "probability",
    ].some((term) => subjectLower.includes(term) || topicsLower.includes(term));

    if (isMathSite && !isMathContent) {
      logWarn("Rejected math source for non-math content", { url, subject: contentSubject });
      return true;
    }

    // Science sites for non-science content
    const isScienceSite = [
      "physics",
      "chemistry",
      "biology",
      "nasa",
      "nsta",
      "scientificamerican",
    ].some((term) => urlLower.includes(term));

    const isScienceContent = [
      "science",
      "physics",
      "chemistry",
      "biology",
      "earth",
      "cell",
      "rock",
      "mineral",
    ].some((term) => subjectLower.includes(term) || topicsLower.includes(term));

    if (isScienceSite && !isScienceContent) {
      logWarn("Rejected science source for non-science content", { url, subject: contentSubject });
      return true;
    }

    // Filipino/language sites for non-Filipino content
    const isFilipinoSite = [
      "filipino",
      "tagalog",
      "wika",
      "panitikan",
      "komunikasyon",
      "wikang",
    ].some((term) => urlLower.includes(term));

    const isFilipinoContent = [
      "filipino",
      "tagalog",
      "wika",
      "panitikan",
      "komunikasyon",
      "pagbasa",
      "pagsusuri",
      "pananaliksik",
    ].some((term) => subjectLower.includes(term) || topicsLower.includes(term));

    // Allow Filipino sites for Filipino content, reject for non-Filipino content
    if (isFilipinoSite && !isFilipinoContent) {
      logWarn("Rejected Filipino source for non-Filipino content", {
        url,
        subject: contentSubject,
      });
      return true;
    }

    return false;
  }

  /**
   * Get topic-specific fallback sources - quality over quantity approach
   */
  private getTopicSpecificFallbackSources(topics: string[]): string[] {
    const fallbackSources: string[] = [];

    for (const topic of topics) {
      const topicLower = topic.toLowerCase();

      // Add subject-specific reliable sources with specific content focus
      if (["math", "algebra", "geometry"].some((term) => topicLower.includes(term))) {
        fallbackSources.push(
          "https://mathworld.wolfram.com/", // Specific mathematical content
          "https://www.britannica.com/science/mathematics" // Specific math section
        );
      } else if (
        ["science", "physics", "chemistry", "biology"].some((term) => topicLower.includes(term))
      ) {
        fallbackSources.push(
          "https://www.britannica.com/science", // Specific science section
          "https://www.nasa.gov/news/" // Specific content section
        );
      } else if (
        ["english", "communication", "writing"].some((term) => topicLower.includes(term))
      ) {
        fallbackSources.push(
          "https://www.britannica.com/topic/English-language", // Specific content
          "https://www.britannica.com/art/literature" // Specific content section
        );
      } else if (["filipino", "komunikasyon"].some((term) => topicLower.includes(term))) {
        fallbackSources.push(
          "https://www.britannica.com/place/Philippines/Languages", // Specific content
          "https://scholar.google.com/" // Search portal rather than homepage
        );
      }
    }

    // Only add high-quality, content-specific fallbacks if no topic-specific sources found
    if (fallbackSources.length === 0) {
      fallbackSources.push(
        "https://www.britannica.com/", // Reliable encyclopedia
        "https://scholar.google.com/" // Academic search portal
      );
    }

    // Remove duplicates and return - prioritize quality over quantity
    return [...new Set(fallbackSources)].slice(0, 3); // Maximum 3 fallback sources
  }

  /**
   * Clean text from markdown formatting, asterisks, and other formatting symbols
   */
  private cleanTextFromFormatting(text: string): string {
    // ✅ CRITICAL FIX: Type guard to prevent "text.replace is not a function" error
    if (!text || typeof text !== "string") return "";

    return (
      text
        // Remove asterisks used for italics/bold (*text* or **text**)
        .replace(/\*{1,2}([^*]+)\*{1,2}/g, "$1")
        // Remove underscores used for italics/bold (_text_ or __text__)
        .replace(/_{1,2}([^_]+)_{1,2}/g, "$1")
        // Remove backticks used for code (`text`)
        .replace(/`([^`]+)`/g, "$1")
        // Remove brackets used for links [text]
        .replace(/\[([^\]]+)\]/g, "$1")
        // Remove HTML tags if any
        .replace(/<[^>]+>/g, "")
        // Clean up multiple spaces
        .replace(/\s+/g, " ")
        // Trim whitespace
        .trim()
    );
  }

  /**
   * Enhanced text cleaning specifically for source titles and content
   */
  private cleanSourceText(text: string): string {
    // ✅ CRITICAL FIX: Type guard to prevent "text.replace is not a function" error
    if (!text || typeof text !== "string") return "";

    return (
      this.cleanTextFromFormatting(text)
        // Remove quotes that might wrap titles
        .replace(/^["'](.*)["']$/, "$1")
        // Remove period at the end if it looks like a title
        .replace(/\.$/, "")
        // Clean up extra whitespace again
        .replace(/\s+/g, " ")
        .trim()
    );
  }

  /**
   * Check if URL points to a direct file download (PDF, DOC, XLS, etc.)
   * These files cannot be scraped for content and should be filtered out
   * ✅ EXCEPTION: Filipino educational PDFs are allowed as reference links
   */
  private isDirectFileUrl(url: string): boolean {
    const urlLower = url.toLowerCase();

    // ✅ EXCEPTION: Allow Filipino educational PDFs (even if not scrapable, they're valuable references)
    const filipinoEducationalDomains = [
      "deped.gov.ph",
      "depedtambayan.net",
      "lrmds.deped.gov.ph",
      "kwf.gov.ph",
      ".edu.ph",
      "ched.gov.ph",
      "dost.gov.ph",
      "upd.edu.ph",
      "dlsu.edu.ph",
      "ust.edu.ph",
      "ateneo.edu",
    ];

    const isFilipinoEducationalPDF =
      urlLower.endsWith(".pdf") &&
      filipinoEducationalDomains.some((domain) => urlLower.includes(domain));

    if (isFilipinoEducationalPDF) {
      logDebug("✅ Allowing Filipino educational PDF as reference", {
        url: url.substring(0, 80),
        reason: "Filipino educational content - valuable even without scraping",
      });
      return false; // Don't filter it out
    }

    // File extensions that indicate direct file downloads (excluding PDFs for Filipino sites)
    const fileExtensions = [
      ".pdf", // Will be filtered unless Filipino educational
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".ppt",
      ".pptx",
      ".zip",
      ".rar",
      ".tar",
      ".gz",
      ".mp3",
      ".mp4",
      ".avi",
      ".mov",
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
    ];

    // Check if URL ends with a file extension
    const hasFileExtension = fileExtensions.some((ext) => {
      // Check both URL path and after query parameters
      return urlLower.includes(ext);
    });

    if (hasFileExtension) {
      logDebug("URL filtered due to file extension", {
        url: url.substring(0, 80),
        reason: "Direct file download URL",
      });
      return true;
    }

    return false;
  }

  /**
   * Check if URL is likely to be broken or cause 403 errors based on common patterns from Google Search results
   */
  private isLikelyBrokenUrl(url: string): boolean {
    const urlLower = url.toLowerCase();

    // Common patterns that indicate likely broken links, 403 errors, or "Document Not Found" from Google Search
    const brokenUrlPatterns = [
      // Error and document not found pages
      "error",
      "404",
      "not-found",
      "page-not-found",
      "document-not-found",
      "file-not-found",
      "content-not-found",
      "resource-not-found",
      "not_found",
      "notfound",
      "missing",
      "unavailable",
      "removed",
      "deleted",
      "expired",
      "gone",
      "moved",
      "relocated",
      "access-denied",
      "forbidden",
      "temp",
      "temporary",
      "cache",
      "cached",
      "preview",
      "draft",
      "staging",

      // ✅ FIX #1: ENHANCED - Added more specific 404 patterns based on real-world issues
      "content-unavailable",
      "page-removed",
      "link-expired",
      "resource-deleted",
      "no-longer-available",
      "been-removed",
      "been-deleted",
      "has-expired",
      "has-moved",

      // Creative 404 and error page patterns (often used by websites for broken links)
      "new-species",
      "pagus-notfoundii",
      "notfoundii",
      "page-cant-be-found",
      "cant-be-found",
      "page-can-not-be-found",
      "oops",
      "whoops",
      "uh-oh",
      "something-went-wrong",
      "lost",
      "searching-for-isnt",
      "nothing-was-found",
      "nothing-found",
      "try-search",
      "try-navigation",
      "start-from-homepage",
      "no-longer-exists",
      "cannot-be-found",
      "what-you-were-searching",
      "paths-below-will-help",
      "find-your-way",
      "looks-like-nothing",
      "this-location",
      "maybe-try-search",

      // URL shorteners and redirects (often broken)
      "bit.ly",
      "tinyurl.com",
      "short.link",
      "ow.ly",
      "t.co",

      // Print/PDF versions that are often broken
      "print.php",
      "print.html",
      "/print/",
      "printview",
      "pdf.php",

      // Session-based or dynamic URLs
      "sessionid",
      "session=",
      "sid=",
      "jsessionid",
      "phpsessid",

      // Search result pages within sites
      "/search?",
      "/search.php",
      "/search.html",
      "/buscar",

      // Login or user-specific content
      "/login",
      "/signin",
      "/auth/",
      "/user/",
      "/profile/",
      "/dashboard/",

      // Archive or snapshot URLs (often broken)
      "archive.org",
      "wayback",
      "snapshot",
      "web.archive",

      // Social media post URLs (often private or deleted)
      "facebook.com/posts/",
      "twitter.com/status/",
      "instagram.com/p/",
      "linkedin.com/posts/",
      "tiktok.com/@",

      // Content management systems that often show "Document Not Found"
      "drupal",
      "joomla",
      "wordpress.com/post",
      "wix.com/",
      "squarespace.com/",
      "weebly.com/",
      "blogspot.com/",
      "tumblr.com/post",
      "medium.com/p/",

      // Educational platforms with temporary/expired content
      "schoology.com/",
      "edmodo.com/",
      "google.classroom",
      "teams.microsoft.com/",
      "zoom.us/",
      "webex.com/",

      // Database URLs that often lead to "Document Not Found"
      "db.php",
      "database.php",
      "data.php",
      "export.php",
      "import.php",
      "backup.php",

      // Forum thread URLs (often broken)
      "/thread/",
      "/topic/",
      "/post/",
      "showthread.php",

      // Patterns that commonly cause 403 Forbidden errors
      "secure",
      "private",
      "restricted",
      "members",
      "member-only",
      "premium",
      "subscription",
      "paywall",
      "protected",
      "locked",
      "gated",
      "exclusive",
      "internal",
      "intranet",
      "staff",
      "admin",
      "administrator",
      "manage",
      "management",
      "control",
      "panel",
      "backend",
      "back-end",
      "wp-admin",
      "wp-content/uploads",
      "/admin/",
      "/manage/",
      "/staff/",
      "/internal/",
      "/private/",
      "/secure/",
      "/restricted/",
      "/members/",
      "/member/",
      "/premium/",
      "/subscription/",
      "/protected/",
      "/exclusive/",
      "download.php",
      "download?",
      "attachment?",
      "file.php",
      "document.php",
      "asset.php",
      "media.php",

      // Database-driven content URLs that often require special access
      "viewdoc.asp",
      "getfile.asp",
      "download.asp",
      "secure.asp",
      "member.asp",
      "login.asp",
      "access.php",
      "check.php",
      "verify.php",
      "validate.php",

      // Educational content URLs that commonly result in "Document Not Found"
      "lesson.php",
      "module.php",
      "course.php",
      "assignment.php",
      "quiz.php",
      "test.php",
      "exam.php",
      "grade.php",
      "student.php",
      "teacher.php",
      "faculty.php",
      "staff.php",
      "old.html",
      "archive.html",
      "backup.html",
      "temp.html",
      "old/",
      "archive/",
      "backup/",
      "temp/",
      // ✅ FIX #1: RELAXED - Removed overly broad year filters (2020/, 2021/, 2022/)
      // These were rejecting valid educational content from recent years
      "old/2020", // Only filter if combined with "old" or "archive"
      "old/2021",
      "old/2022",
      "archive/2020",
      "archive/2021",
      "archive/2022",
      "spring2020/",
      "fall2020/",
      "summer2021/",
      "semester/",
      "academic-year/",
      "old-curriculum/",
      "previous/",
      "legacy/",
      "deprecated/",

      // Content delivery networks with expired content
      "cdn.jsdelivr.net/",
      "unpkg.com/",
      "cdnjs.cloudflare.com/",
      "maxcdn.bootstrapcdn.com/",

      // Very long URLs with complex parameters (often temporary)
    ];

    // Check for obvious broken patterns
    const hasProblematicPattern = brokenUrlPatterns.some((pattern) => urlLower.includes(pattern));

    if (hasProblematicPattern) {
      logDebug("URL filtered due to problematic pattern", {
        url: url.substring(0, 100),
        reason: "Contains known problematic pattern",
      });
      return true;
    }

    try {
      // Check for overly complex URLs (often temporary/broken)
      const url_obj = new URL(url);
      const queryParams = url_obj.searchParams;
      const paramCount = Array.from(queryParams.keys()).length;
      const domain = url_obj.hostname.toLowerCase();

      // Check for domains that commonly cause 403 errors and "Document Not Found" in educational context
      const restrictedDomainPatterns = [
        "student.portal",
        "studentportal",
        "lms.",
        "blackboard.",
        "canvas.",
        "moodle.",
        "learning.management",
        "elearning.",
        "courseware.",
        "library.access",
        "digital.library",
        "restricted.access",
        "member.access",
        "subscription.",
        "premium.",
        "paid.content",
        "paywall.",
        "secure.content",
        "private.content",
        "protected.content",

        // Domains that commonly show "Document Not Found"
        "old.",
        "archive.",
        "backup.",
        "temp.",
        "temporary.",
        "staging.",
        "dev.",
        "test.",
        "beta.",
        "preview.",
        "draft.",
        "internal.",
        "staff.",
        "admin.",
        "manage.",
        "cms.",
        "backend.",

        // Educational subdomain patterns prone to broken links
        "courses.",
        "classes.",
        "modules.",
        "lessons.",
        "assignments.",
        "resources.",
        "materials.",
        "downloads.",
        "files.",
        "documents.",
        "media.",
        "assets.",

        // Time-sensitive educational domains
        "2020.",
        "2021.",
        "2022.",
        "2023.", // Often outdated academic content
        "spring.",
        "fall.",
        "summer.",
        "winter.",
        "semester.",
        "academic.",
        "school-year.",
      ];

      const hasRestrictedDomain = restrictedDomainPatterns.some(
        (pattern) => domain?.includes(pattern) || false
      );
      if (hasRestrictedDomain) {
        logDebug("URL filtered due to restricted domain pattern", {
          url: url.substring(0, 100),
          domain: domain.substring(0, 50),
          reason: "Domain indicates restricted access",
        });
        return true;
      }

      // Too many parameters often indicates temporary or session-based URLs
      if (paramCount > 5) {
        logDebug("URL filtered due to complex parameters", {
          url: url.substring(0, 100),
          paramCount,
          reason: "Too many URL parameters",
        });
        return true;
      }

      // Very long URLs are often temporary or broken
      if (url.length > 200) {
        logDebug("URL filtered due to excessive length", {
          url: url.substring(0, 100),
          length: url.length,
          reason: "URL too long",
        });
        return true;
      }

      // Check for suspicious parameter names
      const suspiciousParams = [
        "sid",
        "sessionid",
        "token",
        "auth",
        "login",
        "temp",
        "cache",
        "access",
        "key",
        "ticket",
      ];
      const hasSuspiciousParams = suspiciousParams.some((param) =>
        Array.from(queryParams.keys()).some((key) => key.toLowerCase().includes(param))
      );

      if (hasSuspiciousParams) {
        logDebug("URL filtered due to suspicious parameters", {
          url: url.substring(0, 100),
          reason: "Contains session or auth parameters",
        });
        return true;
      }

      // Check for file extensions that often cause 403 errors
      const problematicExtensions = [
        ".doc",
        ".docx",
        ".pdf",
        ".ppt",
        ".pptx",
        ".xls",
        ".xlsx",
        ".zip",
        ".rar",
      ];
      const hasProblematicExtension = problematicExtensions.some((ext) => url.includes(ext));
      if (hasProblematicExtension) {
        logDebug("URL filtered due to file extension", {
          url: url.substring(0, 100),
          reason: "Direct file download URL",
        });
        return true;
      }
    } catch (urlError) {
      logDebug("URL filtering error - treating as broken", {
        url: url.substring(0, 100),
        error: "Invalid URL format",
      });
      return true;
    }

    return false;
  }

  /**
   * Strictly validate if a URL is from a credible source
   */
  private isStrictlyCredibleSource(url: string): boolean {
    if (!url || typeof url !== "string") {
      return false;
    }

    const urlLower = url.toLowerCase();

    // Ensure URL is properly formatted
    if (!urlLower.startsWith("http://") && !urlLower.startsWith("https://")) {
      return false;
    }

    // Credible domains - expanded list to be less restrictive
    const credibleDomains = [
      // Educational institutions
      ".edu",
      // Government sources
      ".gov",
      ".gov.ph",
      // Major academic platforms
      "scholar.google.com",
      "scholar.google",
      "jstor.org",
      "researchgate.net",
      "academia.edu",
      "ncbi.nlm.nih.gov",
      // Philippine educational institutions
      "deped.gov.ph",
      "up.edu.ph",
      "ateneo.edu",
      "dlsu.edu.ph",
      // Major educational organizations
      "nctm.org",
      "ams.org",
      "nsta.org",
      "ncte.org",
      "mla.org",
      // Established encyclopedias and reference sources
      "britannica.com",
      "encyclopedia.com",
      "merriam-webster.com",
      // Science organizations
      "nasa.gov",
      "nsf.gov",
      "nature.com",
      "sciencedirect.com",
      "springer.com",
      "wiley.com",
      "usgs.gov",
      // Professional organizations and societies
      "ieee.org",
      "acm.org",
      "aaas.org",
      // Educational content providers
      "coursera.org",
      "edx.org",
      "khanacademy.org",
      // Mathematic resources
      "mathworld.wolfram.com",
      "wolframalpha.com",
      // Additional credible educational sources
      "pbs.org",
      "nationalgeographic.com",
      "smithsonianmag.com",
    ];

    const isCredible = credibleDomains.some((domain) => {
      // For domains starting with '.', check if URL contains the domain
      if (domain.startsWith(".")) {
        return urlLower.includes(domain);
      }
      // For full domains, check if URL contains the exact domain
      return urlLower.includes(domain);
    });

    // Additional validation: reject common non-credible patterns
    const nonCrediblePatterns = [
      "wikipedia.org",
      "wiki.",
      "blog.",
      "wordpress.",
      "blogspot.",
      "medium.com",
      "reddit.com",
      "quora.com",
      "yahoo.com",
      "answers.com",
      "ask.com",
      "ehow.com",
      "about.com",
    ];

    const hasNonCrediblePattern = nonCrediblePatterns.some((pattern) => urlLower.includes(pattern));

    if (hasNonCrediblePattern) {
      logDebug("Rejected URL due to non-credible pattern", {
        url: url.substring(0, 100),
        rejectionReason: "Contains non-credible domain pattern",
      });
      return false;
    }

    logDebug("URL credibility validation", {
      url: url.substring(0, 100),
      isCredible: isCredible,
      validationLevel: "expanded",
    });

    return isCredible;
  }

  /**
   * Check if URL is accessible (quick head request to avoid broken links)
   */
  private async isUrlAccessible(url: string): Promise<boolean> {
    try {
      // Quick timeout to avoid long waits on broken URLs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      // Try a GET request first to get proper status codes
      const response = await fetch(url, {
        method: "GET",
        signal: controller.signal,
        headers: {
          "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
      });

      clearTimeout(timeoutId);

      // Check for successful status codes
      if (response.status >= 200 && response.status < 400) {
        // Additional check: make sure it's not a redirect to a 404 or error page
        const finalUrl = response.url.toLowerCase();

        // Check if redirected to common error pages
        if (
          finalUrl.includes("404") ||
          finalUrl.includes("not-found") ||
          finalUrl.includes("error") ||
          finalUrl.includes("page-not-found")
        ) {
          logDebug("URL redirected to error page", {
            url: url.substring(0, 60),
            finalUrl: finalUrl.substring(0, 60),
          });
          return false;
        }

        // Check content type if available
        const contentType = response.headers.get("content-type");
        if (
          contentType &&
          !contentType.includes("text/html") &&
          !contentType.includes("application/pdf")
        ) {
          logDebug("URL has non-web content type", {
            url: url.substring(0, 60),
            contentType,
          });
          return false;
        }

        return true;
      } else {
        logDebug("URL returned error status", {
          url: url.substring(0, 60),
          status: response.status,
        });
        return false;
      }
    } catch (error) {
      // If GET fails, try HEAD as fallback
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch(url, {
          method: "HEAD",
          signal: controller.signal,
          headers: {
            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
          },
        });

        clearTimeout(timeoutId);

        if (response.status >= 200 && response.status < 400) {
          return true;
        }
      } catch (headError) {
        // Both GET and HEAD failed
        logDebug("URL accessibility check failed completely", {
          url: url.substring(0, 60),
          getError: error instanceof Error ? error.message : "Unknown error",
          headError: headError instanceof Error ? headError.message : "Unknown error",
        });
      }

      return false;
    }
  }

  /**
   * Enhanced topic relevance validation with URL path and title analysis
   */
  private hasStrictTopicRelevance(url: string, topic: string, searchContext?: string): boolean {
    const urlLower = url.toLowerCase();
    const topicLower = topic.toLowerCase();
    const topicKeyTerms = this.extractTopicKeyTerms(topic);

    // Stricter validation for Philippine curriculum topics
    const curriculumSubjects = [
      "earth and life science",
      "physical science",
      "earth science",
      "general mathematics",
      "statistics and probability",
      "oral communication",
      "reading and writing",
      "21st century",
      "komunikasyon at pananaliksik",
      "pagbasa at pagsusuri",
    ];

    // 1. Direct topic match in URL path (highest confidence)
    if (
      urlLower.includes(topicLower.replace(/\s+/g, "-")) ||
      urlLower.includes(topicLower.replace(/\s+/g, ""))
    ) {
      return true;
    }

    // 2. ✅ FIX #3: RELAXED key term matching for better source discovery
    const matchingTerms = topicKeyTerms.filter(
      (term) => term.length > 3 && urlLower.includes(term)
    );

    if (topic.includes(" ") && topicKeyTerms.length > 1) {
      // ✅ RELAXED: For multi-word topics, accept 1+ matching term (was 2+)
      // This allows quality sources that focus on main concept
      if (matchingTerms.length < 1) {
        return false;
      }
    } else {
      // For single topics, require at least 1 strong match
      if (matchingTerms.length < 1) {
        return false;
      }
    }

    // 3. ✅ FIX #3: SMARTER contradictory term filtering - check URL path only, not query params
    const contradictoryTerms = this.getContradictoryTerms(topic);
    const hasContradictory = contradictoryTerms.some((term) => urlLower.includes(term));

    if (hasContradictory) {
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname.toLowerCase();

        // Only reject if contradictory term is in URL path (not query string)
        const contradictoryInPath = contradictoryTerms.some((term) => path.includes(term));

        if (contradictoryInPath) {
          logDebug("URL rejected due to contradictory term in path", {
            url: url.substring(0, 60),
            contradictoryTerm: contradictoryTerms.find((term) => path.includes(term)),
          });
          return false;
        }
        // If contradictory term is only in query params, allow the URL
      } catch (urlError) {
        // If URL parsing fails, apply strict filtering as fallback
        return !hasContradictory;
      }
    }

    // 4. Additional validation for search context if available
    if (searchContext && !this.isContextRelevant(searchContext, topic, topicKeyTerms)) {
      return false;
    }

    return true;
  }

  /**
   * Get terms that would contradict the main topic (enhanced for Philippine curriculum)
   */
  private getContradictoryTerms(topic: string): string[] {
    const topicLower = topic.toLowerCase();

    // Earth Science topics - avoid biology/life science content
    if (
      topicLower.includes("rock") ||
      topicLower.includes("geology") ||
      topicLower.includes("mineral") ||
      topicLower.includes("weathering") ||
      topicLower.includes("erosion") ||
      topicLower.includes("igneous") ||
      topicLower.includes("sedimentary") ||
      topicLower.includes("metamorphic")
    ) {
      return [
        "biology",
        "cell",
        "organism",
        "photosynthesis",
        "mitosis",
        "dna",
        "genetics",
        "evolution",
        "ecosystem",
        "biodiversity",
        "food chain",
        "respiration",
        "meiosis",
        "protein synthesis",
      ];
    }

    // Life Science/Biology topics - avoid earth science content
    if (
      topicLower.includes("cell") ||
      topicLower.includes("biology") ||
      topicLower.includes("organism") ||
      topicLower.includes("photosynthesis") ||
      topicLower.includes("ecosystem") ||
      topicLower.includes("evolution") ||
      topicLower.includes("dna") ||
      topicLower.includes("genetics") ||
      topicLower.includes("biodiversity")
    ) {
      return [
        "rock",
        "mineral",
        "geology",
        "igneous",
        "sedimentary",
        "metamorphic",
        "weathering",
        "erosion",
        "plate tectonics",
        "volcano",
        "earthquake",
        "fossil formation",
      ];
    }

    // Physical Science topics - avoid biology and earth science specifics
    if (
      topicLower.includes("physics") ||
      topicLower.includes("force") ||
      topicLower.includes("energy") ||
      topicLower.includes("wave") ||
      topicLower.includes("motion") ||
      topicLower.includes("electricity") ||
      topicLower.includes("magnetism") ||
      topicLower.includes("light") ||
      topicLower.includes("sound")
    ) {
      return [
        "biology",
        "cell",
        "organism",
        "photosynthesis",
        "evolution",
        "geology",
        "rock",
        "mineral",
        "sedimentary",
        "igneous",
        "metamorphic",
        "fossil",
      ];
    }

    // Chemistry topics - avoid biology and geology
    if (
      topicLower.includes("chemistry") ||
      topicLower.includes("chemical") ||
      topicLower.includes("molecule") ||
      topicLower.includes("atom") ||
      topicLower.includes("periodic table") ||
      topicLower.includes("bonding") ||
      topicLower.includes("acid") ||
      topicLower.includes("base") ||
      topicLower.includes("compound")
    ) {
      return [
        "biology",
        "cell",
        "organism",
        "photosynthesis",
        "geology",
        "rock",
        "mineral",
        "sedimentary",
        "igneous",
        "metamorphic",
        "plate tectonics",
      ];
    }

    // Mathematics topics - avoid all science content
    if (
      topicLower.includes("math") ||
      topicLower.includes("algebra") ||
      topicLower.includes("geometry") ||
      topicLower.includes("calculus") ||
      topicLower.includes("statistics") ||
      topicLower.includes("probability") ||
      topicLower.includes("trigonometry") ||
      topicLower.includes("polynomial")
    ) {
      return [
        "biology",
        "chemistry",
        "physics",
        "cell",
        "organism",
        "rock",
        "mineral",
        "geology",
        "photosynthesis",
        "evolution",
        "force",
        "energy",
        "wave",
        "atom",
        "molecule",
      ];
    }

    // English/Communication topics - avoid science and math content
    if (
      topicLower.includes("communication") ||
      topicLower.includes("reading") ||
      topicLower.includes("writing") ||
      topicLower.includes("grammar") ||
      topicLower.includes("essay") ||
      topicLower.includes("rhetoric") ||
      topicLower.includes("21st century")
    ) {
      return [
        "biology",
        "chemistry",
        "physics",
        "mathematics",
        "algebra",
        "geometry",
        "calculus",
        "cell",
        "organism",
        "rock",
        "mineral",
        "geology",
        "equation",
        "function",
      ];
    }

    // Filipino subjects - avoid all other subjects
    if (
      topicLower.includes("filipino") ||
      topicLower.includes("komunikasyon") ||
      topicLower.includes("pananaliksik") ||
      topicLower.includes("pagbasa") ||
      topicLower.includes("pagsusuri") ||
      topicLower.includes("panitikan")
    ) {
      return [
        "biology",
        "chemistry",
        "physics",
        "mathematics",
        "english",
        "communication",
        "science",
        "algebra",
        "geometry",
        "cell",
        "organism",
        "rock",
        "mineral",
      ];
    }

    return [];
  }

  /**
   * Check if search context is relevant to topic
   */
  private isContextRelevant(context: string, topic: string, topicKeyTerms: string[]): boolean {
    const contextLower = context.toLowerCase();
    const matchingContextTerms = topicKeyTerms.filter((term) => contextLower.includes(term));

    // Require at least 50% of key terms to appear in context
    return matchingContextTerms.length >= Math.ceil(topicKeyTerms.length * 0.5);
  }

  /**
   * Check if search result content or page title indicates an error page
   */
  private isErrorPageContent(content: string): boolean {
    if (!content || typeof content !== "string") {
      return false;
    }

    const contentLower = content.toLowerCase();

    // Patterns that indicate error pages based on the user's specific examples
    const errorPageContentPatterns = [
      // Specific patterns from user's examples
      "you've discovered a new species",
      "pagus notfoundii",
      "notfoundii",
      "please try the navigation or search above",
      "start from the homepage",
      "oops! that page can't be found",
      "oops! it looks like what you were searching for",
      "it looks like nothing was found at this location",
      "maybe try a search",
      "what you were searching for isn't the only thing that is lost",
      "you may be accessing a page that no longer exists",
      "cannot be found",
      "we are terribly sorry",
      "hope that the paths below will help find your way",

      // Common error page indicators
      "page not found",
      "404 error",
      "file not found",
      "document not found",
      "content not found",
      "resource not found",
      "page doesn't exist",
      "page does not exist",
      "this page is missing",
      "sorry, this page",
      "the page you are looking for",
      "the page you're looking for",
      "could not be found",
      "cannot find the page",
      "can't find the page",
      "broken link",
      "dead link",
      "link is broken",
      "link not working",
      "page has been moved",
      "page has been removed",
      "page has been deleted",
      "temporarily unavailable",
      "under construction",
      "coming soon",
      "check back later",
      "try again later",
      "service unavailable",
      "access denied",
      "forbidden",
      "unauthorized",
      "login required",
      "authentication required",
      "subscription required",
      "premium content",
      "members only",
      "not available in your region",
      "content blocked",
      "site maintenance",
      "scheduled maintenance",
      "server error",
      "internal server error",
      "bad gateway",
      "gateway timeout",
    ];

    // Check if content contains any error page indicators
    const hasErrorPattern = errorPageContentPatterns.some((pattern) =>
      contentLower.includes(pattern)
    );

    if (hasErrorPattern) {
      logDebug("Error page content detected", {
        content: content.substring(0, 150),
        reason: "Contains error page indicators",
      });
      return true;
    }

    return false;
  }

  /**
   * Parse and validate search results with enhanced validation including URL accessibility
   */
  private async parseAndValidateSearchResults(
    searchResults: string,
    topic: string
  ): Promise<
    Array<{
      url: string;
      type: string;
      credibilityScore: number;
      domain: string;
    }>
  > {
    const validatedSources: Array<{
      url: string;
      type: string;
      credibilityScore: number;
      domain: string;
    }> = [];

    try {
      const lines = searchResults.split("\n").filter((line: string) => line.trim());
      const accessibilityChecks: Promise<{ url: string; accessible: boolean; line: string }>[] = [];

      // First pass: extract and validate URLs format
      const potentialUrls: { url: string; line: string }[] = [];

      for (const line of lines) {
        // Enhanced URL extraction with better regex
        const urlMatches = line.match(/https?:\/\/[^\s\[\]<>(){}'"]+/g);

        if (urlMatches) {
          for (const rawUrl of urlMatches) {
            const cleanUrl = this.cleanAndValidateUrl(rawUrl);
            if (cleanUrl && this.isStrictlyCredibleSource(cleanUrl)) {
              potentialUrls.push({ url: cleanUrl, line: line });
            }
          }
        }
      }

      // Second pass: Apply smart filtering to reduce broken links from Google Search results
      const urlsToCheck = potentialUrls.slice(0, 20); // Check more URLs but filter smarter

      // Third pass: validate URLs with enhanced broken link prevention
      for (const { url, line } of urlsToCheck) {
        try {
          // Smart filtering to prevent common broken link patterns from Google Search
          if (this.isLikelyBrokenUrl(url)) {
            logDebug("URL rejected - likely broken based on patterns", {
              url: url.substring(0, 60),
              reason: "broken-url-pattern",
            });
            continue;
          }

          // Check if search result content indicates an error page
          if (this.isErrorPageContent(line)) {
            logDebug("URL rejected - search result content indicates error page", {
              url: url.substring(0, 60),
              content: line.substring(0, 100),
              reason: "error-page-content",
            });
            continue;
          }

          // Enhanced validation: check multiple criteria with BALANCED requirements
          const isTopicRelevant = this.hasStrictTopicRelevance(url, topic, line);
          const isNotTooGeneral = !this.isUrlTooGeneral(url, topic);
          const isSpecificContent = this.isSpecificContentUrl(url, topic);

          // ✅ FIX #2: RELAXED validation - prioritize topic relevance, lower criteria threshold
          const criteriaScore =
            (isTopicRelevant ? 3 : 0) + (isNotTooGeneral ? 1 : 0) + (isSpecificContent ? 1 : 0);

          // ✅ RELAXED: Require minimum score of 1 (was 2) - accept URLs with strong topic relevance alone
          if (criteriaScore >= 1) {
            const domain = this.extractDomain(url);
            const sourceType = this.getSourceType(url);
            const credibilityScore = this.calculateCredibilityScore(url);

            // ✅ RELAXED: Lowered credibility threshold to 40/60 (was 50/65) for more quality sources
            const minCredibilityScore = isTopicRelevant ? 40 : 60;

            if (credibilityScore >= minCredibilityScore) {
              validatedSources.push({
                url: url,
                type: sourceType,
                credibilityScore: credibilityScore,
                domain: domain,
              });

              logSuccess("✅ RELAXED URL VALIDATION PASSED", {
                url: url.substring(0, 60),
                domain: domain,
                type: sourceType,
                credibilityScore: credibilityScore,
                topic: topic,
                topicRelevant: isTopicRelevant,
                criteriaScore: criteriaScore,
                minCriteriaScore: 1,
                minCredibilityScore: minCredibilityScore,
                validationType: "relaxed-filtering",
                note: "Passed relaxed validation - prioritizing topic relevance and quality sources",
              });
            } else {
              logDebug("URL rejected due to low credibility score (relaxed mode)", {
                url: url.substring(0, 60),
                credibilityScore: credibilityScore,
                requiredScore: minCredibilityScore,
                topic: topic,
                reason: "Below relaxed credibility threshold",
              });
            }
          } else {
            logDebug("URL rejected - failed relaxed validation criteria", {
              url: url.substring(0, 60),
              topicRelevant: isTopicRelevant,
              notTooGeneral: isNotTooGeneral,
              specificContent: isSpecificContent,
              criteriaScore: criteriaScore,
              requiredScore: 1,
              topic: topic,
              reason: "Below relaxed criteria threshold",
            });
          }
        } catch (urlError) {
          logDebug("URL validation error", {
            url: url.substring(0, 60),
            error: urlError instanceof Error ? urlError.message : "Unknown error",
          });
        }
      }

      logInfo("Enhanced search results validation completed", {
        totalLines: lines.length,
        potentialUrls: potentialUrls.length,
        checkedUrls: urlsToCheck.length,
        validatedSources: validatedSources.length,
        topic: topic,
        successRate: `${Math.round(
          (validatedSources.length / Math.max(1, urlsToCheck.length)) * 100
        )}%`,
        averageCredibilityScore:
          validatedSources.length > 0
            ? Math.round(
                validatedSources.reduce((sum, s) => sum + s.credibilityScore, 0) /
                  validatedSources.length
              )
            : 0,
      });
    } catch (parseError) {
      logError("Error parsing enhanced search results", { error: parseError, topic: topic });
    }

    return validatedSources;
  }

  /**
   * Clean and validate URL format
   */
  private cleanAndValidateUrl(rawUrl: string): string | null {
    if (!rawUrl || typeof rawUrl !== "string") {
      return null;
    }

    // Remove trailing punctuation and cleanup
    const cleanUrl = rawUrl
      .replace(/[,\]\)\}]+$/, "")
      .replace(/['"]+$/, "")
      .replace(/\.$/, "")
      .trim();

    // Ensure proper protocol
    if (!cleanUrl.startsWith("http://") && !cleanUrl.startsWith("https://")) {
      return null;
    }

    // Basic URL format validation
    try {
      const url = new URL(cleanUrl);

      // Reject suspicious URL patterns
      if (url.pathname.includes("..") || url.hostname.length < 4) {
        return null;
      }

      return cleanUrl;
    } catch {
      return null;
    }
  }

  /**
   * Normalize URL for comparison
   */
  private normalizeUrl(url: string): string {
    if (!url) return "";

    try {
      const urlObj = new URL(url);
      let pathname = urlObj.pathname.replace(/\/$/, "");

      // Remove common file extensions for duplicate detection
      // This prevents "article/view/123" and "article/download/123/456/file.pdf" from being treated as different
      pathname = pathname
        .replace(/\/download\/[^\/]+\/[^\/]+\/[^\/]+\.pdf$/i, "") // Remove /download/x/y/file.pdf
        .replace(/\.pdf$/i, "") // Remove .pdf extension
        .replace(/\.html?$/i, ""); // Remove .html/.htm extension

      // Normalize to https and remove trailing slash
      return `https://${urlObj.hostname}${pathname}`.replace(/\/$/, "");
    } catch {
      return url
        .toLowerCase()
        .replace(/\/$/, "")
        .replace(/\.pdf$/i, "")
        .replace(/\.html?$/i, "");
    }
  }

  /**
   * Extract domain from URL
   */
  private extractDomain(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      const match = url.match(/https?:\/\/([^\/]+)/);
      return match ? match[1] : "unknown";
    }
  }

  /**
   * Calculate credibility score for a URL
   */
  private calculateCredibilityScore(url: string): number {
    const urlLower = url.toLowerCase();
    let score = 50; // Base score

    // High credibility domains
    if (urlLower.includes(".edu")) score += 40;
    if (urlLower.includes(".gov")) score += 40;
    if (urlLower.includes("scholar.google")) score += 35;
    if (urlLower.includes("jstor.org")) score += 35;
    if (urlLower.includes("researchgate.net")) score += 30;
    if (urlLower.includes("ncbi.nlm.nih.gov")) score += 35;
    if (urlLower.includes("deped.gov.ph")) score += 40;

    // Subject-specific high-credibility sources
    if (urlLower.includes("nctm.org") || urlLower.includes("ams.org")) score += 30;
    if (urlLower.includes("nasa.gov") || urlLower.includes("nsta.org")) score += 30;
    if (urlLower.includes("ncte.org") || urlLower.includes("mla.org")) score += 30;

    // Philippine educational institutions
    if (
      urlLower.includes("up.edu.ph") ||
      urlLower.includes("ateneo.edu") ||
      urlLower.includes("dlsu.edu.ph")
    )
      score += 25;

    // Penalty for certain patterns that reduce credibility
    if (urlLower.includes("student") || urlLower.includes("personal")) score -= 20;
    if (urlLower.includes("blog") || urlLower.includes("forum")) score -= 30;

    return Math.min(100, Math.max(0, score));
  }

  private extractKeyTopics(content: string): string[] {
    // Enhanced topic extraction using multiple techniques for better educational relevance
    const text = content.toLowerCase().trim();

    // Step 1: Extract specific educational concepts and processes
    const educationalConcepts = this.extractEducationalConcepts(text);

    // Step 2: Extract academic keywords and phrases
    const academicKeywords = this.extractAcademicKeywords(text);

    // Step 3: Extract noun phrases that could be educational concepts
    const nounPhrases = this.extractEducationalPhrases(text);

    // Step 4: Extract domain-specific terms
    const domainTerms = this.extractDomainSpecificTerms(text);

    // Step 5: Simple word frequency for backup
    const frequentWords = this.extractFrequentWords(text);

    // Combine and rank topics by educational relevance
    const allTopics = [
      ...educationalConcepts,
      ...academicKeywords,
      ...nounPhrases,
      ...domainTerms,
      ...frequentWords,
    ];
    const rankedTopics = this.rankTopicsByEducationalRelevance(allTopics, text);

    // Return top 5 most educationally relevant topics
    const finalTopics = rankedTopics.slice(0, 5);

    logDebug("Enhanced topic extraction completed", {
      educationalConcepts: educationalConcepts.slice(0, 3),
      academicKeywords: academicKeywords.slice(0, 3),
      nounPhrases: nounPhrases.slice(0, 3),
      domainTerms: domainTerms.slice(0, 3),
      finalTopics,
    });

    return finalTopics.length > 0 ? finalTopics : ["education", "learning"];
  }

  /**
   * Extract specific educational concepts and processes (like "rock cycle", "water cycle", etc.)
   */
  private extractEducationalConcepts(text: string): string[] {
    const concepts: string[] = [];

    // Science concepts - prioritize multi-word educational processes and systems
    const sciencePatterns = [
      // Earth and Life Science specific concepts
      /\b(rock\s+cycle|water\s+cycle|carbon\s+cycle|nitrogen\s+cycle|phosphorus\s+cycle)\b/g,
      /\b(plate\s+tectonics|continental\s+drift|seafloor\s+spreading|volcanic\s+activity|seismic\s+activity)\b/g,
      /\b(igneous\s+rock|sedimentary\s+rock|metamorphic\s+rock|mineral\s+formation)\b/g,
      /\b(weathering\s+and\s+erosion|chemical\s+weathering|physical\s+weathering|soil\s+formation)\b/g,
      /\b(food\s+chain|food\s+web|energy\s+pyramid|ecosystem\s+dynamics|biodiversity)\b/g,
      /\b(cellular\s+respiration|photosynthesis|mitosis|meiosis|protein\s+synthesis)\b/g,
      /\b(natural\s+selection|evolution|adaptation|genetic\s+variation|inheritance)\b/g,
      /\b(fossil\s+formation|geological\s+time|radiometric\s+dating|stratigraphy)\b/g,

      // Physical Science concepts
      /\b(atomic\s+structure|periodic\s+table|chemical\s+bonding|ionic\s+bonding|covalent\s+bonding)\b/g,
      /\b(states\s+of\s+matter|phase\s+transitions|kinetic\s+molecular\s+theory)\b/g,
      /\b(conservation\s+of\s+energy|conservation\s+of\s+momentum|newton\'?s\s+laws)\b/g,
      /\b(electromagnetic\s+spectrum|wave\s+properties|light\s+reflection|refraction)\b/g,

      // Astronomy concepts
      /\b(solar\s+system|planetary\s+motion|stellar\s+evolution|galaxy\s+formation)\b/g,
      /\b(big\s+bang\s+theory|cosmic\s+microwave\s+background|dark\s+matter|dark\s+energy)\b/g,
    ];

    // Math concepts - focus on specific mathematical processes and theorems - ENHANCED
    const mathPatterns = [
      /\b(quadratic\s+formula|pythagorean\s+theorem|distance\s+formula|slope\s+formula)\b/g,
      /\b(linear\s+equations|systems\s+of\s+equations|polynomial\s+functions|exponential\s+functions|rational\s+functions)\b/g,
      /\b(trigonometric\s+functions|unit\s+circle|law\s+of\s+sines|law\s+of\s+cosines)\b/g,
      /\b(derivative\s+rules|integration\s+techniques|fundamental\s+theorem\s+of\s+calculus)\b/g,
      /\b(probability\s+distributions|statistical\s+analysis|hypothesis\s+testing|normal\s+distribution|binomial\s+distribution)\b/g,
      /\b(geometric\s+proofs|coordinate\s+geometry|transformation\s+geometry)\b/g,
      // Additional specific math topics
      /\b(permutations|combinations|factorial|standard\s+deviation|variance|mean|median|mode)\b/g,
      /\b(slope-intercept\s+form|point-slope\s+form|domain\s+and\s+range|inverse\s+functions|composite\s+functions)\b/g,
      /\b(parabola|vertex\s+form|axis\s+of\s+symmetry|discriminant|roots\s+of\s+equations)\b/g,
      /\b(arithmetic\s+sequence|geometric\s+sequence|arithmetic\s+series|geometric\s+series)\b/g,
      /\b(correlation|regression|scatter\s+plot|confidence\s+interval|sampling\s+distribution)\b/g,
      // CRITICAL: Financial Mathematics patterns - Prevents "formula feeding" mismatches
      /\b(simple\s+interest|compound\s+interest|interest\s+rate|interest\s+calculation|interest\s+formula)\b/g,
      /\b(principal\s+amount|principal\s+and\s+interest|time\s+period|rate\s+of\s+interest)\b/g,
      /\b(future\s+value|present\s+value|annuity\s+formula|amortization\s+schedule)\b/g,
      /\b(loan\s+calculation|investment\s+return|compound\s+growth|exponential\s+growth\s+formula)\b/g,
    ];

    // English/Communication concepts - focus on language and communication skills
    const englishPatterns = [
      /\b(oral\s+communication|reading\s+comprehension|writing\s+skills|grammar\s+rules)\b/g,
      /\b(communication\s+strategies|presentation\s+techniques|public\s+speaking|verbal\s+skills)\b/g,
      /\b(text\s+analysis|critical\s+reading|essay\s+writing|composition\s+techniques)\b/g,
      /\b(21st\s+century\s+skills|digital\s+literacy|media\s+literacy|information\s+literacy)\b/g,
    ];

    // Filipino concepts - focus on Philippine language and culture
    const filipinoPatterns = [
      /\b(komunikasyon\s+sa\s+filipino|pananaliksik\s+sa\s+wika|pagbasa\s+at\s+pagsusuri)\b/g,
      /\b(filipino\s+kultura|wika\s+at\s+komunikasyon|panitikan\s+ng\s+pilipinas)\b/g,
      /\b(akademikong\s+pagsulat|saliksik\s+sa\s+filipino|tekstong\s+ekspositori)\b/g,
    ];

    const allPatterns = [
      ...sciencePatterns,
      ...mathPatterns,
      ...englishPatterns,
      ...filipinoPatterns,
    ];

    allPatterns.forEach((pattern) => {
      const matches = text.match(pattern) || [];
      concepts.push(...matches.map((match) => match.trim()));
    });

    // Remove duplicates and return unique concepts
    const uniqueConcepts = [...new Set(concepts)];

    logDebug("Educational concepts extracted", {
      totalFound: uniqueConcepts.length,
      concepts: uniqueConcepts.slice(0, 5),
    });

    return uniqueConcepts;
  }

  /**
   * Extract academic keywords commonly used in educational content
   */
  private extractAcademicKeywords(text: string): string[] {
    const academicPatterns = [
      // Science terms
      /\b(atom|molecule|cell|DNA|photosynthesis|gravity|force|energy|momentum|velocity|acceleration|chemical|reaction|element|compound|equation|hypothesis|theory|experiment)\b/g,
      // Math terms - ENHANCED with more specific topics INCLUDING FINANCIAL MATHEMATICS
      /\b(quadratic|rational function|polynomial|linear equation|parabola|vertex|discriminant|permutation|combination|probability|statistics|mean|median|mode|standard deviation|variance|normal distribution|binomial|slope|intercept|asymptote|domain|range|inverse function|logarithm|exponential growth|factorial|sequence|series|limit|continuity)\b/gi,
      // CRITICAL: Financial Mathematics terms - Prevents "infant formula" mismatches
      /\b(interest|compound interest|simple interest|principal|rate|investment|loan|annuity|amortization|future value|present value|financial mathematics|interest rate calculation|compound growth|exponential growth rate)\b/gi,
      /\b(algebra|geometry|calculus|trigonometry|equation|formula|theorem|proof|function|derivative|integral|matrix|vector|polynomial|logarithm|exponential)\b/g,
      // English/Communication terms
      /\b(communication|reading|writing|grammar|composition|essay|presentation|speaking|listening|comprehension|analysis)\b/g,
      // Filipino terms
      /\b(komunikasyon|pananaliksik|pagbasa|pagsusuri|wika|kultura|panitikan|saliksik|akademik)\b/g,
      // General academic terms
      /\b(concept|principle|process|method|technique|analysis|synthesis|evaluation|comparison|classification|definition|explanation)\b/g,
    ];

    const keywords: string[] = [];
    academicPatterns.forEach((pattern) => {
      const matches = text.match(pattern) || [];
      keywords.push(...matches);
    });

    // Remove generic terms like "research", "study", "guide", etc. that aren't topic-specific
    const genericTermsToFilter = new Set([
      "research",
      "study",
      "guide",
      "researcher",
      "analysis",
      "method",
      "technique",
      "process",
      "concept",
      "principle",
    ]);

    const filteredKeywords = keywords.filter(
      (keyword) => !genericTermsToFilter.has(keyword.toLowerCase())
    );

    return [...new Set(filteredKeywords)];
  }

  /**
   * Extract educational noun phrases that represent concepts
   */
  private extractEducationalPhrases(text: string): string[] {
    // Look for educational phrases patterns
    const phrases: string[] = [];

    // Extract capitalized phrases (proper nouns, important concepts)
    const capitalizedPhrases = text.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/g) || [];
    phrases.push(...capitalizedPhrases.map((p) => p.toLowerCase()));

    // ENHANCED: Extract 2-3 word n-grams for better specificity
    // This helps capture specific concepts like "binomial distribution" vs just "distribution"
    const words = text.split(/\s+/);

    // Extract 2-word phrases (bigrams)
    for (let i = 0; i < words.length - 1; i++) {
      const bigram = `${words[i]} ${words[i + 1]}`;
      // Filter out phrases with articles, conjunctions, or too short
      if (
        bigram.length > 8 &&
        bigram.length < 35 &&
        !/\b(the|and|or|but|a|an|of|in|on|at|to|for|with|from)\b/.test(bigram) &&
        /[aeiou]/.test(bigram)
      ) {
        phrases.push(bigram);
      }
    }

    // Extract 3-word phrases (trigrams) for highly specific concepts
    for (let i = 0; i < words.length - 2; i++) {
      const trigram = `${words[i]} ${words[i + 1]} ${words[i + 2]}`;
      // Filter out phrases with articles, conjunctions, or too short
      if (
        trigram.length > 12 &&
        trigram.length < 40 &&
        !/\b(the|and|or|but|a|an|of|in|on|at|to|for|with|from)\b/.test(trigram) &&
        /[aeiou]/.test(trigram)
      ) {
        phrases.push(trigram);
      }
    }

    // Extract compound educational terms (original logic)
    const compoundTerms = text.match(/\b[a-z]+(?:\s+[a-z]+){1,2}\b/g) || [];
    const educationalCompounds = compoundTerms.filter((term) => {
      return (
        term.length > 8 &&
        term.length < 30 && // reasonable length
        !term.includes("the ") &&
        !term.includes("and ") && // avoid articles
        /[aeiou]/.test(term)
      ); // has vowels (real words)
    });
    phrases.push(...educationalCompounds);

    return [...new Set(phrases)].slice(0, 10);
  }

  /**
   * Extract domain-specific terms based on context
   */
  private extractDomainSpecificTerms(text: string): string[] {
    const domainMappings = {
      science: [
        "physics",
        "chemistry",
        "biology",
        "scientific",
        "laboratory",
        "research",
        "study",
        "observation",
      ],
      mathematics: [
        "mathematical",
        "calculation",
        "number",
        "solve",
        "formula",
        "compute",
        "graph",
      ],
      english: ["communication", "reading", "writing", "grammar", "essay", "analysis"],
      filipino: ["komunikasyon", "pananaliksik", "pagbasa", "pagsusuri", "wika", "kultura"],
    };

    const domainTerms: string[] = [];

    Object.entries(domainMappings).forEach(([domain, indicators]) => {
      const domainScore = indicators.reduce((score, indicator) => {
        return score + (text.split(indicator).length - 1);
      }, 0);

      if (domainScore > 0) {
        domainTerms.push(domain);
        // Add domain-specific terms found in text
        indicators.forEach((indicator) => {
          if (text.includes(indicator)) {
            domainTerms.push(indicator);
          }
        });
      }
    });

    return [...new Set(domainTerms)];
  }

  /**
   * Fallback: Extract frequent meaningful words
   */
  private extractFrequentWords(text: string): string[] {
    const words = text
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 3);

    // Enhanced stop words list for educational content
    const stopWords = new Set([
      "this",
      "that",
      "with",
      "have",
      "will",
      "been",
      "from",
      "they",
      "know",
      "want",
      "good",
      "much",
      "some",
      "time",
      "very",
      "when",
      "come",
      "here",
      "just",
      "like",
      "long",
      "make",
      "many",
      "over",
      "such",
      "take",
      "than",
      "them",
      "well",
      "were",
      "also",
      "each",
      "which",
      "their",
      "said",
      "there",
      "would",
      "what",
      "about",
      "after",
      "first",
      "never",
      "these",
      "think",
      "where",
      "being",
      "every",
      "great",
      "might",
      "shall",
      "still",
      "those",
      "under",
      "while",
      "should",
    ]);

    const filteredWords = words.filter((word) => !stopWords.has(word));

    // Get word frequency
    const wordCount = new Map<string, number>();
    filteredWords.forEach((word) => {
      wordCount.set(word, (wordCount.get(word) || 0) + 1);
    });

    const topics = Array.from(wordCount.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([word]) => word);

    return topics;
  }

  /**
   * Rank topics by educational relevance
   */
  private rankTopicsByEducationalRelevance(topics: string[], text: string): string[] {
    const educationalKeywords = [
      "education",
      "educational",
      "academic",
      "school",
      "university",
      "college",
      "study",
      "learn",
      "teaching",
      "research",
      "knowledge",
      "science",
      "mathematics",
      "english",
      "filipino",
      "physics",
      "chemistry",
      "biology",
      "communication",
      "reading",
      "writing",
      "statistics",
      "probability",
      "earth science",
      "life science",
      "physical science",
    ];

    return topics
      .filter((topic) => topic && topic.length > 2)
      .map((topic) => ({
        topic,
        score: this.calculateEducationalRelevanceScore(topic, text, educationalKeywords),
        isMultiWord: topic.includes(" "), // Prioritize multi-word concepts like "rock cycle"
        isSpecificConcept: this.isSpecificEducationalConcept(topic),
      }))
      .sort((a, b) => {
        // First prioritize specific educational concepts
        if (a.isSpecificConcept && !b.isSpecificConcept) return -1;
        if (!a.isSpecificConcept && b.isSpecificConcept) return 1;

        // Then prioritize multi-word topics
        if (a.isMultiWord && !b.isMultiWord) return -1;
        if (!a.isMultiWord && b.isMultiWord) return 1;

        // Finally sort by relevance score
        return b.score - a.score;
      })
      .map((item) => item.topic)
      .slice(0, 10); // Top 10 for further filtering
  }

  /**
   * Check if a topic is a specific educational concept
   */
  private isSpecificEducationalConcept(topic: string): boolean {
    const specificConcepts = [
      // Science processes and cycles
      "rock cycle",
      "water cycle",
      "carbon cycle",
      "nitrogen cycle",
      "phosphorus cycle",
      "plate tectonics",
      "continental drift",
      "seafloor spreading",
      "cellular respiration",
      "photosynthesis",
      "mitosis",
      "meiosis",
      "food chain",
      "food web",
      "energy pyramid",
      "natural selection",
      "evolution",
      "adaptation",

      // Rock and geology
      "igneous rock",
      "sedimentary rock",
      "metamorphic rock",
      "weathering and erosion",
      "chemical weathering",
      "physical weathering",
      "mineral formation",
      "fossil formation",
      "geological time",

      // Physics and chemistry
      "atomic structure",
      "periodic table",
      "chemical bonding",
      "states of matter",
      "phase transitions",
      "kinetic molecular theory",
      "conservation of energy",
      "conservation of momentum",
      "newtons laws",
      "electromagnetic spectrum",
      "wave properties",

      // Math concepts
      "quadratic formula",
      "pythagorean theorem",
      "distance formula",
      "linear equations",
      "systems of equations",
      "polynomial functions",
      "trigonometric functions",
      "unit circle",
      "law of sines",
      "law of cosines",
      "derivative rules",
      "integration techniques",
      "fundamental theorem of calculus",
      "probability distributions",
      "statistical analysis",
      "hypothesis testing",
      "data analysis",
      "sampling methods",

      // English concepts - Oral Communication, Reading and Writing, 21st Century
      "oral communication",
      "reading comprehension",
      "writing skills",
      "communication strategies",
      "presentation techniques",
      "public speaking",
      "text analysis",
      "critical reading",
      "essay writing",
      "composition techniques",
      "21st century skills",
      "digital literacy",
      "media literacy",

      // Filipino concepts - Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri
      "komunikasyon sa filipino",
      "pananaliksik sa wika",
      "pagbasa at pagsusuri",
      "filipino kultura",
      "wika at komunikasyon",
      "panitikan ng pilipinas",
      "akademikong pagsulat",
      "saliksik sa filipino",
      "tekstong ekspositori",
    ];

    const normalizedTopic = topic.toLowerCase().trim();
    return specificConcepts.some(
      (concept) => normalizedTopic.includes(concept) || concept.includes(normalizedTopic)
    );
  }

  /**
   * Calculate educational relevance score for a topic
   */
  private calculateEducationalRelevanceScore(
    topic: string,
    text: string,
    educationalKeywords: string[]
  ): number {
    let score = 0;

    // Base frequency score
    const frequency = text.split(topic).length - 1;
    score += frequency * 2;

    // Educational context bonus
    educationalKeywords.forEach((keyword) => {
      if (text.includes(keyword)) {
        score += 5;
      }
    });

    // Length bonus (prefer substantial terms)
    if (topic.length >= 6) score += 3;
    if (topic.length >= 10) score += 2;

    // Academic term bonus
    const academicIndicators = ["theory", "principle", "concept", "method", "analysis", "process"];
    if (academicIndicators.some((indicator) => topic.includes(indicator))) {
      score += 10;
    }

    // Penalize very common words
    const commonWords = ["information", "example", "important", "different", "possible"];
    if (commonWords.includes(topic)) {
      score -= 5;
    }

    return score;
  }

  /**
   * Enhanced summary generation with HuggingFace + Gemini + LangChain + Google Search
   * Optimized for parallel processing to minimize total time
   * SAFESEARCH: Content safety checks prevent explicit/inappropriate content processing
   * @param preferredLanguage - Optional language preference override ('english' or 'filipino')
   */
  async generateEnhancedSummaryWithHF(
    content: string,
    fileName?: string,
    preferredLanguage?: "english" | "filipino",
    sessionSubject?: string // ✅ NEW: Subject from completed session (bypasses HF subject detection)
  ): Promise<EnhancedSummaryResult> {
    // 🔍 TRACE: Log entry to identify duplicate calls
    const callId = `ENHANCED-SUMMARY-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.warn(`\n🚨🚨🚨 [${callId}] generateEnhancedSummaryWithHF() INVOKED 🚨🚨🚨`);
    console.warn(`   Content length: ${content.length}`);
    console.warn(`   File name: ${fileName || "none"}`);
    console.warn(`   Language: ${preferredLanguage || "auto"}`);
    console.warn(`   Session subject: ${sessionSubject || "none"}`);
    console.trace(`[${callId}] Call stack trace:`);

    // ⚡ OPTIMIZATION 1: Truncate extremely large content to prevent memory issues
    const MAX_CONTENT_LENGTH = 50000; // 50k characters max (~12k tokens)
    let processedContent = content;
    if (content.length > MAX_CONTENT_LENGTH) {
      logWarn(`Content too large (${content.length} chars), truncating to ${MAX_CONTENT_LENGTH}`, {
        originalLength: content.length,
        truncatedLength: MAX_CONTENT_LENGTH,
      });
      processedContent = content.substring(0, MAX_CONTENT_LENGTH);
      console.warn(`⚠️ Content truncated: ${content.length} → ${MAX_CONTENT_LENGTH} chars`);
    }

    // STEP 0: CONTENT SAFETY CHECK - Block explicit/inappropriate content
    const safetyStartTime = performance.now();
    console.log(`\n${"=".repeat(80)}`);
    console.log(`🔬 [CONTENT SAFETY CHECK] Started at ${new Date().toLocaleTimeString()}`);
    console.log(`   Content length: ${processedContent.length} chars`);

    logDebug("Performing content safety check before enhanced summary generation");
    const safetyCheck = checkContentSafety(processedContent);
    const safetyDuration = performance.now() - safetyStartTime;

    if (safetyCheck.blocked) {
      console.error(`❌ [CONTENT SAFETY CHECK] BLOCKED after ${safetyDuration.toFixed(0)}ms`);
      console.log(`   Severity: ${safetyCheck.severity}`);
      console.log(`   Reasons: ${safetyCheck.reasons.join(", ")}`);
      console.log(`${"=".repeat(80)}\n`);
      logError("Content blocked due to safety concerns", {
        severity: safetyCheck.severity,
        reasonCount: safetyCheck.reasons.length,
        fileName: fileName || "unnamed",
      });
      throw new Error(
        safetyCheck.message || "Content contains inappropriate material and cannot be processed."
      );
    }

    if (safetyCheck.severity === "medium") {
      console.warn(
        `⚠️ [CONTENT SAFETY CHECK] WARNING: Potentially inappropriate content (${safetyDuration.toFixed(
          0
        )}ms)`
      );
      console.log(`   Severity: ${safetyCheck.severity}`);
      console.log(`   Reasons: ${safetyCheck.reasons.join(", ")}`);
      console.log(`${"=".repeat(80)}\n`);
      logWarn("Content contains potentially inappropriate language", {
        severity: safetyCheck.severity,
        reasons: safetyCheck.reasons,
      });
    } else {
      console.log(`✅ [CONTENT SAFETY CHECK] PASSED in ${safetyDuration.toFixed(0)}ms`);
      console.log(`   Severity: ${safetyCheck.severity}`);
      console.log(`${"=".repeat(80)}\n`);
    }

    const overallStartTime = Date.now();

    try {
      logInfo("Starting enhanced AI processing pipeline", {
        contentLength: content.length,
        fileName: fileName || "unnamed",
        pipeline: "HF + Gemini + LangChain + Google Search",
        safetyStatus: "passed",
      });

      // Step 1: Backend HF Analysis (language only if sessionSubject provided)
      const hfStartTime = Date.now();
      console.log(`\n${"=".repeat(80)}`);
      console.log(`🔬 [HF LANGUAGE ANALYSIS CALL] Started at ${new Date().toLocaleTimeString()}`);
      console.log(`   Timeout: 10s`);

      if (sessionSubject) {
        console.log(
          `   ✅ Using session subject: ${sessionSubject} (skipping AI subject detection)`
        );
      }

      logDebug("Step 1: Running Backend HuggingFace content analysis...", {
        skipSubjectDetection: !!sessionSubject,
        sessionSubject: sessionSubject || "none",
      });

      // ✅ Skip subject detection if sessionSubject is provided
      const backendAIService = await getBackendAIService();

      // 🔒 CRITICAL FIX #3: Runtime validation of backendAIService
      if (!backendAIService || typeof backendAIService.analyzeContent !== "function") {
        logError("BackendAIService not properly initialized", {
          hasService: !!backendAIService,
          hasMethod: backendAIService ? typeof backendAIService.analyzeContent : "N/A",
        });
        throw new Error("Backend AI service initialization failed - cannot proceed with analysis");
      }

      const hfAnalysis: HFAnalysisResult = await this.withTimeout(
        backendAIService.analyzeContent(processedContent, !!sessionSubject).catch((err: Error) => {
          // 🔒 CRITICAL FIX #2: Error handling in Promise chain
          logError("Backend HF analysis failed, using fallback", err);
          return {
            language: preferredLanguage || "English",
            languageConfidence: 50,
            subject: sessionSubject || "General Knowledge",
            subjectConfidence: 50,
            isReady: false,
            processingTime: 0,
          } as HFAnalysisResult;
        }),
        10000, // 10 second timeout
        "Backend HuggingFace content analysis"
      );

      // 🔒 CRITICAL FIX #1: Null checks on hfAnalysis properties
      // ✅ Use session subject if provided, otherwise validate and fallback
      if (sessionSubject) {
        hfAnalysis.subject = sessionSubject;
        hfAnalysis.subjectConfidence = 100; // Session subject is authoritative
        logInfo("Using session subject instead of AI detection", {
          sessionSubject,
        });
      } else {
        // Ensure subject is never null/undefined
        if (!hfAnalysis.subject || hfAnalysis.subject.trim() === "") {
          hfAnalysis.subject = "General Knowledge";
          hfAnalysis.subjectConfidence = 50;
          logWarn("HF analysis returned empty subject, using fallback", {
            fallback: "General Knowledge",
          });
        }
      }

      // Ensure language is never null/undefined
      if (!hfAnalysis.language || hfAnalysis.language.trim() === "") {
        hfAnalysis.language = preferredLanguage || "English";
        hfAnalysis.languageConfidence = 50;
        logWarn("HF analysis returned empty language, using fallback", {
          fallback: hfAnalysis.language,
        });
      }

      const hfTime = Date.now() - hfStartTime;

      console.log(`✅ [HF LANGUAGE ANALYSIS CALL] Completed in ${hfTime}ms`);
      console.log(
        `   Language: ${hfAnalysis.language} (${hfAnalysis.languageConfidence}% confidence)`
      );
      console.log(
        `   Subject: ${hfAnalysis.subject} (${hfAnalysis.subjectConfidence}% confidence)${
          sessionSubject ? " [FROM SESSION]" : ""
        }`
      );
      console.log(`${"=".repeat(80)}\n`);

      logSuccess("HuggingFace analysis completed", {
        language: hfAnalysis.language,
        subject: hfAnalysis.subject,
        processingTime: `${hfTime}ms`,
        confidence: `Lang: ${hfAnalysis.languageConfidence}%, Subj: ${hfAnalysis.subjectConfidence}%`,
      });

      // Step 2: Sequential processing - Search FIRST, then Gemini WITH sources (factual backing)
      logDebug("Step 2: Sequential pipeline - Search → Gemini WITH sources...");
      const step2StartTime = Date.now();

      // Extract topics for search (enhanced with HF subject detection)
      const keyTopics = this.extractKeyTopicsWithHF(processedContent, hfAnalysis);

      // ✅ FIX #4: Sequential pipeline - Search first, THEN pass sources to Gemini
      // This ensures Gemini has credible sources to generate factual, backed-up summaries
      logDebug("Step 2A: Searching credible sources FIRST (to inform Gemini)...");
      const searchStartTime = Date.now();

      // ⚡ OPTIMIZATION 2: Reduce search timeout to 60s (from 90s)
      const credibleSourcesResult = await Promise.race([
        this.searchCredibleSourcesEnhanced(
          keyTopics,
          hfAnalysis.subject || "General Knowledge"
        ).catch((err: Error) => {
          // 🔒 CRITICAL FIX #2: Error handling in Promise.race branch
          logError("Credible source search failed, continuing with empty sources", err);
          return [];
        }),
        new Promise<string[]>((resolve) =>
          setTimeout(() => {
            logWarn("Source search timeout after 60s, proceeding with empty sources");
            resolve([]);
          }, 60000)
        ), // ⚡ Reduced to 60s - prevents long hangs
      ]);

      const credibleSources = Array.isArray(credibleSourcesResult) ? credibleSourcesResult : [];
      const searchTime = Date.now() - searchStartTime;

      logSuccess("Credible sources retrieved", {
        sourcesFound: credibleSources.length,
        searchTime: `${searchTime}ms`,
        topSources: credibleSources.slice(0, 3),
      });

      // 🚨 CRITICAL FIX: Filter out PDF/file URLs that cannot be scraped
      const scrapableSources = credibleSources.filter((url) => {
        if (this.isDirectFileUrl(url)) {
          logWarn("🚫 Filtered file URL (cannot scrape content)", {
            url: url.substring(0, 80),
            reason: "Direct file download - use HTML sources instead",
          });
          return false;
        }
        return true;
      });

      logInfo("✅ Filtered out non-scrapable file URLs", {
        original: credibleSources.length,
        afterFileFilter: scrapableSources.length,
        filtered: credibleSources.length - scrapableSources.length,
      });

      // 🚨 CRITICAL FIX: VALIDATE URLs and FILTER off-topic sources for SUMMARIES (same as flashcards)
      logDebug("🔍 Validating summary URLs to filter broken links...");
      const validationStartTime = Date.now();

      let validatedSources: string[] = [];
      if (scrapableSources.length > 0) {
        try {
          const backendAIServiceForValidation = await getBackendAIService();
          validatedSources = await backendAIServiceForValidation.validateUrls(scrapableSources, 5);
          const validationTime = Date.now() - validationStartTime;

          logSuccess("✅ Summary URL validation completed", {
            input: credibleSources.length,
            valid: validatedSources.length,
            broken: credibleSources.length - validatedSources.length,
            time: `${validationTime}ms`,
          });

          if (validatedSources.length < credibleSources.length) {
            logWarn(
              `⚠️ Filtered ${credibleSources.length - validatedSources.length} broken summary URLs`,
              {
                brokenUrls: credibleSources
                  .filter((url) => !validatedSources.includes(url))
                  .slice(0, 3),
              }
            );
          }
        } catch (error) {
          logError("URL validation failed for summaries, using unvalidated sources", error);
          validatedSources = credibleSources; // Fallback to original if validation fails
        }
      }

      // 🚨 CRITICAL FIX: Filter out off-topic sources for SUMMARIES (same as flashcards)
      const filteredSources = validatedSources.filter((source) => {
        const isOffTopic = this.isObviouslyOffTopic(source, hfAnalysis.subject, keyTopics);
        if (isOffTopic) {
          logWarn("Filtered off-topic source from summary sources", {
            url: source,
            subject: hfAnalysis.subject,
          });
        }
        return !isOffTopic;
      });

      // 🚨 CRITICAL FIX: Deduplicate sources for SUMMARIES (same as flashcards)
      const deduplicatedSources = filteredSources.filter((source, index, self) => {
        return (
          index ===
          self.findIndex((s) => {
            try {
              return this.normalizeUrl(s) === this.normalizeUrl(source);
            } catch {
              return s === source;
            }
          })
        );
      });

      logDebug("Summary source validation and filtering completed", {
        originalCount: credibleSources.length,
        afterValidation: validatedSources.length,
        afterOffTopicFilter: filteredSources.length,
        finalCount: deduplicatedSources.length,
        filtered: {
          broken: credibleSources.length - validatedSources.length,
          offTopic: validatedSources.length - filteredSources.length,
          duplicate: filteredSources.length - deduplicatedSources.length,
        },
      });

      // 🚨 CRITICAL: Enforce min/max (1-5 sources) with intelligent fallback strategies
      const MIN_QUALITY_THRESHOLD = 2; // ✅ NEW: Require at least 2 sources for quality, or fall back to 0
      let finalSources = deduplicatedSources;

      // Step 2A: Enforce MAX 5 sources (hard limit)
      if (finalSources.length > 5) {
        console.warn(`⚠️ Too many summary sources (${finalSources.length}), truncating to top 5`);
        logWarn("Truncating summary sources to maximum of 5", {
          original: finalSources.length,
          truncated: 5,
        });
        finalSources = finalSources.slice(0, 5);
      }

      // Step 2B: Quality threshold check - prefer 0 sources over single low-quality source
      if (finalSources.length > 0 && finalSources.length < MIN_QUALITY_THRESHOLD) {
        console.warn(
          `⚠️ Only ${finalSources.length} source(s) found - below quality threshold of ${MIN_QUALITY_THRESHOLD}`
        );
        logWarn("Source count below quality threshold - falling back to content-only generation", {
          sourcesFound: finalSources.length,
          threshold: MIN_QUALITY_THRESHOLD,
          reason: "Single source may be unreliable - better to use Gemini's knowledge",
        });
        finalSources = []; // ✅ FIX: Prefer 0 sources (Gemini's knowledge) over 1 questionable source
      }

      // Step 2C: Handle zero sources after quality check
      if (finalSources.length === 0) {
        console.warn(
          "⚠️ No valid summary sources found after all filters! Attempting fallback strategies..."
        );
        logWarn("No sources passed all validation filters", {
          originalCount: credibleSources.length,
          afterValidation: validatedSources.length,
          afterOffTopicFilter: filteredSources.length,
          afterDeduplication: deduplicatedSources.length,
        });

        // Fallback Strategy 1: Use validated sources (skip off-topic filter)
        if (validatedSources.length > 0) {
          console.log(
            `📋 Fallback Strategy 1: Using ${Math.min(
              validatedSources.length,
              5
            )} validated sources (skipping off-topic filter)`
          );
          logInfo("Fallback: Using validated sources without off-topic filtering", {
            count: Math.min(validatedSources.length, 5),
          });
          finalSources = validatedSources.slice(0, 5);
        }
        // Fallback Strategy 2: Use original sources (skip validation)
        else if (credibleSources.length > 0) {
          console.log(
            `📋 Fallback Strategy 2: Using ${Math.min(
              credibleSources.length,
              5
            )} original sources (skipping validation)`
          );
          logWarn("Fallback: Using unvalidated sources (all validated sources failed)", {
            count: Math.min(credibleSources.length, 5),
          });
          finalSources = credibleSources.slice(0, 5);
        }
        // Fallback Strategy 3: Generate without sources (last resort)
        else {
          console.warn(
            "⚠️ No sources available at all - generating summary without external sources"
          );
          logWarn("No sources available - generating summary from content only");
          finalSources = [];
        }
      }

      // Log final source count and validation
      console.log(`\n✅ Final sources for summary generation: ${finalSources.length} URLs`);
      if (finalSources.length > 0) {
        console.log(
          `   Sources:`,
          finalSources
            .map(
              (url, idx) =>
                `\n      ${idx + 1}. ${url.substring(0, 80)}${url.length > 80 ? "..." : ""}`
            )
            .join("")
        );
      }

      // Final validation check
      if (finalSources.length > 5) {
        console.error(`❌ CRITICAL: finalSources exceeds max limit (${finalSources.length} > 5)`);
        logError("Source count validation failed - truncating to 5", {
          count: finalSources.length,
        });
        finalSources = finalSources.slice(0, 5);
      }

      logSuccess("Summary source pipeline complete", {
        strategy:
          finalSources.length === deduplicatedSources.length
            ? "all-filters-passed"
            : "fallback-used",
        finalCount: finalSources.length,
        range: "0-5 sources (0 only as last resort)",
      });

      // Step 2B: Generate Gemini summary WITH credible sources for factual backing
      console.log(`\n${"=".repeat(80)}`);
      console.log(`🔬 [GEMINI PROCESSING] Started at ${new Date().toLocaleTimeString()}`);
      console.log(`   Model: gemini-2.5-pro (summary generation)`);
      console.log(`   Timeout: 90s (increased for source scraping + fact-checking)`);
      console.log(
        `   Sources provided: ${finalSources.length} (validated, filtered & enforced 0-5 range)`
      );

      logDebug("Step 2B: Generating Gemini summary WITH credible sources for factual accuracy...");
      const geminiStartTime = Date.now();

      // ⚡ OPTIMIZATION 3: Pass truncated content to Gemini to reduce processing time
      // ⏱️ TIMEOUT FIX: Increased from 30s to 90s because Gemini + source scraping + fact-checking takes ~54s
      const geminiResult = await this.withTimeout(
        this.generateEnhancedGeminiSummary(
          processedContent, // ⚡ Use truncated content
          fileName,
          hfAnalysis,
          preferredLanguage,
          finalSources // 🚨 ENFORCED: Pass min/max validated sources (0-5 range with fallbacks)
        ),
        90000, // ⏱️ FIX: 90s timeout (was 30s) - prevents premature timeout and retry
        "Gemini summary generation with sources"
      ).catch(async (error) => {
        const geminiErrorDuration = Date.now() - geminiStartTime;
        console.error(
          `❌ [GEMINI PROCESSING] Failed after ${geminiErrorDuration}ms, using fallback`
        );
        console.log(`${"=".repeat(80)}\n`);
        logWarn("Enhanced Gemini failed, using standard fallback", error);
        return await this.generateSummary(processedContent, fileName); // ⚡ Use truncated content in fallback
      });

      const geminiTime = Date.now() - geminiStartTime;
      console.log(`✅ [GEMINI PROCESSING] Completed in ${geminiTime}ms`);
      console.log(`${"=".repeat(80)}\n`);

      const sequentialTime = Date.now() - step2StartTime;

      // Step 3: Combine and enhance results
      logDebug("Step 3: Combining and enhancing results...");
      const enhancedResult = this.combineEnhancedResults(
        geminiResult,
        finalSources, // 🚨 ENFORCED: Use min/max validated sources
        hfAnalysis
      );

      const totalTime = Date.now() - overallStartTime;

      const finalResult: EnhancedSummaryResult = {
        ...enhancedResult,
        hfAnalysis,
        processingMetrics: {
          totalTime,
          hfTime,
          geminiTime,
          searchTime,
        },
        enhancementStatus: hfAnalysis.isReady ? "hf_enhanced" : "hf_fallback",
      };

      console.log(`\n${"=".repeat(80)}`);
      console.log(`✅ [PIPELINE COMPLETE] Total time: ${totalTime}ms`);
      console.log(`   Breakdown:`);
      console.log(`      • Content Safety Check: ${safetyDuration.toFixed(0)}ms`);
      console.log(`      • HF Language Analysis: ${hfTime}ms`);
      console.log(`      • Source Search: ${searchTime}ms`);
      console.log(`      • Gemini Processing: ${geminiTime}ms`);
      console.log(`   Results:`);
      console.log(`      • Language: ${hfAnalysis.language}`);
      console.log(`      • Subject: ${hfAnalysis.subject}`);
      console.log(
        `      • Sources found: ${deduplicatedSources.length} (${
          credibleSources.length
        } original, ${credibleSources.length - deduplicatedSources.length} filtered)`
      );
      console.log(`      • Enhancement: ${finalResult.enhancementStatus}`);
      console.log(`${"=".repeat(80)}\n`);

      logSuccess("Enhanced AI processing pipeline completed (SEQUENTIAL for factual accuracy)", {
        totalTime: `${totalTime}ms`,
        breakdown: `HF: ${hfTime}ms, Search: ${searchTime}ms, Gemini: ${geminiTime}ms`,
        pipelineFlow: "HF → Search → Validation → Off-topic Filter → Gemini (with sources)",
        language: hfAnalysis.language,
        subject: hfAnalysis.subject,
        sourcesOriginal: credibleSources.length,
        sourcesFiltered: deduplicatedSources.length,
        sourcesRemoved: credibleSources.length - deduplicatedSources.length,
        enhancementStatus: finalResult.enhancementStatus,
      });

      // 🔍 TRACE: Log completion to match with invocation
      console.warn(`✅✅✅ [${callId}] generateEnhancedSummaryWithHF() COMPLETED ✅✅✅`);
      console.warn(`   Total time: ${totalTime}ms`);
      console.warn(
        `   Sources returned: ${deduplicatedSources.length} (${credibleSources.length} original)`
      );

      return finalResult;
    } catch (error) {
      const fallbackTime = Date.now() - overallStartTime;
      logError("Enhanced processing failed, falling back to standard Gemini", error);

      // Fallback to standard Gemini processing
      const fallbackResult = await this.generateSummary(content, fileName);

      return {
        ...fallbackResult,
        processingMetrics: {
          totalTime: fallbackTime,
          hfTime: 0,
          geminiTime: fallbackTime,
          searchTime: 0,
        },
        enhancementStatus: "standard",
      };
    }
  }

  /**
   * Enhanced Gemini summary generation with HF insights AND credible sources
   * ✅ NEW: Fetches actual source content for fact-checking
   * @param preferredLanguage - Optional language preference override ('english' or 'filipino')
   * @param credibleSources - Array of credible source URLs to inform factual generation
   */
  private async generateEnhancedGeminiSummary(
    content: string,
    fileName: string | undefined,
    hfAnalysis: HFAnalysisResult,
    preferredLanguage?: "english" | "filipino",
    credibleSources?: string[]
  ): Promise<SummaryResult> {
    // Determine output language - use preference if provided, otherwise use HF analysis
    const outputLanguage = preferredLanguage || hfAnalysis.language;

    // Create enhanced prompt with HF insights and language enforcement
    const languageContext = this.buildLanguageContext(hfAnalysis, outputLanguage);
    const subjectContext = this.buildSubjectContext(hfAnalysis);

    // ✅ NEW: Fetch actual content from credible sources for fact-checking
    let sourcesContext = "";

    if (credibleSources && credibleSources.length > 0) {
      console.log(`\n${"=".repeat(80)}`);
      console.log(`📚 [SOURCE CONTENT FETCHING] Started at ${new Date().toLocaleTimeString()}`);
      console.log(`   Fetching content from ${credibleSources.length} sources for fact-checking`);

      logInfo("🔍 Fetching source content for fact-checking", {
        sourcesCount: credibleSources.length,
        purpose: "Cross-reference user content with verified educational sources",
      });

      const sourceContentStart = performance.now();

      // Fetch actual content from top 3 sources
      const backendAIService = await getBackendAIService();
      const sourceContents = await backendAIService.scrapeSources(
        credibleSources.slice(0, 3), // Top 3 sources only
        3
      );

      const sourceContentDuration = performance.now() - sourceContentStart;

      console.log(
        `✅ [SOURCE CONTENT FETCHING] Completed in ${sourceContentDuration.toFixed(0)}ms`
      );
      console.log(
        `   Successfully fetched: ${sourceContents.length}/${Math.min(
          3,
          credibleSources.length
        )} sources`
      );
      console.log(`${"=".repeat(80)}\n`);

      if (sourceContents.length > 0) {
        logSuccess("✅ Source content fetched successfully", {
          fetchedCount: sourceContents.length,
          totalWords: sourceContents.reduce(
            (sum: number, s: { word_count: number }) => sum + s.word_count,
            0
          ),
          duration: `${sourceContentDuration.toFixed(0)}ms`,
        });

        // Build enhanced context with ACTUAL source content
        sourcesContext = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎓 VERIFIED EDUCATIONAL SOURCE CONTENT (GROUND TRUTH FOR FACT-CHECKING)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${sourceContents
  .map(
    (source: { title: string; url: string; word_count: number; content: string }, idx: number) => `
📖 SOURCE ${idx + 1}: ${source.title}
🔗 URL: ${source.url}
📊 Word Count: ${source.word_count}

VERIFIED CONTENT:
${source.content.substring(0, 1500)} ${source.content.length > 1500 ? "..." : ""}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
  )
  .join("\n")}

🔴 CRITICAL FACT-CHECKING MANDATE (HIGHEST PRIORITY):

1. **CROSS-REFERENCE EVERY FACT**: Compare user's content with verified source content above
2. **DETECT ERRORS**: If user's formula/concept CONTRADICTS sources → USE SOURCE VERSION
3. **CORRECT MISINFORMATION**: If user has wrong interpretation → CORRECT using source
4. **SILENT CORRECTION**: Fix errors seamlessly without adding visible tags or markers
5. **NEVER PRESERVE ERRORS**: Do NOT keep user's wrong information "as-is"

FACT-CHECKING WORKFLOW:
Step 1: Extract key factual claims from user content (formulas, processes, definitions)
Step 2: Cross-reference EACH claim with verified source content above
Step 3: If contradiction found → Use source's version (corrected version becomes the output)
Step 4: Generate summary using ONLY verified facts
Step 5: Track corrections internally in correctionsApplied array for analytics

CORRECTION APPROACH (when user has errors):
- Present the CORRECT information naturally in the summary/keyPoints
- Do NOT add [CORRECTED FROM:...] or any other visible correction markers
- Document corrections in the correctionsApplied array only
- Example: If user wrote "P × r × t²", output should be "Simple Interest = P × r × t" (clean, no tags)

✅ FACT-CHECKING ENABLED: ${sourceContents.length} verified sources loaded
`;

        logInfo("📝 Enhanced prompt with source content for fact-checking", {
          sourcesWithContent: sourceContents.length,
          totalContentLength: sourceContents.reduce(
            (sum: number, s: { content: string }) => sum + s.content.length,
            0
          ),
        });
      } else {
        // ✅ FIX: Explicit warning when scraping fails
        logWarn("⚠️ No source content fetched for summary, using URLs only", {
          reason: "All sources failed to fetch or returned empty content",
        });

        const currentYear = new Date().getFullYear();

        // Fallback to URL-only context with explicit limitations
        sourcesContext = `

⚠️ LIMITED FACT-CHECKING MODE - SOURCE CONTENT UNAVAILABLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

VERIFIED SOURCE URLs (Content scraping failed):
${credibleSources
  .slice(0, 5)
  .map((source, idx) => `${idx + 1}. ${source}`)
  .join("\n")}

🔴 CRITICAL LIMITATIONS:
- Actual source content could not be retrieved (scraping failed)
- Cannot cross-reference user content against verified sources
- Must rely on general knowledge for fact-checking
- Higher risk of propagating user errors if present

📋 FALLBACK INSTRUCTIONS:
1. Use your training data to validate facts (up to ${currentYear})
2. Only correct obvious factual errors you're confident about
3. If uncertain, preserve user's original content
4. Add subtle note in key points: "Limited fact-checking - verify independently"
5. Set lower confidence in corrections

These URLs are verified as credible educational sources, but their content is not available for fact-checking.
`;
      }
    } else {
      logDebug("ℹ️ No credible sources provided, using content analysis only");

      const currentYear = new Date().getFullYear();
      const pastDecadeYear = currentYear - 10;

      sourcesContext = `

⚠️ NO EXTERNAL SOURCES AVAILABLE - CONTENT-ONLY MODE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔴 CRITICAL: No credible external sources were found for this topic.

📋 STRICT GUIDELINES:
1. Generate summary ONLY from user's provided content
2. Do NOT add facts not present in user's material
3. Use your training data (${pastDecadeYear}-${currentYear}) to verify obvious errors
4. If user content seems incorrect, note uncertainty in key points
5. Focus on clear presentation of the material provided
6. Recommend credible sources the student should consult for ${hfAnalysis.subject}

Note: Limited fact-checking capability - summary based on user content and general knowledge only.
`;
    }

    const enhancedPrompt = `
${languageContext}
${subjectContext}
${sourcesContext}

USER'S EDUCATIONAL NOTES (VERIFY AGAINST SOURCES ABOVE):
${content}

${fileName ? `Source File: ${fileName}` : ""}

ENHANCED PROCESSING INSTRUCTIONS based on AI pre-analysis:
- Content Language: ${hfAnalysis.language} (${hfAnalysis.languageConfidence}% confidence)
- Subject Area: ${hfAnalysis.subject} (${hfAnalysis.subjectConfidence}% confidence)
- Processing optimization: ${
      hfAnalysis.isReady ? "Full HF analysis available" : "Fallback analysis used"
    }

CRITICAL WRITING GUIDELINES:
- Write DIRECTLY about the concepts, NOT about "the text" or "the content"
- Use active voice and present tense
- Target senior high school students (Grade 11-12 level)
- Be factual, clear, and educational
- NO meta-commentary like "The text explains..." or "The content discusses..."
- ✅ NEW: If user's content contains errors, CORRECT them using verified sources
- ❌ NEVER include [CORRECTED FROM:...] or any correction markers in summary/keyPoints
- Present corrected information naturally and seamlessly

Please respond with the following information:
1. A comprehensive summary in paragraph form (150-300 words) - Write directly about the concepts as educational content
   - If corrections were made, present the CORRECT information naturally (no visible tags)
2. 5-8 key points in bullet form - Present facts and concepts directly
   - If corrections were made, use the CORRECT version naturally (no visible tags)
3. The main topic or title
4. The academic subject (validated: ${hfAnalysis.subject})
5. ✅ CRITICAL - SOURCES MUST BE FROM VALIDATED LIST ONLY:
   - DO NOT add any new sources not in the validated list above
   - DO NOT hallucinate or invent source URLs
   - ONLY use sources from the "VERIFIED SOURCE URLs" or "VERIFIED EDUCATIONAL SOURCE CONTENT" sections above
   - If no sources were provided above, return an empty sources array []
   - Better to have 0 sources than broken/hallucinated links
6. ✅ NEW: List of corrections applied in correctionsApplied array (for internal tracking only)

Format your response as JSON:
{
  "summary": "Direct educational explanation of concepts for Grade 11-12 students with CORRECTED facts (no visible correction markers)",
  "keyPoints": ["Direct fact 1", "Direct concept 2", "Direct principle 3", "Direct process 4", "Direct application 5"],
  "topic": "Main topic or title",
  "subject": "${hfAnalysis.subject}",
  "sources": ["ONLY verified, specific educational sources that were used for fact-checking"],
  "correctionsApplied": [
    {
      "userClaim": "User's incorrect statement or formula",
      "correction": "Correct statement from verified source",
      "sourceUrl": "URL of source that verified this",
      "severity": "critical"
    }
  ]
}

EXAMPLES OF GOOD vs BAD WRITING:
BAD: "The text explains that photosynthesis is a process..."
GOOD: "Photosynthesis is a process where plants convert light energy into chemical energy..."

BAD: "The content discusses the rock cycle..."
GOOD: "The rock cycle describes how rocks transform from one type to another through geological processes..."

CORRECTION EXAMPLE:
User wrote: "Simple Interest = P × r × t²"
Verified source says: "Simple Interest = P × r × t"
Your output in summary/keyPoints: "Simple Interest is calculated using the formula I = P × r × t" (clean, no tags)
Your output in correctionsApplied: {"userClaim": "P × r × t²", "correction": "P × r × t", "sourceUrl": "https://...", "severity": "critical"}

IMPORTANT: 
- Respond only with valid JSON
- Write for Filipino senior high school students
- Focus on ${hfAnalysis.subject} domain-specific terminology and concepts
- NO sources that are homepages, general sites, or unverified links
- ✅ FACT-CHECK: Cross-reference user content with verified sources and correct errors
- If no errors found, correctionsApplied should be an empty array []
`;

    try {
      console.log(`\n${"=".repeat(80)}`);
      console.log(`🤖 [GEMINI API CALL] Started at ${new Date().toLocaleTimeString()}`);
      console.log(`   Model: gemini-2.5-pro`);
      console.log(`   Task: Summary generation with fact-checking`);
      console.log(`   Timeout: 90s`);

      const result = await this.withTimeout(
        this.model.generateContent(enhancedPrompt),
        90000, // 90 second timeout for summary generation (allows for source scraping + fact-checking)
        "Gemini summary generation"
      );
      const response = result.response;

      console.log(`✅ [GEMINI API CALL] Completed successfully`);
      console.log(`${"=".repeat(80)}\n`);

      if (response.promptFeedback?.blockReason) {
        console.error(`❌ Content blocked by Gemini: ${response.promptFeedback.blockReason}`);
        throw new Error(`Content was blocked: ${response.promptFeedback.blockReason}`);
      }

      const text = response.text();
      if (!text || text.trim().length === 0) {
        console.error(`❌ Empty response from Gemini`);
        throw new Error("Empty response received from enhanced Gemini");
      }

      logDebug("📝 Parsing Gemini JSON response", {
        responseLength: text.length,
      });

      // Parse JSON response
      let jsonText = text.trim();
      jsonText = jsonText.replace(/```json\s*/, "").replace(/```\s*$/, "");
      const jsonMatch = jsonText.match(/\{[\s\S]*\}/);

      if (!jsonMatch) {
        logWarn("⚠️ No JSON found in enhanced Gemini response, using manual extraction");
        return this.extractContentManually(text, content);
      }

      const parsed = JSON.parse(jsonMatch[0]) as SummaryResult & {
        correctionsApplied?: Array<{
          userClaim: string;
          correction: string;
          sourceUrl: string;
          severity: string;
        }>;
      };

      // ✅ Log fact-checking results
      if (parsed.correctionsApplied && parsed.correctionsApplied.length > 0) {
        console.log(`\n${"=".repeat(80)}`);
        console.log(
          `🔴 [FACT-CHECKING RESULTS] ${parsed.correctionsApplied.length} error(s) corrected`
        );
        console.log(`${"=".repeat(80)}`);

        parsed.correctionsApplied.forEach((correction, idx) => {
          console.log(`\n❌ ERROR ${idx + 1} [${correction.severity.toUpperCase()}]:`);
          console.log(`   User's Claim: "${correction.userClaim}"`);
          console.log(`   ✅ Corrected To: "${correction.correction}"`);
          console.log(`   📚 Source: ${correction.sourceUrl}`);
        });

        console.log(`\n${"=".repeat(80)}\n`);

        logWarn("⚠️ User content contained factual errors - corrections applied", {
          errorsFound: parsed.correctionsApplied.length,
          criticalErrors: parsed.correctionsApplied.filter((c) => c.severity === "critical").length,
          corrections: parsed.correctionsApplied.map((c) => ({
            claim: c.userClaim.substring(0, 50),
            source: c.sourceUrl,
          })),
        });
      } else {
        console.log(
          `\n✅ [FACT-CHECKING RESULTS] No errors found - user content is factually accurate\n`
        );

        logSuccess("✅ Fact-checking complete - no errors found", {
          message: "User content matches verified sources",
        });
      }

      // ✅ FIX: Ensure all required fields exist before processing (prevent undefined errors)
      if (!parsed.summary) {
        parsed.summary = "Summary generation incomplete";
      }
      if (!parsed.topic) {
        parsed.topic = "Topic not detected";
      }
      if (!parsed.subject) {
        parsed.subject = hfAnalysis.subject || "General";
      }

      // Clean asterisk formatting from all text fields
      if (parsed.summary) {
        parsed.summary = this.cleanTextFromFormatting(parsed.summary);
      }

      if (parsed.topic) {
        parsed.topic = this.cleanTextFromFormatting(parsed.topic);
      }

      if (parsed.subject) {
        parsed.subject = this.cleanTextFromFormatting(parsed.subject);
      }

      // ✅ FIX: Validate and ensure arrays (allow empty arrays, don't treat as falsy)
      if (!Array.isArray(parsed.keyPoints)) {
        parsed.keyPoints =
          typeof parsed.keyPoints === "string"
            ? [parsed.keyPoints]
            : ["Enhanced analysis completed"];
      } else {
        // ✅ CRITICAL FIX: Filter out non-string keyPoints and clean formatting
        parsed.keyPoints = parsed.keyPoints
          .filter((point) => point && typeof point === "string")
          .map((point) => this.cleanTextFromFormatting(point));
      }

      // ✅ FIX: Always initialize sources as array (even if empty from JSON)
      if (!Array.isArray(parsed.sources)) {
        parsed.sources = typeof parsed.sources === "string" ? [parsed.sources] : [];
        logDebug("Initialized sources array (was not array in JSON response)");
      } else {
        // ✅ CRITICAL FIX: Filter out non-string sources and clean formatting
        // Gemini sometimes returns objects/nulls instead of strings
        parsed.sources = parsed.sources
          .filter((source) => source && typeof source === "string")
          .map((source) => this.cleanSourceText(source));
      }

      // ✅ CRITICAL FIX: Filter out 404/error page sources from correctionsApplied
      // Gemini sometimes includes sources that are broken/error pages in fact-checking
      if (parsed.correctionsApplied && Array.isArray(parsed.correctionsApplied)) {
        // Extract correction source URLs
        const correctionSourceUrls = parsed.correctionsApplied
          .filter((c) => c && c.sourceUrl && typeof c.sourceUrl === "string")
          .map((c) => c.sourceUrl);

        // Check if any correction sources are already in main sources
        const correctionSourcesInMain = correctionSourceUrls.filter((url) =>
          parsed.sources.some((source) => source.includes(url))
        );

        if (correctionSourcesInMain.length > 0) {
          logDebug(
            `🔍 Found ${correctionSourcesInMain.length} correction sources in main sources array`,
            {
              correctionSources: correctionSourcesInMain.slice(0, 3),
            }
          );

          // Filter out likely broken sources (404, error pages, off-topic)
          const filteredSources = parsed.sources.filter((source) => {
            // Keep sources that are NOT from corrections
            const isFromCorrections = correctionSourceUrls.some((url) => source.includes(url));

            if (!isFromCorrections) {
              return true; // Keep non-correction sources
            }

            // For correction sources, apply strict validation
            const url = source.match(/https?:\/\/[^\s]+/)?.[0];
            if (!url) return false;

            // ✅ Filter out URLs known to have issues
            const isSuspicious =
              this.isLikelyBrokenUrl(url) ||
              url.includes("/404") ||
              url.includes("/error") ||
              url.includes("page-not-found") ||
              url.includes("nifdi.org") || // Known to have 404s
              url.includes("/blog/"); // Blog posts often get removed

            if (isSuspicious) {
              logWarn(
                `🚫 Filtered correction source (likely 404/error page): ${url.substring(0, 60)}...`
              );
              return false;
            }

            return true;
          });

          if (filteredSources.length < parsed.sources.length) {
            logSuccess(
              `✅ Removed ${
                parsed.sources.length - filteredSources.length
              } suspicious correction sources`,
              {
                before: parsed.sources.length,
                after: filteredSources.length,
              }
            );
            parsed.sources = filteredSources;
          }
        }
      }

      // Ensure subject matches HF analysis
      parsed.subject = hfAnalysis.subject;

      logSuccess("Enhanced Gemini processing completed", {
        subject: parsed.subject,
        keyPointsCount: parsed.keyPoints.length,
        sourcesCount: parsed.sources.length,
      });

      return parsed;
    } catch (error) {
      // 🔍 DETAILED ERROR LOGGING: Identify what's causing fallback trigger
      console.error(`\n${"=".repeat(80)}`);
      console.error(`❌ [FALLBACK TRIGGERED] generateEnhancedGeminiSummary() failed`);
      console.error(`   Error type: ${error instanceof Error ? error.name : typeof error}`);
      console.error(`   Error message: ${error instanceof Error ? error.message : String(error)}`);
      console.error(`   Stack trace:`);
      console.error(error);
      console.error(`${"=".repeat(80)}\n`);

      logWarn("Enhanced Gemini processing failed, using standard processing", error);

      // ⚠️ WARNING: This fallback runs COMPLETE DUPLICATE PIPELINE (HF + Search + Gemini)
      console.warn(`⚠️ Fallback will run DUPLICATE pipeline with HF analysis + Search + Gemini`);
      return await this.generateSummary(content, fileName);
    }
  }

  /**
   * Enhanced topic extraction using HF analysis
   */
  private extractKeyTopicsWithHF(content: string, hfAnalysis: HFAnalysisResult): string[] {
    // Start with standard topic extraction
    const standardTopics = this.extractKeyTopics(content);

    // Enhance with HF subject classification
    const subjectKeywords = this.getSubjectKeywords(hfAnalysis.subject);

    // IMPORTANT: Prioritize SPECIFIC topics over generic subject names
    // Don't put the subject first - put actual content topics first
    const enhancedTopics = [
      ...standardTopics.slice(0, 3), // Prioritize content-specific topics
      ...subjectKeywords.slice(0, 2), // Add subject keywords as context
      // Only add subject name if no specific topics found
      ...(standardTopics.length === 0 ? [hfAnalysis.subject] : []),
    ];

    // Remove duplicates and return top 5
    const uniqueTopics = [...new Set(enhancedTopics)].slice(0, 5);

    logDebug("Enhanced topic extraction with HF", {
      originalTopics: standardTopics.slice(0, 3),
      hfSubject: hfAnalysis.subject,
      enhancedTopics: uniqueTopics,
      prioritization: "content-specific topics BEFORE subject keywords",
    });

    return uniqueTopics;
  }

  /**
   * Enhanced credible source search with strict validation
   * Delegates to LangChain Search Service for better performance
   * ✅ UPDATED: Falls back to backend search service when frontend keys are not available
   */
  private async searchCredibleSourcesEnhanced(
    topics: string[],
    hfSubject: string
  ): Promise<string[]> {
    // ✅ Use backend search service (secure mode - API keys on server)
    // This is the CORRECT configuration - API keys should be secured on backend
    if (!this.googleSearchService) {
      logInfo("✅ Using backend search service (secure mode - API keys on server)", {
        topics: topics.slice(0, 3),
        subject: hfSubject,
        mode: "backend-proxy",
      });

      try {
        // Import backend AI service for secure search
        const { backendAIService } = await import("./backendAIService");

        // Call backend to perform Google Search retrieval + HF reranking
        const sources = await backendAIService.searchCredibleSources(topics, hfSubject);

        logSuccess("Backend search completed successfully", {
          sourcesFound: sources.length,
          topSources: sources.slice(0, 3),
        });

        return sources;
      } catch (error) {
        logError("Backend search failed, returning empty results", { topics, hfSubject, error });
        return [];
      }
    }

    // Frontend Google Search service is available (fallback mode)
    try {
      return await this.googleSearchService.searchCredibleSourcesEnhanced(topics, hfSubject);
    } catch (error) {
      logError("Error in enhanced credible source search", { topics, hfSubject, error });
      return [];
    }
  }

  /**
   * Enhanced credible source search specifically optimized for FLASHCARDS
   * Delegates to Google Search Service for better performance
   * Flashcards require focused, high-quality sources (minimum 3, maximum 5)
   * ✅ Uses backend search service (secure mode - API keys on server)
   */
  private async searchCredibleSourcesForFlashcards(
    topics: string[],
    hfSubject: string
  ): Promise<string[]> {
    // ✅ Use backend search service (secure mode - API keys on server)
    // This is the CORRECT configuration - API keys should be secured on backend
    if (!this.googleSearchService) {
      logInfo("✅ Using backend search service for flashcards (secure mode - API keys on server)", {
        topics: topics.slice(0, 3),
        subject: hfSubject,
        mode: "backend-proxy",
      });

      try {
        // Import backend AI service for secure search
        const { backendAIService } = await import("./backendAIService");

        // Call backend to perform Google Search retrieval + HF reranking
        const sources = await backendAIService.searchCredibleSources(topics, hfSubject);

        logSuccess("Backend flashcard search completed successfully", {
          sourcesFound: sources.length,
          topSources: sources.slice(0, 3),
        });

        return sources;
      } catch (error) {
        logError("Backend flashcard search failed, returning empty results", {
          topics,
          hfSubject,
          error,
        });
        return [];
      }
    }

    // Frontend Google Search service is available (fallback mode)
    try {
      return await this.googleSearchService.searchCredibleSourcesForFlashcards(topics, hfSubject);
    } catch (error) {
      logError("Error in flashcard credible source search", { topics, hfSubject, error });
      return [];
    }
  }

  /**
   * Build language context for enhanced Gemini prompt
   * @param outputLanguage - The language to enforce in the output ('english', 'filipino', or detected language)
   */
  private buildLanguageContext(hfAnalysis: HFAnalysisResult, outputLanguage?: string): string {
    const targetLanguage = outputLanguage || hfAnalysis.language;

    switch (targetLanguage) {
      case "filipino":
        return `LANGUAGE CONTEXT: Content detected as Filipino language. 
STRICT OUTPUT REQUIREMENT: Generate ALL output in FILIPINO/TAGALOG language ONLY.
- Write summary, key points, topic, and subject in Filipino/Tagalog
- Use appropriate Filipino educational terminology
- Cultural sensitivity for Philippine educational context
- NO English words or phrases in the output`;

      case "english":
        return `LANGUAGE CONTEXT: Content detected as English language.
STRICT OUTPUT REQUIREMENT: Generate ALL output in ENGLISH language ONLY.
- Write summary, key points, topic, and subject in English
- Use proper English grammar and vocabulary
- NO Filipino/Tagalog words or phrases in the output`;

      case "mixed":
        // If still mixed (shouldn't happen with user choice), default to English
        return `LANGUAGE CONTEXT: Mixed language content detected.
STRICT OUTPUT REQUIREMENT: Generate ALL output in ENGLISH language ONLY.
- Write summary, key points, topic, and subject in English
- Use proper English grammar and vocabulary
- NO Filipino/Tagalog words or phrases in the output`;

      default:
        return `LANGUAGE CONTEXT: Content detected as ${targetLanguage} language.
STRICT OUTPUT REQUIREMENT: Generate ALL output in ENGLISH language ONLY.`;
    }
  }

  /**
   * Build subject context for enhanced Gemini prompt
   */
  private buildSubjectContext(hfAnalysis: HFAnalysisResult): string {
    const subjectGuidance = {
      mathematics:
        "Focus on mathematical concepts, formulas, problem-solving approaches, and quantitative analysis",
      science:
        "Emphasize scientific method, experimental procedures, theoretical frameworks, and evidence-based conclusions",
      english:
        "Highlight communication skills, reading comprehension, writing techniques, and critical analysis",
      filipino:
        "Focus on Philippine culture, language development, communication skills, and national identity concepts",
      "earth and life science":
        "Emphasize biological processes, earth systems, environmental science, and life science concepts",
      "physical science":
        "Focus on physics, chemistry, energy, matter, forces, and scientific principles",
      "earth science": "Emphasize geology, earth systems, atmospheric science, and earth processes",
      "general mathematics":
        "Focus on fundamental mathematical concepts, problem-solving, and practical applications",
      "statistics and probability":
        "Emphasize data analysis, statistical methods, probability theory, and mathematical reasoning",
      "oral communication":
        "Focus on speaking skills, presentation techniques, verbal communication, and public speaking",
      "reading and writing":
        "Emphasize reading comprehension, writing skills, text analysis, and composition techniques",
      "21st century":
        "Focus on contemporary literature, modern communication, and current cultural contexts",
      "komunikasyon at pananaliksik":
        "Focus on Filipino communication skills, research methods, and academic writing in Filipino",
      "pagbasa at pagsusuri":
        "Emphasize Filipino reading comprehension, text analysis, and critical thinking in Filipino context",
    };

    const guidance =
      subjectGuidance[hfAnalysis.subject as keyof typeof subjectGuidance] ||
      "Apply general educational best practices and comprehensive analysis";

    return `SUBJECT CONTEXT: Content classified as ${hfAnalysis.subject} (${hfAnalysis.subjectConfidence}% confidence).
Subject-specific guidance: ${guidance}`;
  }

  /**
   * Get subject-specific keywords for enhanced search
   * Aligned with Firebase subject names: Oral Communication, Reading and Writing, 21st Century,
   * Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri, General Mathematics, Statistics and Probability,
   * Earth and Life Science, Physical Science, Earth Science
   */
  private getSubjectKeywords(subject: string): string[] {
    const subjectKeywords = {
      mathematics: ["algebra", "geometry", "calculus", "statistics", "problem solving"],
      science: ["biology", "chemistry", "physics", "experiment", "hypothesis"],
      english: ["communication", "grammar", "writing", "reading comprehension", "essay"],
      filipino: ["kultura", "komunikasyon", "wika", "panitikan", "lipunan"],
      // Sciences - Firebase subject names
      "earth and life science": [
        "biology",
        "ecology",
        "environment",
        "earth systems",
        "life processes",
        "geology",
        "organisms",
      ],
      "physical science": ["physics", "chemistry", "energy", "matter", "forces", "motion"],
      "earth science": [
        "geology",
        "earth systems",
        "minerals",
        "rocks",
        "atmosphere",
        "weathering",
      ],
      // Mathematics - Firebase subject names
      "general mathematics": [
        "algebra",
        "geometry",
        "basic math",
        "problem solving",
        "functions",
        "equations",
      ],
      "statistics and probability": [
        "data analysis",
        "statistics",
        "probability",
        "distributions",
        "sampling",
        "hypothesis testing",
      ],
      // English - Firebase subject names
      "oral communication": [
        "speaking",
        "presentation",
        "verbal skills",
        "public speaking",
        "communication",
        "listening",
      ],
      "reading and writing": [
        "reading comprehension",
        "writing skills",
        "text analysis",
        "composition",
        "literacy",
        "grammar",
      ],
      "21st century": ["contemporary", "modern", "current", "digital age", "global", "literature"],
      // Filipino - Firebase subject names
      "komunikasyon at pananaliksik": [
        "komunikasyon",
        "pananaliksik",
        "wika",
        "akademik",
        "saliksik",
        "research",
      ],
      "pagbasa at pagsusuri": [
        "pagbasa",
        "pagsusuri",
        "teksto",
        "analisis",
        "pag-unawa",
        "panitikan",
      ],
    };

    return (
      subjectKeywords[subject as keyof typeof subjectKeywords] || ["education", "learning", "study"]
    );
  }

  /**
   * Build subject-specific search queries
   * Aligned with Firebase subject names: Oral Communication, Reading and Writing, 21st Century,
   * Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri, General Mathematics, Statistics and Probability,
   * Earth and Life Science, Physical Science, Earth Science
   */
  private buildSubjectSpecificQueries(topics: string[], subject: string): string[] {
    const baseQueries = topics.map(
      (topic) =>
        `${topic} definition explanation -program -course -degree -university -login -error -404 -session`
    );

    // Add subject-specific content searches with quality filters to avoid broken links
    const domainQueries = {
      mathematics: [
        `mathematics concept theorem -program -course -login -error site:britannica.com OR site:mathworld.wolfram.com OR site:.edu`,
      ],
      science: [
        `science definition principle -program -course -login -error site:britannica.com OR site:nasa.gov OR site:.gov`,
      ],
      english: [
        `english communication analysis -program -course -login -error site:britannica.com OR site:.edu`,
      ],
      filipino: [
        `filipino research analysis -program -course -login -error site:britannica.com OR site:.edu.ph`,
      ],
      // Sciences - Firebase subject names
      "earth and life science": [
        `earth life science biology ecology geology -program -course -login -error site:britannica.com OR site:nasa.gov OR site:usgs.gov`,
      ],
      "physical science": [
        `physical science physics chemistry energy -program -course -login -error site:britannica.com OR site:nasa.gov OR site:.gov`,
      ],
      "earth science": [
        `earth science geology rocks minerals weathering -program -course -login -error site:britannica.com OR site:usgs.gov`,
      ],
      // Mathematics - Firebase subject names
      "general mathematics": [
        `general mathematics algebra geometry equations -program -course -login -error site:britannica.com OR site:mathworld.wolfram.com`,
      ],
      "statistics and probability": [
        `statistics probability data analysis distributions -program -course -login -error site:britannica.com OR site:mathworld.wolfram.com`,
      ],
      // English - Firebase subject names
      "oral communication": [
        `oral communication speaking presentation listening -program -course -login -error site:britannica.com OR site:.edu`,
      ],
      "reading and writing": [
        `reading writing comprehension composition grammar -program -course -login -error site:britannica.com OR site:.edu`,
      ],
      "21st century": [
        `21st century literature contemporary modern -program -course -login -error site:britannica.com OR site:.edu`,
      ],
      // Filipino - Firebase subject names
      "komunikasyon at pananaliksik": [
        `komunikasyon pananaliksik filipino wika saliksik -program -course -login -error site:.edu.ph OR site:britannica.com`,
      ],
      "pagbasa at pagsusuri": [
        `pagbasa pagsusuri filipino teksto panitikan -program -course -login -error site:.edu.ph OR site:britannica.com`,
      ],
    };

    const subjectSpecific = domainQueries[subject as keyof typeof domainQueries] || [];

    return [...baseQueries, ...subjectSpecific];
  }

  /**
   * Parse enhanced search results with subject context
   */
  /**
   * Legacy method - now redirects to enhanced validation
   * @deprecated Use parseAndValidateSearchResults instead
   */
  private async parseEnhancedSearchResults(
    searchResults: string,
    subject: string
  ): Promise<string[]> {
    logDebug("Using legacy parseEnhancedSearchResults - redirecting to enhanced validation");

    // Use the new enhanced validation method
    const validatedSources = await this.parseAndValidateSearchResults(searchResults, subject);
    return validatedSources.map((source) => source.url);
  }

  /**
   * Enhanced credible source validation
   */
  private isEnhancedCredibleSource(url: string, subject: string): boolean {
    const urlLower = url.toLowerCase();

    // Basic credible domains
    const credibleDomains = [".edu", ".gov", "scholar.google", "jstor.org", "researchgate.net"];
    const isBasicCredible = credibleDomains.some((domain) => urlLower.includes(domain));

    // Subject-specific credible sources
    const subjectDomains = {
      mathematics: ["nctm.org", "ams.org", "mathworld.wolfram.com"],
      science: ["nasa.gov", "nsta.org", "ncbi.nlm.nih.gov", "nature.com"],
      english: ["ncte.org", "mla.org", "poetryfoundation.org"],
      filipino: ["deped.gov.ph", "up.edu.ph", "ateneo.edu", "dlsu.edu.ph"],
      "earth and life science": ["nasa.gov", "nsta.org", "ncbi.nlm.nih.gov", "usgs.gov"],
      "physical science": ["nasa.gov", "nsta.org", "nist.gov", "britannica.com"],
      "earth science": ["nasa.gov", "usgs.gov", "nsta.org", "britannica.com"],
      "general mathematics": ["nctm.org", "ams.org", "mathworld.wolfram.com"],
      "statistics and probability": ["nctm.org", "ams.org", "mathworld.wolfram.com"],
      "oral communication": ["ncte.org", "mla.org", "deped.gov.ph"],
      "reading and writing": ["ncte.org", "mla.org", "deped.gov.ph"],
      "21st century": ["ncte.org", "mla.org", "deped.gov.ph"],
      "komunikasyon at pananaliksik": ["deped.gov.ph", "up.edu.ph", "ateneo.edu"],
      "pagbasa at pagsusuri": ["deped.gov.ph", "up.edu.ph", "ateneo.edu"],
    };

    const subjectSpecific = subjectDomains[subject as keyof typeof subjectDomains] || [];
    const isSubjectCredible = subjectSpecific.some((domain) => urlLower.includes(domain));

    return isBasicCredible || isSubjectCredible;
  }

  /**
   * Get source type for enhanced display
   */
  private getSourceType(url: string): string {
    if (url.includes(".edu")) return "Educational Institution";
    if (url.includes(".gov")) return "Government Resource";
    if (url.includes("scholar.google")) return "Academic Paper";
    if (url.includes("jstor") || url.includes("researchgate")) return "Research Database";
    return "Academic Source";
  }

  /**
   * Get fallback sources specific to Philippine curriculum subjects
   * Only return truly relevant sources, not generic ones
   * Aligned with Firebase subject names: Oral Communication, Reading and Writing, 21st Century,
   * Komunikasyon at Pananaliksik, Pagbasa at Pagsusuri, General Mathematics, Statistics and Probability,
   * Earth and Life Science, Physical Science, Earth Science
   */
  private getFallbackSourcesForSubject(subject: string): string[] {
    const fallbackSources = {
      // English subjects - Firebase subject names
      "oral communication": [
        "https://www.britannica.com/topic/oral-communication",
        "https://scholar.google.com/scholar?q=oral+communication+skills",
      ],
      "reading and writing": [
        "https://www.britannica.com/topic/literacy",
        "https://scholar.google.com/scholar?q=reading+comprehension+writing+skills",
      ],
      "21st century": [
        "https://www.britannica.com/art/literature",
        "https://scholar.google.com/scholar?q=21st+century+literature",
      ],

      // Filipino subjects - Firebase subject names
      "komunikasyon at pananaliksik": [
        "https://scholar.google.com/scholar?q=filipino+communication+research",
        "https://www.britannica.com/place/Philippines/Languages",
      ],
      "pagbasa at pagsusuri": [
        "https://scholar.google.com/scholar?q=text+analysis+reading+comprehension",
        "https://www.britannica.com/topic/reading-education",
      ],

      // Mathematics subjects - Firebase subject names
      "general mathematics": [
        "https://mathworld.wolfram.com/",
        "https://www.britannica.com/science/mathematics",
      ],
      "statistics and probability": [
        "https://mathworld.wolfram.com/Probability.html",
        "https://www.britannica.com/science/statistics",
      ],

      // Science subjects - Firebase subject names
      "earth and life science": [
        "https://www.britannica.com/science/Earth-sciences",
        "https://www.britannica.com/science/life-science",
      ],
      "physical science": [
        "https://www.britannica.com/science/physical-science",
        "https://www.britannica.com/science/physics",
      ],
      "earth science": [
        "https://www.britannica.com/science/Earth-sciences",
        "https://www.usgs.gov/science/",
      ],

      // Legacy fallback subjects for compatibility - specific content only
      mathematics: [
        "https://mathworld.wolfram.com/",
        "https://www.britannica.com/science/mathematics",
      ],
      science: [
        "https://www.britannica.com/science",
        "https://scholar.google.com/scholar?q=science+education",
      ],
      english: [
        "https://www.britannica.com/topic/English-language",
        "https://scholar.google.com/scholar?q=english+communication",
      ],
      filipino: [
        "https://www.britannica.com/place/Philippines/Languages",
        "https://scholar.google.com/scholar?q=filipino+language+culture",
      ],
    };

    const sources = fallbackSources[subject.toLowerCase() as keyof typeof fallbackSources];

    // Return specific subject sources or empty array if no specific sources available
    return sources || [];
  }

  /**
   * Combine enhanced results from all AI services
   */
  private combineEnhancedResults(
    geminiResult: SummaryResult,
    credibleSources: string[],
    hfAnalysis: HFAnalysisResult
  ): SummaryResult {
    // CRITICAL FIX: Prioritize searched credible sources over Gemini's generic sources
    // This ensures we always show real academic URLs, not generic fallback text
    let finalSources: string[];

    if (credibleSources.length > 0) {
      // ✅ NEW: Filter out off-topic sources BEFORE adding to final list
      const filteredCredibleSources = credibleSources.filter((source) => {
        const isOffTopic = this.isObviouslyOffTopic(
          source,
          hfAnalysis.subject,
          geminiResult.keyPoints || []
        );
        if (isOffTopic) {
          logWarn("Filtered off-topic source from credible sources", {
            url: source,
            subject: hfAnalysis.subject,
          });
        }
        return !isOffTopic;
      });

      // Start with filtered credible sources from search (these are the real URLs)
      finalSources = [...filteredCredibleSources.slice(0, 5)];

      // ✅ CRITICAL FIX: STRICTLY validate Gemini sources against search results
      // Prevent Gemini from adding hallucinated URLs
      const validatedSourceSet = new Set(
        filteredCredibleSources.map((url) => this.normalizeUrl(url))
      );

      const geminiSources = Array.isArray(geminiResult.sources) ? geminiResult.sources : [];

      // Only add Gemini sources if they EXACTLY match validated sources from Google Search
      geminiSources.forEach((geminiSource) => {
        if (!geminiSource || typeof geminiSource !== "string") return;

        const cleanedSource = this.cleanSourceText(geminiSource);
        const normalizedGemini = this.normalizeUrl(cleanedSource);

        // ONLY allow if it matches a validated source from Google Search
        if (validatedSourceSet.has(normalizedGemini)) {
          if (!this.isDuplicateUrl(cleanedSource, finalSources)) {
            finalSources.push(cleanedSource);
          }
        } else {
          // Gemini tried to add a source not in our validated list
          logWarn("🚫 Filtered Gemini hallucinated source (not in validated list)", {
            geminiSource: cleanedSource.substring(0, 80),
            reason: "Source not from Google Search validation pipeline",
          });
        }
      });

      logSuccess("Prioritized searched credible sources for summary", {
        totalSources: finalSources.length,
        searchedSources: credibleSources.length,
        geminiSourcesCount: geminiSources.length,
      });
    } else {
      // No credible sources found - use Gemini's sources or fallback
      finalSources = [...geminiResult.sources];
      if (finalSources.length === 0) {
        finalSources = ["AI-generated summary - verify with authoritative sources"];
      }
    }

    // Keep only educational content in key points - no AI metadata for students
    const enhancedKeyPoints = [...geminiResult.keyPoints];

    // ✅ CRITICAL FIX: Deduplicate sources before returning to prevent duplicates
    const deduplicatedSources = finalSources.filter((source, index, self) => {
      // Keep only first occurrence of each normalized URL
      return (
        index ===
        self.findIndex((s) => {
          try {
            return this.normalizeUrl(s) === this.normalizeUrl(source);
          } catch {
            // Fallback to simple string comparison if URL parsing fails
            return s === source;
          }
        })
      );
    });

    logDebug(`✅ Deduplicated sources: ${finalSources.length} → ${deduplicatedSources.length}`);

    // ✅ CRITICAL FIX: Hard limit to 5 sources (min/max enforcement)
    const finalLimitedSources = deduplicatedSources.slice(0, 5);

    if (deduplicatedSources.length > 5) {
      logWarn(`⚠️ Truncated sources from ${deduplicatedSources.length} to 5 (max enforcement)`, {
        removed: deduplicatedSources.length - 5,
      });
    }

    return {
      ...geminiResult,
      keyPoints: enhancedKeyPoints,
      sources: finalLimitedSources, // ✅ FIX: Hard limit to 5 sources (never exceed)
      subject: hfAnalysis.subject, // Use HF-detected subject
    };
  }

  async generateSummary(content: string, fileName?: string): Promise<SummaryResult> {
    // STEP 0: CONTENT SAFETY CHECK - Block explicit/inappropriate content
    logDebug("Performing content safety check before summary generation");
    const safetyCheck = checkContentSafety(content);

    if (safetyCheck.blocked) {
      logError("Content blocked due to safety concerns", {
        severity: safetyCheck.severity,
        reasonCount: safetyCheck.reasons.length,
        fileName: fileName || "unnamed",
      });
      throw new Error(
        safetyCheck.message || "Content contains inappropriate material and cannot be processed."
      );
    }

    if (safetyCheck.severity === "medium") {
      logWarn("Content contains potentially inappropriate language", {
        severity: safetyCheck.severity,
        reasons: safetyCheck.reasons,
      });
    }

    // Check if API key is configured
    if (!API_KEY || API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
      throw new Error(
        "Gemini API key not configured. Please add your API key to the environment variables."
      );
    }

    try {
      logInfo("Starting enhanced summary generation with HF analysis and LangChain integration", {
        safetyStatus: "passed",
      });

      // Step 1: Backend HF Language & Subject Detection (REQUIRED for proper reranking)
      const optimizedHFContent = content.substring(0, 1000);
      const backendAIService = await getBackendAIService();
      const hfAnalysis: HFAnalysisResult = await backendAIService.analyzeContent(
        optimizedHFContent
      );

      logInfo("HF Analysis completed for summary", {
        language: hfAnalysis.language,
        subject: hfAnalysis.subject,
        confidence: hfAnalysis.subjectConfidence,
      });

      // Step 2: Extract key topics for search
      const keyTopics = this.extractKeyTopicsWithHF(content, hfAnalysis);

      // Step 3: Search for credible sources using LangChain + HF Reranker (with subject context)
      const credibleSources = await this.searchCredibleSourcesEnhanced(
        keyTopics,
        hfAnalysis.subject
      );

      // Step 3.5: Scrape actual content from credible sources
      let sourcesContext = "";
      const currentYear = new Date().getFullYear();
      const pastDecadeYear = currentYear - 10;
      const successfulSourceUrls: string[] = [];

      if (credibleSources.length > 0) {
        logInfo("🔍 Scraping content from credible sources for summary enhancement", {
          sourcesCount: credibleSources.length,
        });

        try {
          const scrapedSources = await backendAIService.scrapeSources(
            credibleSources.slice(0, 3),
            3
          );

          const successfulScrapes = scrapedSources.filter((s: any) => s.success && s.content);

          if (successfulScrapes.length > 0) {
            // Track successful URLs (with deduplication and topic validation)
            successfulScrapes.forEach((s: any) => {
              // Check for duplicates
              if (this.isDuplicateUrl(s.url, successfulSourceUrls)) {
                logDebug("Skipping duplicate scraped source", { url: s.url });
                return;
              }

              // Check for off-topic sources
              if (this.isObviouslyOffTopic(s.url, hfAnalysis?.subject || "", keyTopics || [])) {
                logWarn("Skipping off-topic scraped source", {
                  url: s.url,
                  subject: hfAnalysis?.subject,
                });
                return;
              }

              successfulSourceUrls.push(s.url);
            });

            const scrapedContent = successfulScrapes
              .map(
                (source: any) =>
                  `SOURCE: ${source.url}\nTITLE: ${
                    source.title
                  }\nCONTENT: ${source.content.substring(0, 2000)}...`
              )
              .join("\n\n");

            sourcesContext = `\n\nSCRAPED EDUCATIONAL CONTENT FROM CREDIBLE SOURCES:\n${scrapedContent}\n\nIMPORTANT: Use the above verified educational content to enhance your summary with accurate, fact-checked information.`;

            logSuccess("✅ Successfully scraped content from sources", {
              successfulScrapes: successfulScrapes.length,
              totalWords: successfulScrapes.reduce((sum: number, s: any) => sum + s.word_count, 0),
            });
          } else {
            logWarn("⚠️ No content could be scraped, using URLs only");
            successfulSourceUrls.push(...credibleSources);

            sourcesContext = `\n\nAUTHORITATIVE SOURCE URLS:\n${credibleSources
              .map((source, index) => `${index + 1}. ${source}`)
              .join(
                "\n"
              )}\n\nNote: Source content scraping was attempted but failed. These are verified authoritative educational URLs. Generate accurate content based on established educational facts about this subject from peer-reviewed sources, and cite these URLs as authoritative references.`;
          }
        } catch (scrapingError) {
          logError("Source scraping failed for summary", scrapingError);
          successfulSourceUrls.push(...credibleSources);
          sourcesContext = `\n\nAUTHORITATIVE SOURCE URLS:\n${credibleSources
            .map((source, index) => `${index + 1}. ${source}`)
            .join(
              "\n"
            )}\n\nNote: Source content scraping failed due to technical issues. These are verified authoritative educational URLs. Generate accurate content based on established educational facts from peer-reviewed sources, and cite these URLs as authoritative references.`;
        }
      } else {
        sourcesContext = `\n\nNote: No external source URLs were found. Generate accurate educational content based on your training data and cite authoritative sources from ${pastDecadeYear}-${currentYear}.`;
      }

      // Step 4: Prepare enhanced context for Gemini

      const prompt = `
You are an educational content expert with access to verified academic sources. Create a comprehensive study material summary based on established educational facts and authoritative sources.

Educational content for analysis:
${content}

${fileName ? `Source material: ${fileName}` : ""}

${sourcesContext}

CONTENT CREATION REQUIREMENTS:
1. DIRECT EDUCATIONAL WRITING: Write directly about concepts, NOT about "the text" or "the content"
2. SENIOR HIGH SCHOOL LEVEL: Target Grade 11-12 Filipino students with clear, academic language
3. FACTUAL FOUNDATION: Base all content on verified educational facts from authoritative sources
4. CREDIBLE SOURCES: Use only peer-reviewed academic sources, educational institutions, and official educational materials
5. OBJECTIVE PRESENTATION: Present information as established educational facts using present tense and active voice
6. VERIFICATION STANDARD: Every statement must be verifiable through credible educational sources
7. CURRENT INFORMATION: Use sources published between ${pastDecadeYear}-${currentYear} for relevance
8. NO META-COMMENTARY: Never write "The text explains..." or "The content discusses..." - write directly about the concepts

WRITING EXAMPLES:
❌ BAD: "The text explains that photosynthesis is a biological process..."
✅ GOOD: "Photosynthesis is a biological process where plants convert light energy into chemical energy..."

❌ BAD: "The content discusses how the rock cycle works..."  
✅ GOOD: "The rock cycle describes the continuous transformation of rocks through geological processes..."

MATHEMATICAL FORMULAS & SPECIAL NOTATION:
- For mathematical formulas, use LaTeX notation wrapped in $ for inline math or $$ for display math
- Examples: 
  * Inline: $E = mc^2$ or $x^2 + y^2 = r^2$
  * Display: $$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$
  * Complex: $$\\int_{0}^{\\infty} e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}$$
- Use LaTeX for ALL mathematical expressions, equations, formulas, symbols
- Common symbols: $\\pi$, $\\theta$, $\\alpha$, $\\beta$, $\\sum$, $\\prod$, $\\int$, $\\frac{a}{b}$, $\\sqrt{x}$, $x^2$, $x_i$

Generate educational content with:
1. Comprehensive educational summary (150-300 words) explaining concepts directly for students
2. 5-8 key educational points that present facts and concepts students should understand
3. Clear topic title for the educational content
4. Academic subject classification
5. Only verified, specific educational sources (minimum 3, maximum 5) - NO generic homepages

SOURCE REQUIREMENTS:
- Academic institutions (.edu domains) - ${pastDecadeYear}-${currentYear} publications only
- Peer-reviewed journals and publications - ${pastDecadeYear}-${currentYear} publications only
- Official government educational resources - ${pastDecadeYear}-${currentYear} publications only
- Established encyclopedias (Britannica, etc.) - ${pastDecadeYear}-${currentYear} editions only
- Recognized textbook publishers - ${pastDecadeYear}-${currentYear} editions only
- Professional educational organizations - ${pastDecadeYear}-${currentYear} publications only
- NO Wikipedia, blogs, or unverified websites
- NO sources older than ${pastDecadeYear}
- Provide full citations in APA format or working URLs
- Do NOT use asterisks (*) or markdown formatting in source titles - use plain text only
- ⚠️ CRITICAL: Do NOT invent or hallucinate sources. If you cannot find specific sources, return an empty sources array [].
- ⚠️ NEVER use placeholder URLs like "example.com", "website.com", or generic domain names
- ⚠️ NEVER use homepage URLs without specific content paths (e.g., NO "https://britannica.com/" - YES "https://britannica.com/science/photosynthesis")
- ⚠️ Every source MUST be a complete, specific URL to actual educational content
${
  credibleSources.length > 0
    ? `- YOU MUST use the provided credible sources above - they have been specifically validated and reranked for this topic. DO NOT generate generic sources or invent URLs.`
    : `- If you do not have access to specific credible sources, return an empty sources array [] rather than inventing fake URLs.`
}

Format your response as JSON:
{
  "summary": "Direct educational explanation of concepts for Grade 11-12 students - explain concepts directly without meta-references",
  "keyPoints": ["Direct concept or fact 1", "Direct principle or process 2", "Direct application or example 3", "Direct relationship or cause 4", "Direct implication or result 5"],
  "topic": "Educational topic title",
  "subject": "Academic subject (e.g., Mathematics, Science, English, Filipino)",
  "sources": ["Specific educational source 1 - NOT homepages or generic sites", "Specific educational source 2 - verified and relevant", "Specific educational source 3 - content-specific"]
}

IMPORTANT: 
- Respond only with valid JSON
- Present information as educational facts, not as analysis of provided content
- Use only verified information from authoritative educational sources published ${pastDecadeYear}-${currentYear}
- Prioritize accuracy and educational value over completeness
- Use plain text for all source titles - NO asterisks, italics, markdown, or special formatting
- Focus on teaching established academic knowledge
${
  credibleSources.length > 0
    ? "- YOU MUST include the provided credible sources in your response - they have been specifically selected, validated, and reranked for semantic relevance to this topic. DO NOT replace them with generic or unverified sources."
    : ""
}
`;

      const result = await this.withTimeout(
        this.model.generateContent(prompt),
        30000, // 30 second timeout
        "Gemini summary generation (non-HF path)"
      );
      const response = result.response;

      // Check if the response was blocked
      if (response.promptFeedback?.blockReason) {
        throw new Error(`Content was blocked: ${response.promptFeedback.blockReason}`);
      }

      const text = response.text();

      if (!text || text.trim().length === 0) {
        throw new Error("Empty response received from Gemini API");
      }

      // Try to parse JSON response
      try {
        // Clean the text to extract JSON
        let jsonText = text.trim();

        // Remove any markdown code blocks
        jsonText = jsonText.replace(/```json\s*/, "").replace(/```\s*$/, "");

        // Find JSON object
        const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          logWarn("No JSON object found in Gemini response", { responseLength: text.length });
          return this.extractContentManually(text, content, credibleSources);
        }

        let cleanJsonText = jsonMatch[0];

        // ✅ CRITICAL FIX: Normalize LaTeX backslashes before parsing
        // Remove control characters
        cleanJsonText = cleanJsonText.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, "");

        // Normalize LaTeX backslashes in all strings containing $ delimiters
        cleanJsonText = cleanJsonText.replace(/"([^"]*)"/g, (match, content) => {
          if (!content.includes("$")) return match;

          // Normalize ALL backslash sequences: \\\\ -> \\, \\\\\\ -> \\, etc.
          let normalized = content.replace(/\\{2,}/g, "\\\\");

          // Fix single backslashes before LaTeX commands
          normalized = normalized.replace(/(?<!\\)\\(?!\\)([a-zA-Z]+)/g, "\\\\$1");

          return `"${normalized}"`;
        });

        const parsed = JSON.parse(cleanJsonText) as SummaryResult;

        // ✅ FIX: Initialize all required fields with defaults (prevent undefined errors)
        if (!parsed.summary) parsed.summary = "Summary generation incomplete";
        if (!parsed.topic) parsed.topic = "Topic not detected";
        if (!parsed.subject) parsed.subject = "General";
        if (!parsed.keyPoints) parsed.keyPoints = [];
        if (!parsed.sources) parsed.sources = [];

        // Clean asterisk formatting from all text fields
        if (parsed.summary) {
          parsed.summary = this.cleanTextFromFormatting(parsed.summary);
        }

        if (parsed.topic) {
          parsed.topic = this.cleanTextFromFormatting(parsed.topic);
        }

        if (parsed.subject) {
          parsed.subject = this.cleanTextFromFormatting(parsed.subject);
        }

        // Clean and validate arrays
        if (Array.isArray(parsed.keyPoints)) {
          parsed.keyPoints = parsed.keyPoints.map((point) => this.cleanTextFromFormatting(point));
        } else {
          // ✅ FIX: Convert non-array keyPoints to array
          parsed.keyPoints =
            typeof parsed.keyPoints === "string"
              ? [parsed.keyPoints]
              : ["Unable to extract key points"];
        }

        if (Array.isArray(parsed.sources)) {
          parsed.sources = parsed.sources.map((source) => this.cleanSourceText(source));
        } else {
          // ✅ FIX: Convert non-array sources to array (allow empty array)
          parsed.sources = typeof parsed.sources === "string" ? [parsed.sources] : [];
          logDebug("Initialized sources array from non-array value in JSON");
        }

        // ✅ FIX: Changed validation to allow empty arrays (check for array type, not truthiness)
        // Validate required fields
        if (
          !parsed.summary ||
          !Array.isArray(parsed.keyPoints) ||
          !parsed.topic ||
          !parsed.subject ||
          !Array.isArray(parsed.sources)
        ) {
          logWarn("Missing required fields in Gemini response", {
            hasSummary: !!parsed.summary,
            hasKeyPoints: !!parsed.keyPoints,
            isKeyPointsArray: Array.isArray(parsed.keyPoints),
            hasTopic: !!parsed.topic,
            hasSubject: !!parsed.subject,
            hasSources: !!parsed.sources,
            isSourcesArray: Array.isArray(parsed.sources),
          });
          return this.extractContentManually(text, content, credibleSources);
        }

        // Ensure sources is an array and enhance with found sources
        if (!Array.isArray(parsed.sources)) {
          logWarn("sources is not an array in Gemini response", {
            sourcesType: typeof parsed.sources,
            sourcesValue: parsed.sources,
          });
          parsed.sources =
            typeof parsed.sources === "string"
              ? [parsed.sources]
              : ["AI-generated content - verify with authoritative sources"];
        }

        // ✅ CRITICAL FIX: Validate and filter out fake/hallucinated sources from Gemini
        // Gemini sometimes invents sources when none are provided. Filter these out.
        const validatedSources = parsed.sources.filter((source) => {
          // Remove generic/fake source indicators
          const isFake =
            source.includes("example.com") ||
            source.includes("AI-generated") ||
            source.includes("verify with") ||
            source.includes("authoritative sources") ||
            source.includes("[Insert") ||
            source.includes("[Add") ||
            source.includes("placeholder") ||
            // Reject homepage-only URLs (must have specific content path)
            /^https?:\/\/[^\/]+\/?$/.test(source.trim()) ||
            // Reject URLs that are just domains
            !source.includes("/") ||
            source.length < 20; // Too short to be a real reference

          if (isFake) {
            logWarn("Filtered out fake/generic source from Gemini", { source });
            return false;
          }

          // ✅ NEW: Check for obviously off-topic sources (e.g., math sites for Filipino content)
          const isOffTopic = this.isObviouslyOffTopic(
            source,
            parsed.subject || hfAnalysis?.subject || "",
            keyTopics || []
          );

          if (isOffTopic) {
            return false;
          }

          return true;
        });

        // If we filtered out all sources OR Gemini generated <3 real sources,
        // replace with successfully scraped/validated sources
        if (successfulSourceUrls.length > 0 && validatedSources.length < 3) {
          parsed.sources = successfulSourceUrls.slice(0, 5); // Use top 5 successful sources
          logSuccess("Replaced Gemini sources with successfully scraped sources", {
            geminiSourcesFiltered: parsed.sources.length - validatedSources.length,
            successfulSourcesUsed: parsed.sources.length,
          });
        } else if (validatedSources.length > 0) {
          // Keep validated sources, optionally supplement with successful sources
          parsed.sources = validatedSources;
          if (successfulSourceUrls.length > 0) {
            // Add additional successful sources if we have room
            successfulSourceUrls.slice(0, 5 - parsed.sources.length).forEach((source) => {
              // Check for duplicates using normalized URLs
              const isDuplicate = parsed.sources.some((s) => {
                try {
                  return this.normalizeUrl(s) === this.normalizeUrl(source);
                } catch {
                  return s === source || s.includes(source);
                }
              });
              if (!isDuplicate) {
                parsed.sources.push(source);
              }
            });
          }
          logSuccess("Used validated Gemini sources + successful source supplements", {
            validatedCount: validatedSources.length,
            totalSources: parsed.sources.length,
          });
        } else {
          // Gemini generated all fake sources, use fallback with successful sources
          parsed.sources =
            successfulSourceUrls.length > 0
              ? successfulSourceUrls.slice(0, 5)
              : ["AI-generated content - verify with authoritative sources"];
          logWarn("All Gemini sources were fake, using successful scraped sources or fallback", {
            successfulSourcesAvailable: successfulSourceUrls.length,
            usedFallback: successfulSourceUrls.length === 0,
          });
        }

        logSuccess("Enhanced summary generation completed successfully", {
          hasSearchSources: credibleSources.length > 0,
          totalSources: parsed.sources.length,
          keyPointsCount: parsed.keyPoints.length,
        });

        return parsed;
      } catch (parseError) {
        logWarn("Failed to parse JSON response from Gemini", parseError);
        logDebug("Raw Gemini response", {
          responseLength: text.length,
          firstChars: text.substring(0, 100),
        });
        return this.extractContentManually(text, content, credibleSources);
      }
    } catch (error: any) {
      logError("Error generating enhanced Gemini summary", error);

      // Handle specific API errors
      if (error.message?.includes("API_KEY_INVALID")) {
        throw new Error("Invalid API key. Please check your Gemini API key configuration.");
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        throw new Error("API quota exceeded. Please try again later or check your API limits.");
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        throw new Error("Rate limit exceeded. Please wait a moment and try again.");
      } else if (error.message?.includes("blocked")) {
        throw new Error(
          "Content was blocked by safety filters. Please try with different content."
        );
      } else {
        throw new Error(`Failed to generate summary: ${error.message || "Unknown error"}`);
      }
    }
  }

  private extractContentManually(
    text: string,
    originalContent: string,
    credibleSources: string[] = []
  ): SummaryResult {
    // Fallback method to extract content if JSON parsing fails
    const lines = text.split("\n").filter((line: string) => line.trim());

    let summary = "";
    const keyPoints: string[] = [];
    let topic = "Study Material Summary";
    const subject = "General";
    let sources = ["AI-generated content - verify with authoritative sources"];

    // Include credible sources if found
    if (credibleSources.length > 0) {
      sources = [...credibleSources.slice(0, 3), ...sources];
      logInfo("Enhanced fallback extraction with credible sources", {
        sourceCount: credibleSources.length,
      });
    }

    // Try to extract summary (usually the longest paragraph)
    const paragraphs = text.split("\n\n").filter((p: string) => p.trim() && p.length > 50);
    if (paragraphs.length > 0) {
      summary = this.cleanTextFromFormatting(paragraphs[0].trim());
    }

    // Extract bullet points or numbered lists
    lines.forEach((line: string) => {
      const trimmed = line.trim();
      if (trimmed.match(/^[-•*]\s+/) || trimmed.match(/^\d+\.\s+/)) {
        const point = trimmed.replace(/^[-•*]\s+/, "").replace(/^\d+\.\s+/, "");
        if (point.length > 10) {
          keyPoints.push(this.cleanTextFromFormatting(point));
        }
      }
    });

    // Try to infer topic from first few words of content
    const words = originalContent.trim().split(/\s+/).slice(0, 5);
    if (words.length > 0) {
      topic = words
        .join(" ")
        .replace(/[^\w\s]/g, "")
        .trim();
    }

    // Ensure we have at least some key points
    if (keyPoints.length === 0) {
      const sentences = summary.split(/[.!?]+/).filter((s: string) => s.trim() && s.length > 20);
      keyPoints.push(
        ...sentences.slice(0, 5).map((s: string) => this.cleanTextFromFormatting(s.trim()))
      );
    }

    // Clean sources from formatting
    const cleanedSources = sources.map((source) => this.cleanSourceText(source));

    return {
      summary:
        summary || "Summary generation completed. Please review the content for key insights.",
      keyPoints:
        keyPoints.length > 0 ? keyPoints : ["Key insights extracted from provided content"],
      topic: topic || "Study Material",
      subject: subject,
      sources: cleanedSources,
    };
  }

  async testConnection(): Promise<boolean> {
    try {
      if (!API_KEY || API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
        logError("Gemini API key not configured for testing");
        return false;
      }

      const result = await this.withTimeout(
        this.model.generateContent(
          'Hello! Please respond with just the word "OK" to confirm the connection is working.'
        ),
        10000, // 10 second timeout for test connection
        "Gemini connection test"
      );
      const response = result.response;

      // Check if the response was blocked
      if (response.promptFeedback?.blockReason) {
        logError("Gemini API test connection blocked", {
          blockReason: response.promptFeedback.blockReason,
        });
        return false;
      }

      const text = response.text();
      const isWorking = text.toLowerCase().includes("ok") || text.toLowerCase().includes("yes");

      logInfo("Gemini API test completed", {
        success: isWorking,
        responseText: text.substring(0, 50),
      });

      return isWorking;
    } catch (error: any) {
      logError("Gemini API test failed", error);

      // Log specific error types for debugging
      if (error.message?.includes("API_KEY_INVALID")) {
        logError("Invalid API key detected during test");
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        logError("API quota exceeded during test");
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        logError("Rate limit exceeded during test");
      }

      return false;
    }
  }

  async testGoogleSearch(): Promise<boolean> {
    try {
      if (!this.googleSearchService) {
        logError("Google Search tool not initialized", {
          hasApiKey: !!GOOGLE_SEARCH_API_KEY,
          hasEngineId: !!GOOGLE_SEARCH_ENGINE_ID,
          searchToolStatus: "null",
        });
        return false;
      }

      if (!GOOGLE_SEARCH_API_KEY || !GOOGLE_SEARCH_ENGINE_ID) {
        logError("Google Search API credentials not configured", {
          hasApiKey: !!GOOGLE_SEARCH_API_KEY,
          hasEngineId: !!GOOGLE_SEARCH_ENGINE_ID,
          apiKeyLength: GOOGLE_SEARCH_API_KEY?.length || 0,
          engineIdLength: GOOGLE_SEARCH_ENGINE_ID?.length || 0,
        });
        return false;
      }

      logInfo("Testing Google Search API connection", {
        apiKeyConfigured: !!GOOGLE_SEARCH_API_KEY,
        engineIdConfigured: !!GOOGLE_SEARCH_ENGINE_ID,
        searchToolInitialized: !!this.googleSearchService,
      });

      // Test with a simple educational query relevant to Philippine curriculum
      const testQuery = "mathematics education Philippines site:edu OR site:gov";
      logDebug("Testing search with educational query", {
        query: testQuery,
        purpose: "Testing Google Custom Search integration",
      });

      const searchStartTime = Date.now();
      // Use the search service to perform the test query
      const searchResults = await this.googleSearchService.searchCredibleSources([testQuery]);
      const searchTime = Date.now() - searchStartTime;

      if (searchResults && searchResults.length > 0) {
        // searchResults is already an array of URLs from googleSearchService
        const credibleUrls = searchResults.filter(
          (url: string) =>
            url.includes(".edu") ||
            url.includes(".gov") ||
            url.includes("scholar.google") ||
            url.includes("researchgate")
        );

        logSuccess("Google Search API test successful", {
          searchTime: `${searchTime}ms`,
          totalResults: searchResults.length,
          credibleUrls: credibleUrls.length,
          sampleUrls: credibleUrls.slice(0, 3),
          googleSearchIntegration: "working",
        });

        // Log detailed results for debugging
        logDebug("Search results analysis", {
          allUrls: searchResults.slice(0, 5),
          credibleSources: credibleUrls,
        });

        return credibleUrls.length > 0;
      } else {
        logError("Google Search API returned empty results", {
          searchTime: `${searchTime}ms`,
          resultType: typeof searchResults,
          resultLength: searchResults?.length || 0,
        });
        return false;
      }
    } catch (error: any) {
      logError("Google Search API test failed", error);

      // Enhanced error analysis for debugging
      const errorDetails = {
        errorType: error.constructor.name,
        errorMessage: error.message,
        hasApiKey: !!GOOGLE_SEARCH_API_KEY,
        hasEngineId: !!GOOGLE_SEARCH_ENGINE_ID,
        searchToolStatus: !!this.googleSearchService,
      };

      if (error.message?.includes("API_KEY_INVALID")) {
        logError("❌ Invalid Google Search API key - please check credentials", errorDetails);
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        logError("❌ Google Search API quota exceeded - check billing/limits", errorDetails);
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        logError("❌ Google Search API rate limit exceeded - try again later", errorDetails);
      } else if (error.message?.includes("PERMISSION_DENIED")) {
        logError("❌ Google Search API permission denied - check API configuration", errorDetails);
      } else {
        logError("❌ Unknown Google Search API error", errorDetails);
      }

      return false;
    }
  }

  /**
   * Progressive loading optimization: Fast basic results → Enhanced background processing
   * Provides immediate user feedback while building comprehensive results
   */
  async generateProgressiveSummary(
    content: string,
    fileName?: string,
    onProgressUpdate?: (progress: ProgressiveResult) => void
  ): Promise<EnhancedSummaryResult> {
    const overallStartTime = Date.now();

    logInfo("Starting progressive AI processing", {
      contentLength: content.length,
      fileName: fileName || "unnamed",
      strategy: "Basic → Enhanced",
    });

    try {
      // Phase 1: Fast Basic Processing (parallel basic operations)
      logDebug("Phase 1: Starting basic parallel processing...");
      const basicStartTime = Date.now();

      // Start Backend HF analysis in background (optimized content)
      const optimizedHFContent = content.substring(0, 500); // First 500 chars for speed
      const backendAIService = await getBackendAIService();
      const hfPromise = backendAIService.analyzeContent(optimizedHFContent);

      // Get basic Gemini summary immediately
      const basicResult = await this.generateSummary(content, fileName);
      const basicTime = Date.now() - basicStartTime;

      // Notify progress with basic results
      const basicProgress: ProgressiveResult = {
        phase: "basic",
        result: basicResult,
        processingTime: basicTime,
        confidence: "medium",
      };

      logSuccess("Phase 1 completed - basic results ready", {
        processingTime: `${basicTime}ms`,
        keyPointsCount: basicResult.keyPoints.length,
        sourcesCount: basicResult.sources.length,
      });

      if (onProgressUpdate) {
        onProgressUpdate(basicProgress);
      }

      // Phase 2: Enhanced Processing (background improvement)
      logDebug("Phase 2: Starting enhanced background processing...");
      const enhancedStartTime = Date.now();

      try {
        // Wait for HF analysis to complete
        const hfAnalysis = await hfPromise;
        const hfTime = Date.now() - overallStartTime;

        // Extract enhanced topics using HF insights
        const enhancedTopics = this.extractKeyTopicsWithHF(content, hfAnalysis);

        // Run enhanced Gemini and search in parallel
        const [enhancedGeminiResult, credibleSources] = await Promise.all([
          this.generateEnhancedGeminiSummary(content, fileName, hfAnalysis),
          this.searchCredibleSourcesEnhanced(enhancedTopics, hfAnalysis.subject),
        ]);

        const enhancedTime = Date.now() - enhancedStartTime;
        const totalTime = Date.now() - overallStartTime;

        // Combine enhanced results
        const finalResult = this.combineEnhancedResults(
          enhancedGeminiResult,
          credibleSources,
          hfAnalysis
        );

        // Calculate improvements over basic result
        const improvements = this.calculateImprovements(basicResult, finalResult, hfAnalysis);

        const enhancedFinalResult: EnhancedSummaryResult = {
          ...finalResult,
          hfAnalysis,
          processingMetrics: {
            totalTime,
            hfTime,
            geminiTime: enhancedTime,
            searchTime: enhancedTime, // Parallel with Gemini
          },
          enhancementStatus: hfAnalysis.isReady ? "hf_enhanced" : "hf_fallback",
        };

        // Notify progress with enhanced results
        const enhancedProgress: ProgressiveResult = {
          phase: "enhanced",
          result: enhancedFinalResult,
          processingTime: totalTime,
          confidence: "high",
          improvements,
        };

        logSuccess("Phase 2 completed - enhanced results ready", {
          totalTime: `${totalTime}ms`,
          improvements: improvements,
          enhancementStatus: enhancedFinalResult.enhancementStatus,
        });

        if (onProgressUpdate) {
          onProgressUpdate(enhancedProgress);
        }

        return enhancedFinalResult;
      } catch (enhancementError) {
        logWarn("Enhancement phase failed, returning basic results", enhancementError);

        // Return basic result wrapped as enhanced format
        const fallbackTime = Date.now() - overallStartTime;
        return {
          ...basicResult,
          processingMetrics: {
            totalTime: fallbackTime,
            hfTime: 0,
            geminiTime: basicTime,
            searchTime: 0,
          },
          enhancementStatus: "standard",
        };
      }
    } catch (error) {
      logError("Progressive processing failed completely", error);
      throw error;
    }
  }

  /**
   * Calculate improvements between basic and enhanced results
   */
  private calculateImprovements(
    basicResult: SummaryResult,
    enhancedResult: SummaryResult,
    hfAnalysis?: HFAnalysisResult
  ): {
    sourcesAdded: number;
    accuracyBoost: string;
    hfInsights: boolean;
  } {
    const sourcesAdded = Math.max(0, enhancedResult.sources.length - basicResult.sources.length);

    let accuracyBoost = "Standard";
    if (hfAnalysis?.isReady) {
      if (hfAnalysis.languageConfidence > 80 && hfAnalysis.subjectConfidence > 70) {
        accuracyBoost = "High accuracy boost";
      } else if (hfAnalysis.languageConfidence > 60 || hfAnalysis.subjectConfidence > 50) {
        accuracyBoost = "Moderate accuracy boost";
      } else {
        accuracyBoost = "Basic accuracy boost";
      }
    }

    return {
      sourcesAdded,
      accuracyBoost,
      hfInsights: !!hfAnalysis?.isReady,
    };
  }

  /**
   * Test the enhanced HuggingFace + Gemini + LangChain pipeline with Philippine curriculum
   */
  async testEnhancedPipeline(): Promise<boolean> {
    try {
      logInfo("🧪 Testing enhanced AI pipeline with Philippine curriculum subjects", {
        pipeline: "HuggingFace + Gemini + LangChain + Google Search",
        curriculum: "Philippine Senior High School",
      });

      // Test content that should trigger Philippine curriculum subjects
      const testContent = `
        Statistics and Probability are fundamental concepts in mathematics education. 
        Students learn to analyze data, calculate probabilities, and understand 
        statistical distributions. This subject helps develop analytical thinking 
        and problem-solving skills essential for STEM fields.
      `;

      logDebug("Running enhanced pipeline test", {
        contentLength: testContent.length,
        expectedSubject: "Statistics and Probability",
        expectedLanguage: "english",
      });

      const testStartTime = Date.now();
      const result = await this.generateEnhancedSummaryWithHF(testContent, "test-statistics.txt");
      const testTime = Date.now() - testStartTime;

      // Validate the results
      const isValid = Boolean(
        result.summary &&
          result.summary.length > 50 &&
          result.keyPoints &&
          result.keyPoints.length > 0 &&
          result.sources &&
          result.sources.length > 0 &&
          result.hfAnalysis &&
          result.processingMetrics
      );

      const expectedPhilippineSubjects = [
        "Statistics and Probability",
        "General Mathematics",
        "Oral Communication",
        "Reading and Writing",
        "21st Century Literature from the Philippines and the World",
        "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
        "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
        "Earth and Life Science",
        "Physical Science",
        "Earth Science",
      ];

      const foundPhilippineSubject = expectedPhilippineSubjects.some(
        (subject) =>
          result.subject?.includes(subject) || result.hfAnalysis?.subject?.includes(subject)
      );

      logSuccess("Enhanced pipeline test completed", {
        testTime: `${testTime}ms`,
        isValid,
        foundPhilippineSubject,
        detectedSubject: result.hfAnalysis?.subject || result.subject,
        detectedLanguage: result.hfAnalysis?.language,
        sourcesFound: result.sources.length,
        hfProcessingTime: `${result.hfAnalysis?.processingTime || 0}ms`,
        totalMetrics: result.processingMetrics,
        enhancementStatus: result.enhancementStatus,
      });

      // Test Google Search functionality specifically
      const searchWorking: boolean = await this.testGoogleSearch();

      logInfo("Complete pipeline test results", {
        enhancedPipelineWorking: isValid,
        philippineCurriculumSupport: foundPhilippineSubject,
        googleSearchWorking: searchWorking,
        googleSearchIntegration: !!this.googleSearchService,
        overallSuccess: isValid && searchWorking,
      });

      return isValid && searchWorking;
    } catch (error) {
      logError("Enhanced pipeline test failed", error);
      return false;
    }
  }

  /**
   * Generate flashcards using Gemini 2.5-flash with enhanced fact-checking
   * Uses the same LangChain + Google Search pipeline as summary generation
   * SAFESEARCH: Content safety checks prevent explicit/inappropriate content processing
   * @param preferredLanguage - Optional language preference override ('english' or 'filipino')
   */
  async generateFlashcards(
    content: string,
    fileName?: string,
    count: number = 10, // ✅ ALWAYS generate 10 flashcards from user's uploaded content (not less)
    sessionSubject?: string, // ✅ NEW: Subject from completed session (bypasses HF subject detection)
    preferredLanguage?: "english" | "filipino" // ✅ MOVED: Language preference now optional last param
  ): Promise<FlashcardResult> {
    // 🔍 TRACE: Log entry to identify duplicate calls
    const callId = `FLASHCARDS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.warn(`\n🚨🚨🚨 [${callId}] generateFlashcards() INVOKED 🚨🚨🚨`);
    console.warn(`   Content length: ${content.length}`);
    console.warn(`   File name: ${fileName || "none"}`);
    console.trace(`[${callId}] Call stack trace:`);

    // ⚡ OPTIMIZATION 1: Truncate extremely large content to prevent memory issues
    const MAX_CONTENT_LENGTH = 50000; // 50k characters max (~12k tokens)
    let processedContent = content;
    if (content.length > MAX_CONTENT_LENGTH) {
      logWarn(
        `Content too large for flashcards (${content.length} chars), truncating to ${MAX_CONTENT_LENGTH}`,
        {
          originalLength: content.length,
          truncatedLength: MAX_CONTENT_LENGTH,
        }
      );
      processedContent = content.substring(0, MAX_CONTENT_LENGTH);
      console.warn(
        `⚠️ Flashcard content truncated: ${content.length} → ${MAX_CONTENT_LENGTH} chars`
      );
    }

    // STEP 0: CONTENT SAFETY CHECK - Block explicit/inappropriate content
    logDebug("Performing content safety check before flashcard generation");
    const safetyCheck = checkContentSafety(processedContent);

    if (safetyCheck.blocked) {
      logError("Content blocked due to safety concerns", {
        severity: safetyCheck.severity,
        reasonCount: safetyCheck.reasons.length,
        fileName: fileName || "unnamed",
      });
      throw new Error(
        safetyCheck.message || "Content contains inappropriate material and cannot be processed."
      );
    }

    if (safetyCheck.severity === "medium") {
      logWarn("Content contains potentially inappropriate language", {
        severity: safetyCheck.severity,
        reasons: safetyCheck.reasons,
      });
    }

    // Check if API key is configured
    if (!API_KEY || API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
      throw new Error(
        "Gemini API key not configured. Please add your API key to the environment variables."
      );
    }

    try {
      logInfo("Starting flashcard generation with Gemini 2.5-flash", {
        contentLength: processedContent.length,
        fileName: fileName || "unnamed",
        model: "gemini-2.5-flash",
        safetyStatus: "passed",
      });

      // Step 1: Analyze content with Backend HF to detect language and subject
      logDebug("Analyzing content for language and subject detection", {
        skipSubjectDetection: !!sessionSubject,
        sessionSubject: sessionSubject || "none",
      });
      // ✅ Skip subject detection if sessionSubject is provided
      // ⚡ OPTIMIZATION 2: Only analyze first 2000 chars (reduced from full content)
      const backendAIService = await getBackendAIService();

      // 🔒 CRITICAL FIX #3: Runtime validation of backendAIService
      if (!backendAIService || typeof backendAIService.analyzeContent !== "function") {
        logError("BackendAIService not properly initialized for flashcards", {
          hasService: !!backendAIService,
          hasMethod: backendAIService ? typeof backendAIService.analyzeContent : "N/A",
        });
        throw new Error("Backend AI service initialization failed - cannot generate flashcards");
      }

      const hfAnalysis: HFAnalysisResult = await this.withTimeout(
        backendAIService
          .analyzeContent(processedContent.substring(0, 2000), !!sessionSubject)
          .catch((err: Error) => {
            // 🔒 CRITICAL FIX #2: Error handling in Promise chain
            logError("Backend HF analysis failed for flashcards, using fallback", err);
            return {
              language: preferredLanguage || "English",
              languageConfidence: 50,
              subject: sessionSubject || "General Knowledge",
              subjectConfidence: 50,
              isReady: false,
              processingTime: 0,
            } as HFAnalysisResult;
          }),
        10000, // 10 second timeout
        "Backend HuggingFace flashcard analysis"
      );

      // 🔒 CRITICAL FIX #1: Null checks on hfAnalysis properties
      // ✅ Use session subject if provided, otherwise validate and fallback
      if (sessionSubject) {
        hfAnalysis.subject = sessionSubject;
        hfAnalysis.subjectConfidence = 100; // Session subject is authoritative
        logInfo("Using session subject instead of AI detection for flashcards", {
          sessionSubject,
        });
      } else {
        // Ensure subject is never null/undefined
        if (!hfAnalysis.subject || hfAnalysis.subject.trim() === "") {
          hfAnalysis.subject = "General Knowledge";
          hfAnalysis.subjectConfidence = 50;
          logWarn("HF analysis returned empty subject for flashcards, using fallback", {
            fallback: "General Knowledge",
          });
        }
      }

      // Ensure language is never null/undefined
      if (!hfAnalysis.language || hfAnalysis.language.trim() === "") {
        hfAnalysis.language = preferredLanguage || "English";
        hfAnalysis.languageConfidence = 50;
        logWarn("HF analysis returned empty language for flashcards, using fallback", {
          fallback: hfAnalysis.language,
        });
      }

      logDebug("HuggingFace analysis completed", {
        language: hfAnalysis.language,
        subject: hfAnalysis.subject,
        isReady: hfAnalysis.isReady,
        fromSession: !!sessionSubject,
      });

      // Step 2: Extract key topics using HF insights (same as summary generation)
      const keyTopics = this.extractKeyTopicsWithHF(processedContent, hfAnalysis);
      logDebug("Extracted topics for flashcard generation", {
        topics: keyTopics,
        detectedLanguage: hfAnalysis.language,
        detectedSubject: hfAnalysis.subject,
      });

      // ✅ FIX #4: Sequential pipeline - Search FIRST, then Gemini WITH sources
      // Step 3: Search for credible sources BEFORE flashcard generation (factual backing)
      logDebug("Searching credible sources FIRST for flashcard factual accuracy...");
      const searchStartTime = Date.now();

      // ⚡ OPTIMIZATION 3: Reduce flashcard search timeout to 45s (from 60s)
      const credibleSourcesResult = await Promise.race([
        this.searchCredibleSourcesForFlashcards(
          keyTopics,
          hfAnalysis.subject || "General Knowledge"
        ).catch((err: Error) => {
          // 🔒 CRITICAL FIX #2: Error handling in Promise.race branch
          logError("Flashcard source search failed, continuing with empty sources", err);
          return [];
        }),
        new Promise<string[]>(
          (resolve) =>
            setTimeout(() => {
              logWarn("Flashcard source search timeout after 45s, proceeding with empty sources");
              resolve([]);
            }, 45000) // ⚡ Reduced to 45s for flashcards (less critical than summaries)
        ),
      ]);

      const credibleSources = Array.isArray(credibleSourcesResult) ? credibleSourcesResult : [];
      const searchTime = Date.now() - searchStartTime;

      // ✅ FIX BUG #2: VALIDATE URLs before using them (filter broken links)
      logDebug("🔍 Validating flashcard URLs to filter broken links...");
      const validationStartTime = Date.now();

      let validatedSources: string[] = [];
      if (credibleSources.length > 0) {
        try {
          const backendAIServiceForValidation = await getBackendAIService();
          validatedSources = await backendAIServiceForValidation.validateUrls(credibleSources, 5);
          const validationTime = Date.now() - validationStartTime;

          logSuccess("✅ Flashcard URL validation completed", {
            input: credibleSources.length,
            valid: validatedSources.length,
            broken: credibleSources.length - validatedSources.length,
            time: `${validationTime}ms`,
          });

          if (validatedSources.length < credibleSources.length) {
            logWarn(
              `⚠️ Filtered ${
                credibleSources.length - validatedSources.length
              } broken flashcard URLs`,
              {
                brokenUrls: credibleSources
                  .filter((url) => !validatedSources.includes(url))
                  .slice(0, 3),
              }
            );
          }
        } catch (error) {
          logError("URL validation failed for flashcards, using unvalidated sources", error);
          validatedSources = credibleSources; // Fallback to original if validation fails
        }
      }

      // ✅ NEW: Filter out off-topic sources for flashcards (same as summaries)
      const filteredSources = validatedSources.filter((source) => {
        const isOffTopic = this.isObviouslyOffTopic(source, hfAnalysis.subject, keyTopics);
        if (isOffTopic) {
          logWarn("Filtered off-topic source from flashcard sources", {
            url: source,
            subject: hfAnalysis.subject,
          });
        }
        return !isOffTopic;
      });

      // ✅ NEW: Deduplicate sources for flashcards
      const deduplicatedSources = filteredSources.filter((source, index, self) => {
        return (
          index ===
          self.findIndex((s) => {
            try {
              return this.normalizeUrl(s) === this.normalizeUrl(source);
            } catch {
              return s === source;
            }
          })
        );
      });

      logDebug("Flashcard source search completed", {
        sourceCount: deduplicatedSources.length,
        originalCount: credibleSources.length,
        filteredCount: filteredSources.length,
        sources: deduplicatedSources.slice(0, 3),
        subject: hfAnalysis.subject,
        strategy: "enhanced multi-tier search with 3-5 source cap + off-topic filtering",
        searchTime: `${searchTime}ms`,
      });

      // 🚨 CRITICAL: Enforce min/max (1-5 sources) with intelligent fallback strategies
      const MIN_QUALITY_THRESHOLD = 2; // ✅ NEW: Require at least 2 sources for quality, or fall back to 0
      let finalFlashcardSources = deduplicatedSources;

      // Step 3A: Enforce MAX 5 sources (hard limit)
      if (finalFlashcardSources.length > 5) {
        console.warn(
          `⚠️ Too many flashcard sources (${finalFlashcardSources.length}), truncating to top 5`
        );
        logWarn("Truncating flashcard sources to maximum of 5", {
          original: finalFlashcardSources.length,
          truncated: 5,
        });
        finalFlashcardSources = finalFlashcardSources.slice(0, 5);
      }

      // Step 3B: Quality threshold check - prefer 0 sources over single low-quality source
      if (
        finalFlashcardSources.length > 0 &&
        finalFlashcardSources.length < MIN_QUALITY_THRESHOLD
      ) {
        console.warn(
          `⚠️ Only ${finalFlashcardSources.length} flashcard source(s) found - below quality threshold of ${MIN_QUALITY_THRESHOLD}`
        );
        logWarn(
          "Flashcard source count below quality threshold - falling back to content-only generation",
          {
            sourcesFound: finalFlashcardSources.length,
            threshold: MIN_QUALITY_THRESHOLD,
            reason: "Single source may be unreliable - better to use Gemini's knowledge",
          }
        );
        finalFlashcardSources = []; // ✅ FIX: Prefer 0 sources (Gemini's knowledge) over 1 questionable source
      }

      // Step 3C: Handle zero sources after quality check
      if (finalFlashcardSources.length === 0) {
        console.warn(
          "⚠️ No valid flashcard sources found after all filters! Attempting fallback strategies..."
        );
        logWarn("No flashcard sources passed all validation filters", {
          originalCount: credibleSources.length,
          afterValidation: validatedSources.length,
          afterOffTopicFilter: filteredSources.length,
          afterDeduplication: deduplicatedSources.length,
        });

        // Fallback Strategy 1: Use validated sources (skip off-topic filter)
        if (validatedSources.length > 0) {
          console.log(
            `📋 Fallback Strategy 1: Using ${Math.min(
              validatedSources.length,
              5
            )} validated sources (skipping off-topic filter)`
          );
          logInfo("Fallback: Using validated sources without off-topic filtering for flashcards", {
            count: Math.min(validatedSources.length, 5),
          });
          finalFlashcardSources = validatedSources.slice(0, 5);
        }
        // Fallback Strategy 2: Use original sources (skip validation)
        else if (credibleSources.length > 0) {
          console.log(
            `📋 Fallback Strategy 2: Using ${Math.min(
              credibleSources.length,
              5
            )} original sources (skipping validation)`
          );
          logWarn(
            "Fallback: Using unvalidated sources for flashcards (all validated sources failed)",
            {
              count: Math.min(credibleSources.length, 5),
            }
          );
          finalFlashcardSources = credibleSources.slice(0, 5);
        }
        // Fallback Strategy 3: Generate without sources (last resort)
        else {
          console.warn(
            "⚠️ No sources available at all - generating flashcards without external sources"
          );
          logWarn("No sources available - generating flashcards from content only");
          finalFlashcardSources = [];
        }
      }

      // Log final source count and validation
      console.log(
        `\n✅ Final sources for flashcard generation: ${finalFlashcardSources.length} URLs`
      );
      if (finalFlashcardSources.length > 0) {
        console.log(
          `   Sources:`,
          finalFlashcardSources
            .map(
              (url, idx) =>
                `\n      ${idx + 1}. ${url.substring(0, 80)}${url.length > 80 ? "..." : ""}`
            )
            .join("")
        );
      }

      // Final validation check
      if (finalFlashcardSources.length > 5) {
        console.error(
          `❌ CRITICAL: finalFlashcardSources exceeds max limit (${finalFlashcardSources.length} > 5)`
        );
        logError("Flashcard source count validation failed - truncating to 5", {
          count: finalFlashcardSources.length,
        });
        finalFlashcardSources = finalFlashcardSources.slice(0, 5);
      }

      logSuccess("Flashcard source pipeline complete", {
        strategy:
          finalFlashcardSources.length === deduplicatedSources.length
            ? "all-filters-passed"
            : "fallback-used",
        finalCount: finalFlashcardSources.length,
        range: "0-5 sources (0 only as last resort)",
      });

      // Step 4: Build language context for prompt with preference override
      const outputLanguage = preferredLanguage || hfAnalysis.language;
      const languageContext = this.buildLanguageContext(hfAnalysis, outputLanguage);
      const subjectContext = this.buildSubjectContext(hfAnalysis);

      // Step 5: Prepare enhanced context for Gemini 2.5-flash
      const currentYear = new Date().getFullYear();
      const pastDecadeYear = currentYear - 10;

      // ✅ NEW: Fetch actual content from credible sources for fact-checking
      let sourcesContext = "";

      if (finalFlashcardSources.length > 0) {
        console.log(`\n${"=".repeat(80)}`);
        console.log(`📚 [FLASHCARD SOURCE FETCHING] Started at ${new Date().toLocaleTimeString()}`);
        console.log(
          `   Fetching content from ${finalFlashcardSources.length} sources for flashcard fact-checking`
        );

        logInfo("🔍 Fetching source content for flashcard fact-checking", {
          sourcesCount: finalFlashcardSources.length,
          purpose: "Ensure flashcard accuracy with verified educational content",
        });

        const sourceContentStart = performance.now();

        // ⚡ OPTIMIZATION 4: Fetch only TOP 2 sources (reduced from 3) with shorter timeout
        const backendAIService2 = await getBackendAIService();
        const sourceContents = await Promise.race([
          backendAIService2
            .scrapeSources(
              finalFlashcardSources.slice(0, 2), // 🚨 ENFORCED: Use min/max validated sources
              2
            )
            .catch((err: Error) => {
              // 🔒 CRITICAL FIX #2: Error handling in Promise.race branch
              logError("Flashcard source scraping failed, continuing without sources", err);
              return [];
            }),
          new Promise<any[]>(
            (resolve) =>
              setTimeout(() => {
                logWarn("Source scraping timeout after 15s");
                resolve([]);
              }, 15000) // ⚡ 15s timeout for source scraping
          ),
        ]);

        const sourceContentDuration = performance.now() - sourceContentStart;

        console.log(
          `✅ [FLASHCARD SOURCE FETCHING] Completed in ${sourceContentDuration.toFixed(0)}ms`
        );
        console.log(
          `   Successfully fetched: ${sourceContents.length}/${Math.min(
            3,
            finalFlashcardSources.length
          )} sources`
        );
        console.log(`${"=".repeat(80)}\n`);

        if (sourceContents.length > 0) {
          logSuccess("✅ Flashcard source content fetched", {
            fetchedCount: sourceContents.length,
            totalWords: sourceContents.reduce(
              (sum: number, s: { word_count: number }) => sum + s.word_count,
              0
            ),
            duration: `${sourceContentDuration.toFixed(0)}ms`,
          });

          // Build enhanced context with ACTUAL source content
          // ⚡ OPTIMIZATION 5: Reduce source content length to 800 chars (from 1200)
          sourcesContext = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📚 SUPPLEMENTARY EXTERNAL SOURCES (FOR FACT-CHECKING ONLY - NOT PRIMARY CONTENT)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${sourceContents
  .map(
    (source: { title: string; url: string; word_count: number; content: string }, idx: number) => `
📖 REFERENCE ${idx + 1}: ${source.title}
🔗 URL: ${source.url}

REFERENCE CONTENT (For verification only):
${source.content.substring(0, 800)} ${source.content.length > 800 ? "..." : ""}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
  )
  .join("\n")}

✅ USE THESE EXTERNAL SOURCES TO:
- Verify facts from the student's uploaded content
- Cross-check terminology and definitions
- Ensure accuracy of concepts presented in the student's material

❌ DO NOT USE THESE EXTERNAL SOURCES TO:
- Generate flashcard content (use the student's uploaded material for that)
- Replace the student's content as the primary source
- Override the student's study notes or uploaded file
`;

          logInfo("📝 Enhanced flashcard prompt with source content", {
            sourcesWithContent: sourceContents.length,
            totalContentLength: sourceContents.reduce(
              (sum: number, s: { content: string }) => sum + s.content.length,
              0
            ),
          });
        } else {
          logWarn("⚠️ No source content fetched for flashcards, using URLs only");

          // ✅ FIX: Explicit warning to Gemini about limited fact-checking capability
          sourcesContext = `

⚠️ LIMITED FACT-CHECKING MODE - EXTERNAL SOURCE CONTENT UNAVAILABLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

VERIFIED SOURCE URLs (Content scraping failed):
${credibleSources.map((source, index) => `${index + 1}. ${source}`).join("\n")}

🔴 LIMITATION: External source content could not be retrieved for cross-referencing.

📋 INSTRUCTIONS:
1. ✅ STILL GENERATE ALL 10 FLASHCARDS FROM THE STUDENT'S UPLOADED CONTENT (Primary Source above)
2. ✅ Use your training knowledge to verify basic facts from the student's material
3. ✅ The student's uploaded content is sufficient - external sources are optional
4. ❌ DO NOT reduce flashcard count due to missing external sources
5. ❌ DO NOT require external source content to generate flashcards

These URLs are verified as credible educational sources, but their content is not available for cross-referencing.`;
        }
      } else {
        logDebug("ℹ️ No credible sources for flashcards, using content analysis only");

        // ✅ FIX: More explicit fallback instructions
        sourcesContext = `

✅ CONTENT-ONLY MODE - NO EXTERNAL SOURCES NEEDED
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

ℹ️ No external sources were found, but this is acceptable.

📋 INSTRUCTIONS:
1. ✅ Generate all 10 flashcards from the student's uploaded content (Primary Source above)
2. ✅ The student's material is sufficient - external sources are NOT required
3. ✅ Use your training knowledge to ensure educational accuracy
4. ✅ Focus on clear definitions, concepts, and facts from the student's material
5. ✅ Extract key information to create all 10 required flashcards

Note: External sources are optional - the student's uploaded content is the primary and sufficient source.`;
      }

      const flashcardPrompt = `
Create exactly ${count} UNIQUE educational flashcards for Grade 11-12 Filipino students studying this topic.

${languageContext}

${subjectContext}

🎯 PRIMARY SOURCE (THE STUDENT'S UPLOADED MATERIAL - THIS IS YOUR MAIN CONTENT):
${processedContent.substring(0, 10000)}${processedContent.length > 10000 ? "..." : ""}

${fileName ? `Source material: ${fileName}` : ""}

${sourcesContext}

📌 CONTENT GENERATION PRIORITY:
1. ✅ GENERATE ALL FLASHCARDS FROM THE STUDENT'S UPLOADED CONTENT ABOVE (Primary Source)
2. ✅ External sources below are ONLY for cross-referencing and fact-checking
3. ✅ If the student's content has sufficient material, external sources are NOT required
4. ✅ Use external sources ONLY to verify facts, NOT as the basis for flashcard content
5. ✅ When student content and external sources differ, TRUST THE STUDENT'S MATERIAL (it's their study notes/uploaded file)

CRITICAL REQUIREMENTS:
⚠️ EACH FLASHCARD MUST BE COMPLETELY UNIQUE - NO DUPLICATE QUESTIONS
⚠️ KEEP IT CONCISE: Questions should be SHORT and DIRECT, Answers should be MAXIMUM 2 SENTENCES
⚠️ FOCUS ON ONE CONCEPT PER CARD - flashcards work best when simple and memorable
⚠️ COMPLETE QUESTIONS ONLY: Every question must be fully formed with complete context
⚠️ NO INCOMPLETE SENTENCES: Do NOT end questions with stray characters like \ " or incomplete phrases

QUALITY STANDARDS - AVOID THESE LOW-QUALITY PATTERNS:
❌ INCOMPLETE: "It is derived from the Latin word \" (lacks context, what "it" refers to?)
❌ VAGUE: "What does it mean?" (what is "it"? no context)
❌ VAGUE: "Define it." (what is "it"? unclear)
❌ INCOMPLETE: "This comes from the Greek term" (which concept? no subject)
❌ TOO BROAD: "Magbigay ng dalawang katangian ng mahusay na mananaliksik." (answers will vary - no definitive answer)
❌ TOO BROAD: "Give 2 examples of..." (subjective - many valid answers, not testable)
❌ TOO BROAD: "Ano ang layunin ng..." (too general - multiple valid interpretations)
❌ TOO BROAD: "What are the characteristics of..." (too open-ended, answers vary)
❌ SUBJECTIVE: "Explain why..." (requires personal interpretation, not factual recall)
✅ GOOD: "What is the Latin origin of the word 'photosynthesis'?" (specific, complete, one answer)
✅ GOOD: "Define photosynthesis." (clear subject, complete question, factual)
✅ GOOD: "What Greek terms form the word 'photosynthesis'?" (specific, contextual, testable)
✅ GOOD: "Ano ang kahulugan ng 'pananaliksik'?" (specific definition, one correct answer)
✅ GOOD: "Alin sa mga sumusunod ang pangunahing layunin ng pananaliksik?" (specific, factual)

MATHEMATICAL FORMULAS & SPECIAL NOTATION:
- For mathematical formulas, use LaTeX notation wrapped in $ for inline math or $$ for display math
- Examples: 
  * Inline: $E = mc^2$ or $x^2 + y^2 = r^2$
  * Display: $$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$
  * Chemical equations: $6CO_2 + 6H_2O + \\text{light} \\rightarrow C_6H_{12}O_6 + 6O_2$
- Use LaTeX for ALL mathematical expressions, equations, formulas, symbols
- Common symbols: $\\pi$, $\\theta$, $\\alpha$, $\\beta$, $\\sum$, $\\prod$, $\\int$, $\\frac{a}{b}$, $\\sqrt{x}$, $x^2$, $x_i$

CONCISENESS RULES:
❌ AVOID WORDY QUESTIONS: "Can you explain the process by which plants convert sunlight into energy?"
✅ GOOD QUESTION: "What is photosynthesis?"

❌ TOO LONG ANSWER: "Photosynthesis is the process by which plants convert light energy into chemical energy stored in glucose. This process occurs in the chloroplasts of plant cells using chlorophyll pigments. The overall equation is: 6CO2 + 6H2O + light energy → C6H12O6 + 6O2. It is essential for life on Earth."
✅ GOOD ANSWER WITH LATEX (MAX 2 SENTENCES): "Photosynthesis is the process by which plants convert light energy into glucose using chlorophyll in chloroplasts. The equation is: $6CO_2 + 6H_2O + \\text{light} \\rightarrow C_6H_{12}O_6 + 6O_2$"

SPECIAL RULES FOR MATHEMATICS (General Mathematics, Statistics and Probability):
❌ AVOID VAGUE QUESTIONS: "Give an example of a rational function." (expects specific memorization)
❌ AVOID VAGUE QUESTIONS: "What key formula should be remembered?" (too many formulas, unclear)
❌ AVOID ASKING FOR EXAMPLES: "Provide an example of..." (learners may think of different valid examples)

✅ GOOD MATH QUESTIONS WITH LATEX:
- "What is the general form of a quadratic equation?" → Answer: "$ax^2 + bx + c = 0$, where $a \\neq 0$."
- "What does the discriminant determine?" → Answer: "The discriminant $b^2 - 4ac$ determines the nature and number of roots of a quadratic equation."
- "What is the formula for the mean of a dataset?" → Answer: "Sum of all values divided by the number of values: $\\bar{x} = \\frac{\\sum x_i}{n}$"
- "What is the difference between permutation and combination?" → Answer: "Permutation ($P$) considers order; combination ($C$) does not. Formulas: $P(n,r) = \\frac{n!}{(n-r)!}$ and $C(n,r) = \\frac{n!}{r!(n-r)!}$"
- "What is the slope-intercept form of a line?" → Answer: "$y = mx + b$, where $m$ is the slope and $b$ is the y-intercept."
- "What is the compound interest formula?" → Answer: "$A = P(1 + \\frac{r}{n})^{nt}$, where $A$ is final amount, $P$ is principal, $r$ is rate, $n$ is compounds per year, $t$ is time."

MATHEMATICS FLASHCARD GUIDELINES:
- Ask about DEFINITIONS, FORMULAS, PROPERTIES, and CONCEPTS
- Provide SPECIFIC formulas or equations when relevant (not just "give an example")
- Focus on UNDERSTANDING relationships and principles
- Include the ACTUAL formula or property in the answer
- Make questions ANSWERABLE without requiring memorization of arbitrary examples
- Ask "What is..." "How do you..." "What does... mean?" instead of "Give an example..."

STRICTLY FORBIDDEN QUESTION PATTERNS (English and Filipino):
🚫 "Magbigay ng..." / "Give..." / "Provide..." (requesting multiple items/examples)
🚫 "Ilista ang..." / "List the..." / "Enumerate..." (requesting lists)
🚫 "Ano ang mga layunin..." / "What are the purposes..." (too broad, multiple valid answers)
🚫 "Ano ang layunin ng..." / "What is the purpose of..." (too vague, subjective)
🚫 "Bakit mahalaga ang..." / "Why is... important..." (opinion-based, not factual)
🚫 "Ipaliwanag kung bakit..." / "Explain why..." (requires interpretation, not recall)
🚫 "Ano ang mga katangian..." / "What are the characteristics..." (open-ended, varies)
🚫 "Give 2 examples of..." / "Magbigay ng 2 halimbawa..." (answers will vary)

✅ ACCEPTABLE QUESTION PATTERNS (Specific, factual, one correct answer):
✅ "Ano ang kahulugan ng..." / "What is the definition of..." (factual definition)
✅ "Ano ang unang hakbang sa..." / "What is the first step in..." (specific fact)
✅ "Totoo ba na..." / "Is it true that..." (true/false, verifiable)
✅ "Ano ang tawag sa..." / "What is the term for..." (terminology)
✅ "Ano ang pagkakaiba ng X at Y?" / "What is the difference between X and Y?" (specific comparison)
✅ "Sino ang..." / "Who..." (specific person, verifiable)
✅ "Kailan..." / "When..." (specific date/time, verifiable)
✅ "Saan..." / "Where..." (specific location, verifiable)

WRITING REQUIREMENTS - DO NOT USE THESE PHRASES:
❌ "This text discusses..."
❌ "The material explains..."
❌ "According to the content..."
❌ "The source states that..."
❌ "Based on the information provided..."

FLASHCARD CREATION STANDARDS:
1. SHORT QUESTIONS: Ask directly about ONE specific concept (5-10 words ideal)
2. CONCISE ANSWERS: Maximum 2 sentences - clear definition or explanation
3. ONE CONCEPT: Each card focuses on a single fact, term, or idea
4. MEMORABLE: Easy to review quickly and remember
5. NO FLUFF: Remove unnecessary words - be direct and precise
6. CURRICULUM-ALIGNED: Follow Philippine Senior High School curriculum standards
7. NO META-COMMENTARY: Present information directly, not as analysis of material
8. REAL KNOWLEDGE: Focus on actual facts, formulas, processes, and concepts
9. UNIQUE QUESTIONS: Every question must be different - no repeats or rephrasing

ANSWER STRUCTURE (PICK ONE - STAY CONCISE):
- Definition + Key detail (2 sentences max)
- Formula + Brief explanation (2 sentences max)
- Process + Purpose (2 sentences max)
- Concept + Example (2 sentences max)

Create exactly ${count} UNIQUE flashcards distributed as follows:
${
  count >= 20
    ? `- Core definitions and terminology (${Math.ceil(
        count * 0.4
      )} cards) - Define key terms concisely
- Essential facts and formulas (${Math.ceil(
        count * 0.3
      )} cards) - State facts or formulas with brief context
- Quick processes (${Math.ceil(count * 0.2)} cards) - Explain steps or how things work briefly
- Applications (${Math.ceil(count * 0.1)} cards) - Show real-world use in 1-2 sentences`
    : `- Focus on core definitions, key facts, and essential concepts
- Distribute evenly across important topics
- Prioritize fundamental understanding over detail`
}

TOPIC GENERATION:
- Create a DESCRIPTIVE, SPECIFIC topic title that clearly identifies the subject matter
- Example: "Photosynthesis Process in Plants" NOT just "Science Notes"
- Example: "Quadratic Equations and Their Applications" NOT just "Math Chapter 3"
- The topic should tell readers exactly what they're learning about

Format your response as JSON:
{
  "flashcards": [
    {
      "front": "Short, direct question (5-10 words)",
      "back": "Concise answer - MAXIMUM 2 sentences with essential information only"
    }
  ],
  "topic": "Descriptive, specific educational topic title that clearly identifies the subject matter",
  "subject": "Academic subject matching Philippine curriculum (e.g., General Mathematics, Earth and Life Science, Statistics and Probability, etc.)",
  "sources": ["List credible educational sources used, or leave as generic if none available"]
}

QUALITY CHECKLIST BEFORE RESPONDING:
✓ All ${count} flashcards have DIFFERENT questions (no duplicates)
✓ All questions are SHORT and DIRECT (5-10 words ideal)
✓ All answers are MAXIMUM 2 SENTENCES - no exceptions
✓ Each card focuses on ONE concept only
✓ Every question is COMPLETE with full context (no vague "it" or "this" without explanation)
✓ NO incomplete sentences or stray special characters (\\ " \` at end of questions)
✓ Every question has a clear subject - avoid vague pronouns without context
✓ Topic title is descriptive and specific to the content
✓ Subject matches Philippine Senior High School curriculum
✓ Plain text only - no markdown, asterisks, or special formatting
✓ Cards are SIMPLE and MEMORABLE - perfect for quick review

IMPORTANT: 
- Respond only with valid JSON
- Create exactly ${count} UNIQUE flashcards with NO DUPLICATES
- Each answer MUST be 1-2 sentences maximum (conciseness is key!)
- Each question should be short and direct (5-10 words)
- Topic must be descriptive and specific to the content
- Flashcards are for memorization - keep them simple and effective
${
  credibleSources.length > 0
    ? "- YOU MUST include the provided credible sources in your response - they have been specifically selected, validated, and semantically reranked for this topic. DO NOT replace them with generic or invented sources."
    : ""
}
`;

      logDebug("Sending flashcard generation request to Gemini 2.5-flash");

      const result = await this.withTimeout(
        this.flashcardModel.generateContent(flashcardPrompt),
        90000, // 90 second timeout for flashcard generation (allows for source scraping + fact-checking)
        "Gemini flashcard generation"
      );
      const response = result.response;

      // Check if the response was blocked
      if (response.promptFeedback?.blockReason) {
        throw new Error(`Content was blocked: ${response.promptFeedback.blockReason}`);
      }

      const text = response.text();

      if (!text || text.trim().length === 0) {
        throw new Error("Empty response received from Gemini 2.5-flash");
      }

      logDebug("Received response from Gemini 2.5-flash", {
        responseLength: text.length,
        firstChars: text.substring(0, 100),
      });

      // Try to parse JSON response
      try {
        // Clean the text to extract JSON
        let jsonText = text.trim();

        // Remove any markdown code blocks
        jsonText = jsonText.replace(/```json\s*/, "").replace(/```\s*$/, "");

        // Remove any explanatory text before or after JSON
        jsonText = jsonText.replace(/^[^{]*/, "").replace(/[^}]*$/, "");

        // Find JSON object with better regex
        const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          logWarn("No JSON object found in flashcard response", {
            responseLength: text.length,
            cleanedText: jsonText.substring(0, 200) + "...",
          });
          throw new Error("Invalid JSON response from Gemini 2.5-flash");
        }

        let cleanJsonText = jsonMatch[0];

        // Additional JSON cleanup steps
        // Fix common JSON formatting issues
        cleanJsonText = cleanJsonText
          // Fix trailing commas in arrays and objects
          .replace(/,(\s*[\]}])/g, "$1")
          // Fix missing quotes around keys
          .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
          // Fix single quotes to double quotes
          .replace(/'/g, '"')
          // Remove any control characters that might break JSON
          .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, "");

        // ✅ CRITICAL FIX: Normalize LaTeX backslashes in all strings (same as post-test fix)
        // Process all strings containing LaTeX notation ($...$) to normalize backslashes
        cleanJsonText = cleanJsonText.replace(/"([^"]*)"/g, (match, content) => {
          // Only process if string contains LaTeX delimiters ($)
          if (!content.includes("$")) return match;

          // Normalize ALL backslash sequences in LaTeX:
          // Replace any sequence of 2+ backslashes with exactly 2 (proper JSON escape)
          // This handles: \\\\ -> \\, \\\\\\ -> \\, etc.
          let normalized = content.replace(/\\{2,}/g, "\\\\");

          // Also fix single backslashes before LaTeX commands (but not already escaped sequences)
          // Match: single backslash followed by letter (LaTeX command) that's not part of \\
          normalized = normalized.replace(/(?<!\\)\\(?!\\)([a-zA-Z]+)/g, "\\\\$1");

          return `"${normalized}"`;
        });

        logDebug("Attempting to parse cleaned JSON", {
          originalLength: jsonMatch[0].length,
          cleanedLength: cleanJsonText.length,
          preview: cleanJsonText.substring(0, 300) + "...",
        });

        let parsed: FlashcardResult;

        try {
          parsed = JSON.parse(cleanJsonText) as FlashcardResult;

          // ✅ FIX: Ensure sources is always a valid array (prevent undefined/null causing validation errors)
          if (!parsed.sources || !Array.isArray(parsed.sources)) {
            parsed.sources = [];
            logDebug("Initialized empty sources array (was missing/invalid in JSON response)");
          }

          // ✅ CRITICAL FIX: Filter out non-string sources BEFORE cleaning (prevents "text.replace is not a function" error)
          // Gemini sometimes returns: sources: ["url1", {object}, null, "url2"] which crashes cleanSourceText()
          if (parsed.sources && Array.isArray(parsed.sources)) {
            parsed.sources = parsed.sources
              .filter((source) => source && typeof source === "string")
              .map((source) => this.cleanSourceText(source));
          }

          if (parsed.topic) {
            parsed.topic = this.cleanTextFromFormatting(parsed.topic);
          }

          if (parsed.subject) {
            parsed.subject = this.cleanTextFromFormatting(parsed.subject);
          }

          // ✅ CRITICAL FIX: Filter out invalid flashcard objects BEFORE cleaning
          // Ensure each card has valid front/back strings (prevents crashes on malformed objects)
          if (parsed.flashcards && Array.isArray(parsed.flashcards)) {
            parsed.flashcards = parsed.flashcards
              .filter((card) => card && typeof card === "object" && card.front && card.back)
              .map((card) => ({
                front: this.cleanTextFromFormatting(card.front || ""),
                back: this.cleanTextFromFormatting(card.back || ""),
              }));
          }
        } catch (jsonError) {
          logWarn("Primary JSON parsing failed, attempting string repair", {
            error: jsonError instanceof Error ? jsonError.message : "Unknown JSON error",
            textLength: cleanJsonText.length,
          });

          // Try string repair before falling back to manual extraction
          try {
            let repairedJson = cleanJsonText;

            // ✅ FIX: Handle Filipino special characters and Unicode escaping
            // Fix literal newlines in string values
            repairedJson = repairedJson.replace(/"([^"\\]*(?:\\.[^"\\]*)*)"/g, (match, content) => {
              let fixed = content;

              // Fix newlines
              if (fixed.includes("\n") || fixed.includes("\r")) {
                fixed = fixed.replace(/\r\n/g, "\\n").replace(/\n/g, "\\n").replace(/\r/g, "\\r");
              }

              // ✅ Fix unescaped quotes within strings (common in Filipino text)
              fixed = fixed.replace(/(?<!\\)"/g, '\\"');

              return `"${fixed}"`;
            });

            // ✅ Fix trailing commas before closing braces/brackets (common Gemini error)
            repairedJson = repairedJson.replace(/,(\s*[}\]])/g, "$1");

            parsed = JSON.parse(repairedJson) as FlashcardResult;
            logSuccess("✅ Flashcard JSON parsed after string repair");
          } catch (repairError) {
            logWarn("String repair failed, falling back to manual extraction");
            // Fallback: Try to manually extract data using regex with HF context
            // ✅ FIX: Pass count parameter to extract the correct number of flashcards
            parsed = this.extractFlashcardDataManually(text, credibleSources, hfAnalysis, count);
          }

          // ✅ FIX: Ensure fallback extraction also has valid sources array
          if (!parsed.sources || !Array.isArray(parsed.sources)) {
            parsed.sources = [];
            logDebug("Initialized empty sources array after manual extraction fallback");
          }
        }

        // ✅ FIX: Change validation to allow empty sources array (was failing with !parsed.sources check)
        // Validate required fields
        if (
          !parsed.flashcards ||
          !Array.isArray(parsed.flashcards) ||
          !parsed.topic ||
          !parsed.subject ||
          !Array.isArray(parsed.sources) // Changed from !parsed.sources to !Array.isArray check
        ) {
          logWarn("Missing required fields in flashcard response", {
            hasFlashcards: !!parsed.flashcards,
            isArray: Array.isArray(parsed.flashcards),
            hasTopic: !!parsed.topic,
            hasSubject: !!parsed.subject,
            hasSources: !!parsed.sources,
            isSourcesArray: Array.isArray(parsed.sources),
          });
          throw new Error("Invalid flashcard response structure");
        }

        // ✅ ENHANCED QUALITY VALIDATION: Filter out low-quality, incomplete, or malformed flashcards
        const validFlashcards = parsed.flashcards.filter((card) => {
          if (!card || !card.front || !card.back) return false;
          if (typeof card.front !== "string" || typeof card.back !== "string") return false;

          const front = card.front.trim();
          const back = card.back.trim();

          // Minimum length requirements
          if (front.length < 10 || back.length < 15) return false; // Increased from 5 to 10 for questions

          // ❌ REJECT incomplete/malformed questions with stray special characters
          // Examples: "It is derived from the Latin word \", "What is the meaning of \"
          const hasIncompleteEnding = /[\\"`]$/.test(front) || /[\\"`]$/.test(back);
          if (hasIncompleteEnding) {
            logDebug("Rejected flashcard: incomplete ending detected", { front, back });
            return false;
          }

          // ❌ REJECT questions lacking proper sentence structure
          // Must have at least 3 words and end with proper punctuation or be a complete phrase
          const wordCount = front.split(/\s+/).length;
          if (wordCount < 3) {
            logDebug("Rejected flashcard: too few words", { front, wordCount });
            return false;
          }

          // ❌ REJECT questions with excessive special characters (malformed JSON artifacts)
          const specialCharCount = (front.match(/[\\`"{}[\]]/g) || []).length;
          if (specialCharCount > 2) {
            logDebug("Rejected flashcard: excessive special characters", {
              front,
              specialCharCount,
            });
            return false;
          }

          // ❌ REJECT vague/low-quality questions lacking context
          // Examples: "It is derived from", "What does it mean", "Define it"
          const vaguePatterns = [
            /^(it|this|that|these|those)\s+(is|are|was|were)\s+\w+\s+(from|by|in|at|to)\s*$/i,
            /^what\s+(is|are|does|do)\s+(it|this|that)\s*\?*$/i,
            /^define\s+(it|this|that)\s*\?*$/i,
            /^\w+\s+from\s+the\s+\w+\s+word\s*\\*\s*$/i, // "derived from the Latin word \"
          ];

          const isVague = vaguePatterns.some((pattern) => pattern.test(front));
          if (isVague) {
            logDebug("Rejected flashcard: vague/low-quality question", { front });
            return false;
          }

          // ✅ ACCEPT: Valid, complete, contextual flashcard
          return true;
        });

        // Remove duplicate flashcards (same front question)
        const uniqueFlashcards: Flashcard[] = [];
        const seenFronts = new Set<string>();

        for (const card of validFlashcards) {
          const normalizedFront = card.front.trim().toLowerCase();
          if (!seenFronts.has(normalizedFront)) {
            seenFronts.add(normalizedFront);
            uniqueFlashcards.push(card);
          }
        }

        logDebug("Flashcard validation results", {
          totalGenerated: parsed.flashcards.length,
          validCount: validFlashcards.length,
          uniqueCount: uniqueFlashcards.length,
          duplicatesRemoved: validFlashcards.length - uniqueFlashcards.length,
        });

        // ✅ STRICT: Always require at least 10 unique flashcards
        // User's uploaded content is the PRIMARY SOURCE - external sources are only for fact-checking
        // If content is insufficient, Gemini should generate more cards from the user's material
        if (uniqueFlashcards.length < 10) {
          logWarn("Insufficient unique flashcards generated", {
            uniqueCount: uniqueFlashcards.length,
            validCount: validFlashcards.length,
            totalCount: parsed.flashcards.length,
          });
          throw new Error(
            `Insufficient flashcards generated: only ${uniqueFlashcards.length} unique cards (need at least 10)`
          );
        }

        // Use whatever unique flashcards we have (10-20)
        // Don't pad with duplicates - better to have fewer quality cards
        parsed.flashcards = uniqueFlashcards.slice(0, 20); // Max 20 cards

        // CRITICAL FIX: Prioritize searched credible sources over Gemini's generic sources
        // This ensures we always show real academic URLs, not generic fallback text
        if (credibleSources.length > 0) {
          // Start with credible sources from LangChain search (these are the real URLs)
          // ✅ NEW: Filter out off-topic sources
          const validatedCredibleSources = credibleSources.slice(0, 5).filter((source) => {
            // Check for off-topic sources
            if (
              this.isObviouslyOffTopic(
                source,
                parsed.subject || hfAnalysis?.subject || "",
                keyTopics || []
              )
            ) {
              logWarn("Filtered off-topic source from flashcard credible sources", {
                source,
                subject: parsed.subject,
              });
              return false;
            }
            return true;
          });

          // Start with normalized URLs from credible sources (track normalized forms)
          const seenNormalizedUrls = new Set<string>();
          const finalSources: string[] = [];

          // Add validated credible sources first
          validatedCredibleSources.forEach((source) => {
            try {
              const normalized = this.normalizeUrl(source);
              if (!seenNormalizedUrls.has(normalized)) {
                seenNormalizedUrls.add(normalized);
                finalSources.push(source);
              }
            } catch {
              // If normalization fails, use exact string comparison
              if (!finalSources.includes(source)) {
                finalSources.push(source);
              }
            }
          });

          // Keep Gemini's original sources for checking
          const geminiSources = Array.isArray(parsed.sources) ? parsed.sources : [];

          // Only add Gemini's sources if they look like real URLs (not generic text) AND are on-topic AND not duplicates
          geminiSources.forEach((geminiSource) => {
            // Check if it's a real URL or specific source reference
            if (
              geminiSource &&
              (geminiSource.includes("http") ||
                geminiSource.includes(".edu") ||
                geminiSource.includes(".gov") ||
                geminiSource.includes(".org"))
            ) {
              // Check for duplicates using normalized URLs
              let isDuplicate = false;
              try {
                const normalizedGemini = this.normalizeUrl(geminiSource);
                isDuplicate = seenNormalizedUrls.has(normalizedGemini);
                if (!isDuplicate) {
                  seenNormalizedUrls.add(normalizedGemini);
                }
              } catch {
                // Fallback to exact string comparison
                isDuplicate = finalSources.includes(geminiSource);
              }

              if (isDuplicate) {
                logDebug("Skipping duplicate Gemini source (already in credible sources)", {
                  source: geminiSource,
                });
                return; // Skip this source
              }

              // ✅ Validate topic relevance
              if (
                !this.isObviouslyOffTopic(
                  geminiSource,
                  parsed.subject || hfAnalysis?.subject || "",
                  keyTopics || []
                )
              ) {
                finalSources.push(geminiSource);
              } else {
                logWarn("Filtered off-topic Gemini source from flashcards", {
                  source: geminiSource,
                  subject: parsed.subject,
                });
              }
            }
          });

          // Update parsed sources with the deduplicated finalSources list
          parsed.sources = finalSources.slice(0, 6); // Limit to 6 sources max

          logSuccess("Prioritized searched credible sources for flashcards (deduplicated)", {
            totalSources: parsed.sources.length,
            searchedSources: validatedCredibleSources.length,
            geminiSourcesCount: geminiSources.length,
            duplicatesFiltered: seenNormalizedUrls.size - parsed.sources.length,
            finalSources: parsed.sources,
          });
        } else {
          // No credible sources found - use Gemini's sources or fallback
          if (!Array.isArray(parsed.sources) || parsed.sources.length === 0) {
            parsed.sources = ["AI-generated flashcards - verify with authoritative sources"];
            logWarn("No credible sources found - using fallback message", {
              credibleSourcesLength: credibleSources.length,
            });
          } else {
            logDebug("Using Gemini-provided sources (no LangChain sources found)", {
              geminiSourcesCount: parsed.sources.length,
              sources: parsed.sources,
            });
          }
        }

        // Use HF-detected subject if Gemini's subject doesn't match Firebase subjects
        if (hfAnalysis.isReady && hfAnalysis.subject) {
          logDebug("Validating subject match with HF analysis", {
            geminiSubject: parsed.subject,
            hfSubject: hfAnalysis.subject,
          });

          // If the HF subject is more specific and educational, prefer it
          const philippineSubjects = [
            "Oral Communication",
            "Reading and Writing",
            "21st Century Literature from the Philippines and the World",
            "Komunikasyon at Pananaliksik sa Wika at Kulturang Pilipino",
            "Pagbasa at Pagsusuri ng Iba't-Ibang Teksto Tungo sa Pananaliksik",
            "General Mathematics",
            "Statistics and Probability",
            "Earth and Life Science",
            "Physical Science",
            "Earth Science",
          ];

          const hfMatchesPhilippine = philippineSubjects.some(
            (subject) =>
              hfAnalysis.subject.toLowerCase().includes(subject.toLowerCase()) ||
              subject.toLowerCase().includes(hfAnalysis.subject.toLowerCase())
          );

          if (hfMatchesPhilippine) {
            logDebug("Using HF-detected subject for better accuracy", {
              original: parsed.subject,
              updated: hfAnalysis.subject,
            });
            parsed.subject = hfAnalysis.subject;
          }
        }

        logSuccess("Flashcard generation completed successfully", {
          flashcardCount: parsed.flashcards.length,
          topic: parsed.topic,
          subject: parsed.subject,
          sourceCount: parsed.sources.length,
          model: "gemini-2.5-flash",
          detectedLanguage: hfAnalysis.language,
          hfEnhanced: hfAnalysis.isReady,
        });

        return parsed;
      } catch (parseError) {
        logError("Failed to parse JSON response from Gemini 2.5-flash", parseError);
        throw new Error("Failed to parse flashcard response");
      }
    } catch (error: any) {
      logError("Error generating flashcards with Gemini 2.5-flash", error);

      // Handle specific API errors
      if (error.message?.includes("API_KEY_INVALID")) {
        throw new Error("Invalid API key. Please check your Gemini API key configuration.");
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        throw new Error("API quota exceeded. Please try again later or check your API limits.");
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        throw new Error("Rate limit exceeded. Please wait a moment and try again.");
      } else if (error.message?.includes("blocked")) {
        throw new Error(
          "Content was blocked by safety filters. Please try with different content."
        );
      } else {
        throw new Error(`Failed to generate flashcards: ${error.message || "Unknown error"}`);
      }
    }
  }

  /**
   * Manual extraction of flashcard data when JSON parsing fails
   * Enhanced with HF analysis for better subject detection
   */
  private extractFlashcardDataManually(
    text: string,
    credibleSources: string[],
    hfAnalysis?: HFAnalysisResult,
    count: number = 10 // ✅ FIX: Default to 10 instead of 5
  ): FlashcardResult {
    logInfo("Attempting manual flashcard data extraction", {
      hasHFAnalysis: !!hfAnalysis,
      hfLanguage: hfAnalysis?.language,
      hfSubject: hfAnalysis?.subject,
      targetCount: count,
    });

    try {
      const flashcards: Flashcard[] = [];

      // ✅ ENHANCED: More aggressive patterns to extract flashcards from malformed JSON
      const patterns = [
        // Pattern 1: "front": "question", "back": "answer" (may have escaped quotes)
        /"front"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"\s*,\s*"back"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/g,
        // Pattern 2: "front":"question","back":"answer" (no spaces)
        /"front":"([^"]+)","back":"([^"]+)"/g,
        // Pattern 3: Q: question A: answer
        /Q:\s*(.+?)\s*A:\s*(.+?)(?=Q:|$)/gs,
        // Pattern 4: Front: question Back: answer
        /Front:\s*(.+?)\s*Back:\s*(.+?)(?=Front:|$)/gs,
        // Pattern 5: Number. Question -> Answer
        /\d+\.\s*(.+?)\s*->\s*(.+?)(?=\d+\.|$)/gs,
        // Pattern 6: { front: "...", back: "..." } (with or without quotes on keys)
        /\{\s*front\s*:\s*"([^"]+)"\s*,\s*back\s*:\s*"([^"]+)"\s*\}/gi,
      ];

      for (const pattern of patterns) {
        let match;
        while ((match = pattern.exec(text)) !== null && flashcards.length < count * 2) {
          // Extract twice as many, will dedupe later
          const front = match[1]?.trim();
          const back = match[2]?.trim();

          if (front && back && front.length > 5 && back.length > 5) {
            // Clean escaped characters from JSON
            const cleanedFront = front
              .replace(/\\n/g, " ")
              .replace(/\\"/g, '"')
              .replace(/\\\\/g, "\\")
              .replace(/^["']|["']$/g, "");
            const cleanedBack = back
              .replace(/\\n/g, " ")
              .replace(/\\"/g, '"')
              .replace(/\\\\/g, "\\")
              .replace(/^["']|["']$/g, "");

            flashcards.push({
              front: this.cleanTextFromFormatting(cleanedFront),
              back: this.cleanTextFromFormatting(cleanedBack),
            });
          }
        }

        if (flashcards.length >= count) break;
      }

      // ✅ DEDUPLICATE: Remove duplicate flashcards based on front question
      const seenFronts = new Set<string>();
      const uniqueFlashcards = flashcards.filter((card) => {
        const normalized = card.front.trim().toLowerCase();
        if (seenFronts.has(normalized)) {
          return false;
        }
        seenFronts.add(normalized);
        return true;
      });

      logInfo(
        `Manual extraction: Found ${flashcards.length} flashcards, ${uniqueFlashcards.length} unique`
      );

      // Extract topic and subject - prefer HF analysis if available
      let topic = "Generated Flashcards";
      let subject = hfAnalysis?.subject || "General";

      // Try to extract topic from common patterns
      const topicPatterns = [
        /"topic"\s*:\s*"([^"]+)"/i,
        /topic:\s*(.+?)(?=,|\n|$)/i,
        /Topic:\s*(.+?)(?=,|\n|$)/i,
      ];

      for (const pattern of topicPatterns) {
        const match = text.match(pattern);
        if (match && match[1]) {
          topic = this.cleanTextFromFormatting(match[1].trim().replace(/^["']|["']$/g, ""));
          break;
        }
      }

      // Try to extract subject from common patterns only if HF didn't provide one
      if (!hfAnalysis?.subject) {
        const subjectPatterns = [
          /"subject"\s*:\s*"([^"]+)"/i,
          /subject:\s*(.+?)(?=,|\n|$)/i,
          /Subject:\s*(.+?)(?=,|\n|$)/i,
        ];

        for (const pattern of subjectPatterns) {
          const match = text.match(pattern);
          if (match && match[1]) {
            subject = this.cleanTextFromFormatting(match[1].trim().replace(/^["']|["']$/g, ""));
            break;
          }
        }
      } else {
        logDebug("Using HF-detected subject in manual extraction", { subject });
      }

      // ✅ FIX: Use deduplicated flashcards, respect the requested count
      // If we extracted fewer cards than requested, that's fine (better than filler content)

      const result: FlashcardResult = {
        flashcards: uniqueFlashcards.slice(0, count), // ✅ Use deduplicated flashcards
        topic,
        subject,
        sources:
          credibleSources.length > 0
            ? credibleSources.slice(0, 5).map((source) => this.cleanSourceText(source)) // ✅ FIX: Max 5 sources
            : ["AI-generated content - please verify with authoritative sources"],
      };

      logSuccess("Manual flashcard extraction completed", {
        extractedCards: result.flashcards.length,
        topic: result.topic,
        subject: result.subject,
      });

      return result;
    } catch (error) {
      logError("Manual extraction failed", error);

      // Ultimate fallback - create basic flashcards
      const fallbackFlashcards: Flashcard[] = [];
      for (let i = 1; i <= count; i++) {
        // ✅ FIX: Use count parameter
        fallbackFlashcards.push({
          front: `Study question ${i} based on the provided content`,
          back: `This is a generated answer ${i}. Please review the original content for accurate information.`,
        });
      }

      return {
        flashcards: fallbackFlashcards,
        topic: "Study Flashcards",
        subject: "General",
        sources: ["AI-generated content - please verify with authoritative sources"],
      };
    }
  }

  /**
   * Test the flashcard generation with Gemini 2.5-flash
   */
  async testFlashcardGeneration(): Promise<boolean> {
    try {
      logInfo("Testing flashcard generation with Gemini 2.5-flash");

      const testContent = `
        Statistics and Probability are fundamental concepts in mathematics education. 
        Students learn to analyze data, calculate probabilities, and understand 
        statistical distributions. Key concepts include mean, median, mode, 
        standard deviation, and probability distributions.
      `;

      const testStartTime = Date.now();
      const result = await this.generateFlashcards(testContent, "test-statistics.txt", 20); // Request 20 flashcards for comprehensive test
      const testTime = Date.now() - testStartTime;

      // Validate the results
      const isValid = Boolean(
        result.flashcards &&
          Array.isArray(result.flashcards) &&
          result.flashcards.length === 20 &&
          result.flashcards.every((card) => card.front && card.back) &&
          result.topic &&
          result.subject &&
          result.sources &&
          Array.isArray(result.sources)
      );

      logSuccess("Flashcard generation test completed", {
        testTime: `${testTime}ms`,
        isValid,
        flashcardCount: result.flashcards?.length || 0,
        topic: result.topic,
        subject: result.subject,
        sourceCount: result.sources?.length || 0,
        model: "gemini-2.5-flash",
      });

      return isValid;
    } catch (error) {
      logError("Flashcard generation test failed", error);
      return false;
    }
  }

  /**
   * Generate post-test questions following the same AI pipeline as summaries and flashcards
   *
   * Pipeline:
   * 1. Content safety check
   * 2. HF language detection
   * 3. Extract key topics
   * 4. Google Search for credible sources
   * 5. HF reranking
   * 6. BeautifulSoup content scraping
   * 7. Gemini question generation (10 questions with fact-checking)
   */
  async generatePostTest(
    content: string,
    fileName?: string,
    preferredLanguage?: "english" | "filipino",
    sessionSubject?: string, // Subject from completed session
    scrapedSources?: Array<{ url: string; content: string; title: string; word_count: number }> // ← NEW: Scraped educational sources for fact-checking
  ): Promise<PostTestResult> {
    // 🔍 TRACE: Log entry to identify duplicate calls
    const callId = `POST-TEST-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.warn(`\n🚨🚨🚨 [${callId}] generatePostTest() INVOKED 🚨🚨🚨`);
    console.warn(`   Content length: ${content.length}`);
    console.warn(`   File name: ${fileName || "none"}`);
    console.trace(`[${callId}] Call stack trace:`);

    // Content size optimization
    const MAX_CONTENT_LENGTH = 50000;
    let processedContent = content;
    if (content.length > MAX_CONTENT_LENGTH) {
      logWarn(
        `Content too large for post-test (${content.length} chars), truncating to ${MAX_CONTENT_LENGTH}`,
        {
          originalLength: content.length,
          truncatedLength: MAX_CONTENT_LENGTH,
        }
      );
      processedContent = content.substring(0, MAX_CONTENT_LENGTH);
    }

    // STEP 0: Content safety check
    logDebug("Performing content safety check before post-test generation");
    const safetyCheck = checkContentSafety(processedContent);

    if (safetyCheck.blocked) {
      logError("Content blocked due to safety concerns", {
        severity: safetyCheck.severity,
        reasonCount: safetyCheck.reasons.length,
        fileName: fileName || "unnamed",
      });
      throw new Error(
        safetyCheck.message || "Content contains inappropriate material and cannot be processed."
      );
    }

    if (safetyCheck.severity === "medium") {
      logWarn("Content contains potentially inappropriate language", {
        severity: safetyCheck.severity,
        reasons: safetyCheck.reasons,
      });
    }

    // Check API key
    if (!API_KEY || API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
      throw new Error(
        "Gemini API key not configured. Please add your API key to the environment variables."
      );
    }

    try {
      logInfo("Starting post-test generation with Gemini 2.5-flash", {
        contentLength: processedContent.length,
        fileName: fileName || "unnamed",
        model: "gemini-2.5-flash",
        safetyStatus: "passed",
      });

      // STEP 1: Backend HF language & subject detection
      logDebug("Analyzing content for language and subject detection", {
        skipSubjectDetection: !!sessionSubject,
        sessionSubject: sessionSubject || "none",
      });

      const backendAIService3 = await getBackendAIService();

      // 🔒 CRITICAL FIX #3: Runtime validation of backendAIService
      if (!backendAIService3 || typeof backendAIService3.analyzeContent !== "function") {
        logError("BackendAIService not properly initialized for post-test", {
          hasService: !!backendAIService3,
          hasMethod: backendAIService3 ? typeof backendAIService3.analyzeContent : "N/A",
        });
        throw new Error("Backend AI service initialization failed - cannot generate post-test");
      }

      const hfAnalysis: HFAnalysisResult = await this.withTimeout(
        backendAIService3
          .analyzeContent(processedContent.substring(0, 2000), !!sessionSubject)
          .catch((err: Error) => {
            // 🔒 CRITICAL FIX #2: Error handling in Promise chain
            logError("Backend HF analysis failed for post-test, using fallback", err);
            return {
              language: preferredLanguage || "English",
              languageConfidence: 50,
              subject: sessionSubject || "General Knowledge",
              subjectConfidence: 50,
              isReady: false,
              processingTime: 0,
            } as HFAnalysisResult;
          }),
        10000,
        "Backend HuggingFace post-test analysis"
      );

      // 🔒 CRITICAL FIX #1: Null checks on hfAnalysis properties
      // Use session subject if provided, otherwise validate and fallback
      if (sessionSubject) {
        hfAnalysis.subject = sessionSubject;
        hfAnalysis.subjectConfidence = 100;
        logInfo("Using session subject for post-test", {
          sessionSubject,
        });
      } else {
        // Ensure subject is never null/undefined
        if (!hfAnalysis.subject || hfAnalysis.subject.trim() === "") {
          hfAnalysis.subject = "General Knowledge";
          hfAnalysis.subjectConfidence = 50;
          logWarn("HF analysis returned empty subject for post-test, using fallback", {
            fallback: "General Knowledge",
          });
        }
      }

      // Ensure language is never null/undefined
      if (!hfAnalysis.language || hfAnalysis.language.trim() === "") {
        hfAnalysis.language = preferredLanguage || "English";
        hfAnalysis.languageConfidence = 50;
        logWarn("HF analysis returned empty language for post-test, using fallback", {
          fallback: hfAnalysis.language,
        });
      }

      logDebug("HuggingFace analysis completed", {
        language: hfAnalysis.language,
        subject: hfAnalysis.subject,
        isReady: hfAnalysis.isReady,
        fromSession: !!sessionSubject,
      });

      // STEP 2: Extract key topics
      const keyTopics = this.extractKeyTopicsWithHF(processedContent, hfAnalysis);
      logDebug("Extracted topics for post-test generation", {
        topics: keyTopics,
        detectedLanguage: hfAnalysis.language,
        detectedSubject: hfAnalysis.subject,
      });

      // STEP 3: Search for credible sources with fact-checking
      // ✅ PERFORMANCE OPTIMIZATION: Use same timeout as flashcards (30s) instead of 90s
      logDebug("Searching credible sources for post-test fact-checking...");
      const searchStartTime = Date.now();

      const credibleSourcesResult = await Promise.race([
        this.searchCredibleSourcesEnhanced(
          keyTopics,
          hfAnalysis.subject || "General Knowledge"
        ).catch((err: Error) => {
          // 🔒 CRITICAL FIX #2: Error handling in Promise.race branch
          logError("Post-test source search failed, continuing with empty sources", err);
          return [];
        }),
        new Promise<string[]>(
          (resolve) =>
            setTimeout(() => {
              logWarn("Post-test source search timeout after 20s, proceeding with empty sources");
              resolve([]);
            }, 20000) // ✅ OPTIMIZED: 20s timeout (from 30s) - backend completes in ~15s, this gives 5s buffer
        ),
      ]);

      const credibleSourcesRaw = Array.isArray(credibleSourcesResult) ? credibleSourcesResult : [];
      const searchTime = Date.now() - searchStartTime;

      // ✅ NEW: Filter out off-topic sources for post-test (same as summaries)
      const credibleSources = credibleSourcesRaw.filter((source) => {
        const isOffTopic = this.isObviouslyOffTopic(source, hfAnalysis.subject, keyTopics);
        if (isOffTopic) {
          logWarn("Filtered off-topic source from post-test sources", {
            url: source,
            subject: hfAnalysis.subject,
          });
        }
        return !isOffTopic;
      });

      // ✅ NEW: Deduplicate sources for post-test
      const deduplicatedSources = credibleSources.filter((source, index, self) => {
        return (
          index ===
          self.findIndex((s) => {
            try {
              return this.normalizeUrl(s) === this.normalizeUrl(source);
            } catch {
              return s === source;
            }
          })
        );
      });

      logDebug("Post-test source search completed", {
        sourceCount: deduplicatedSources.length,
        originalCount: credibleSourcesRaw.length,
        filteredCount: credibleSources.length,
        sources: deduplicatedSources.slice(0, 3),
        subject: hfAnalysis.subject,
        searchTime: `${searchTime}ms`,
      });

      // STEP 4: Fetch actual content from credible sources for fact-checking
      const outputLanguage = preferredLanguage || hfAnalysis.language;
      const languageContext = this.buildLanguageContext(hfAnalysis, outputLanguage);
      const subjectContext = this.buildSubjectContext(hfAnalysis);

      const currentYear = new Date().getFullYear();
      const pastDecadeYear = currentYear - 10;

      let sourcesContext = "";

      // ✅ NEW: Use pre-scraped sources if provided (from PostTestModal pipeline)
      const sourceContents = scrapedSources || [];

      if (sourceContents.length > 0) {
        logSuccess("✅ Using pre-scraped educational sources for fact-checking", {
          fetchedCount: sourceContents.length,
          totalWords: sourceContents.reduce((sum, s) => sum + s.word_count, 0),
        });

        sourcesContext = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎓 VERIFIED EDUCATIONAL SOURCE CONTENT FOR POST-TEST FACT-CHECKING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${sourceContents
  .map(
    (source, idx) => `
📖 SOURCE ${idx + 1}: ${source.title}
🔗 URL: ${source.url}

VERIFIED CONTENT:
${source.content.substring(0, 1200)} ${source.content.length > 1200 ? "..." : ""}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
  )
  .join("\n")}

🔴 CRITICAL: All post-test questions and answers MUST be factually accurate and based on verified sources above.
Cross-reference student's content with these sources. If user content contradicts sources, use SOURCE VERSION for correct answers.
`;

        logInfo("📝 Enhanced post-test prompt with fact-checked source content", {
          sourcesWithContent: sourceContents.length,
          totalContentLength: sourceContents.reduce((sum, s) => sum + s.content.length, 0),
        });
      } else if (!scrapedSources && credibleSources.length > 0) {
        // Fallback: If no scraped sources provided, fetch them (backward compatibility)
        // ✅ PERFORMANCE OPTIMIZATION: Reduce timeout from 45s to 20s (same as summaries)
        console.log(`\n${"=".repeat(80)}`);
        console.log(`📚 [POST-TEST SOURCE FETCHING] Started at ${new Date().toLocaleTimeString()}`);
        console.log(`   Fetching content from ${credibleSources.length} sources for fact-checking`);

        logInfo("🔍 Fetching source content for post-test fact-checking", {
          sourcesCount: credibleSources.length,
          purpose: "Ensure post-test accuracy with verified educational content",
        });

        const sourceContentStart = performance.now();

        const backendAIService4 = await getBackendAIService();
        const fetchedContents = await Promise.race([
          backendAIService4.scrapeSources(deduplicatedSources.slice(0, 3), 3),
          new Promise<any[]>(
            (resolve) =>
              setTimeout(() => {
                logWarn("Source scraping timeout after 20s");
                resolve([]);
              }, 20000) // ✅ OPTIMIZED: 20s timeout (same as summaries) instead of 45s
          ),
        ]);

        const sourceContentDuration = performance.now() - sourceContentStart;

        console.log(
          `✅ [POST-TEST SOURCE FETCHING] Completed in ${sourceContentDuration.toFixed(0)}ms`
        );
        console.log(
          `   Successfully fetched: ${fetchedContents.length}/${Math.min(
            3,
            credibleSources.length
          )} sources`
        );
        console.log(`${"=".repeat(80)}\n`);

        if (fetchedContents.length > 0) {
          logSuccess("✅ Post-test source content fetched", {
            fetchedCount: fetchedContents.length,
            totalWords: fetchedContents.reduce(
              (sum: number, s: { word_count: number }) => sum + s.word_count,
              0
            ),
            duration: `${sourceContentDuration.toFixed(0)}ms`,
          });

          sourcesContext = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎓 VERIFIED EDUCATIONAL SOURCE CONTENT FOR POST-TEST FACT-CHECKING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${fetchedContents
  .map(
    (source: { title: string; url: string; word_count: number; content: string }, idx: number) => `
📖 SOURCE ${idx + 1}: ${source.title}
🔗 URL: ${source.url}

VERIFIED CONTENT:
${source.content.substring(0, 1200)} ${source.content.length > 1200 ? "..." : ""}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
  )
  .join("\n")}

🔴 CRITICAL: All post-test questions and answers MUST be factually accurate and based on verified sources above.
Cross-reference student's content with these sources. If user content contradicts sources, use SOURCE VERSION for correct answers.
`;

          logInfo("📝 Enhanced post-test prompt with source content", {
            sourcesWithContent: fetchedContents.length,
            totalContentLength: fetchedContents.reduce(
              (sum: number, s: { content: string }) => sum + s.content.length,
              0
            ),
          });
        } else {
          logWarn("⚠️ No source content fetched for post-test, using URLs only");

          sourcesContext = `\n\nAUTHORITATIVE SOURCE URLS:\n${credibleSources
            .map((source, index) => `${index + 1}. ${source}`)
            .join(
              "\n"
            )}\n\nNote: Source content scraping was attempted but returned no results. These are verified authoritative educational URLs. Generate accurate post-test questions based on established educational facts from peer-reviewed academic sources, and cite these URLs as authoritative references.`;
        }
      } else {
        logDebug("ℹ️ No credible sources for post-test, using content analysis only");

        sourcesContext = `\n\nNote: Generate accurate post-test questions based on your training data about this subject. Use only verifiable educational facts that can be confirmed through standard academic sources from ${pastDecadeYear}-${currentYear}.`;
      }

      // STEP 5: Generate post-test questions with Gemini in JSON mode
      const postTestPrompt = `
Create exactly 10 UNIQUE multiple-choice questions for a post-test assessment for Grade 11-12 Filipino students.

${languageContext}

${subjectContext}

Educational content for assessment (session notes):
${processedContent.substring(0, 10000)}${processedContent.length > 10000 ? "..." : ""}

${fileName ? `Source material: ${fileName}` : ""}

${sourcesContext}

CRITICAL REQUIREMENTS:
⚠️ EACH QUESTION MUST BE COMPLETELY UNIQUE - NO DUPLICATE QUESTIONS
⚠️ ASSESSMENT LEVEL: Create questions at Grade 11-12 Senior High School level
⚠️ 60% PASSING SCORE: Students must get at least 6/10 correct to pass
⚠️ FACT-CHECKED: All correct answers must be verifiable through credible sources
⚠️ ONE CORRECT ANSWER: Each question must have exactly ONE correct answer and 3 distractors
⚠️ CLEAR EXPLANATIONS: Provide brief educational explanations for correct answers

MATHEMATICAL FORMULAS & SPECIAL NOTATION:
- For mathematical formulas, use LaTeX notation wrapped in $ for inline math or $$ for display math
- Examples: 
  * Inline: $E = mc^2$ or $x^2 + y^2 = r^2$
  * Display: $$\\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}$$
- Use LaTeX for ALL mathematical expressions, equations, formulas, symbols
- Common symbols: $\\pi$, $\\theta$, $\\alpha$, $\\beta$, $\\sum$, $\\prod$, $\\int$, $\\frac{a}{b}$, $\\sqrt{x}$, $x^2$, $x_i$

QUESTION TYPES DISTRIBUTION:
- Knowledge/Recall (3 questions): Test understanding of key concepts, definitions, facts
- Application (4 questions): Test ability to apply concepts to new situations
- Analysis (3 questions): Test ability to analyze relationships, causes, effects

QUESTION QUALITY STANDARDS:
1. CLEAR AND CONCISE: Questions should be straightforward and unambiguous
2. APPROPRIATE DIFFICULTY: Match Grade 11-12 Senior High School curriculum level
3. PLAUSIBLE DISTRACTORS: Wrong answers should be believable but clearly incorrect
4. NO TRICK QUESTIONS: Test knowledge, not ability to spot tricks
5. FACTUALLY ACCURATE: All information must be verifiable
6. CURRICULUM-ALIGNED: Follow Philippine Senior High School standards
7. ONE BEST ANSWER: Only one option should be clearly correct

ANSWER OPTIONS FORMAT:
- Provide exactly 4 options for each question
- ⚠️ CRITICAL: DO NOT include letter prefixes (A., B., C., D.) in your options
- ⚠️ CRITICAL: Return options as PLAIN TEXT ONLY (e.g., "A relation where...", NOT "A. A relation where...")
- Options should be similar in length and structure
- Avoid "All of the above" or "None of the above" unless necessary
- Randomize position of correct answer (don't always make it option A or D)

EXPLANATION REQUIREMENTS:
- Provide a brief (2-3 sentences) explanation for why the correct answer is right
- Reference the concept or principle being tested
- If applicable, cite the credible source that verifies the answer

TOPIC GENERATION:
- Create a DESCRIPTIVE, SPECIFIC topic title that clearly identifies the assessment subject
- Example: "Electromagnetic Waves and Radiation - Post-Test" NOT just "Physics Test"
- The topic should tell students exactly what they're being assessed on

Format your response as JSON:
{
  "questions": [
    {
      "question": "Clear, concise question text",
      "options": [
        "First option text WITHOUT A. prefix",
        "Second option text WITHOUT B. prefix",
        "Third option text WITHOUT C. prefix",
        "Fourth option text WITHOUT D. prefix"
      ],
      "correctAnswer": 0,
      "explanation": "Brief explanation (2-3 sentences) of why this answer is correct and the educational concept behind it"
    }
  ],
  "topic": "Descriptive, specific assessment topic title",
  "subject": "Academic subject matching Philippine curriculum",
  "sources": ["List credible educational sources used for fact-checking"],
  "correctionsApplied": [
    {
      "userClaim": "What the student wrote that was incorrect (if any)",
      "correction": "The factually correct information from verified sources",
      "sourceUrl": "URL of the credible source that verifies the correction",
      "severity": "critical|medium|low - How significant the misconception was"
    }
  ]
}

🚨 CRITICAL JSON FORMATTING REQUIREMENTS:
1. Return ONLY valid JSON - no markdown, no code blocks, no extra text
2. Use double quotes (") for all strings - NEVER single quotes (')
3. ⚠️ **QUOTES IN TEXT - PREFERRED APPROACHES** (in priority order):
   a. **BEST**: Rephrase to avoid quotes entirely
      ❌ "What is the meaning of \"AND\" in logic?"
      ✅ "What is the meaning of the AND operation in logic?"
   b. **ACCEPTABLE**: Use single quotes in the actual text (they're safe in JSON)
      ✅ "What is the meaning of the 'AND' operation?"
   c. **LAST RESORT**: If quotes are absolutely necessary, escape them: \\"
4. Escape special characters in strings: \\" for quotes, \\\\ for backslashes, \\n for newlines
5. Do NOT include literal newlines inside string values - use \\n instead
6. Remove ALL trailing commas before closing brackets ] or braces }
7. Ensure ALL property names are quoted: "question": not question:
8. correctAnswer MUST be a number (0-3), NOT a string
9. All arrays must use proper JSON array syntax with square brackets []
10. **SPECIAL CHARACTERS HANDLING**:
    - Em-dashes (–), en-dashes (—): Safe to use as-is
    - Logical symbols (∧, ∨, →, ↔, ~): Safe to use as-is
    - Mathematical symbols (±, ×, ÷, ≠, ≤, ≥): Safe to use as-is
    - Greek letters (α, β, θ, π, Σ): Safe to use as-is
    - Parentheses/brackets: Safe to use as-is
    - LaTeX: Use $\\\\land$ not $\\land$ (double backslash for JSON)
11. Test your JSON is valid before responding
12. If including LaTeX notation, remember to DOUBLE all backslashes: $\\\\frac{a}{b}$ not $\\frac{a}{b}$

QUALITY CHECKLIST BEFORE RESPONDING:
✓ All 10 questions have DIFFERENT topics/concepts (no duplicates)
✓ Each question has exactly 4 options
✓ correctAnswer is an index (0-3) matching the correct option
✓ All explanations are 2-3 sentences and educationally sound
✓ Questions test different cognitive levels (recall, application, analysis)
✓ Topic title is descriptive and specific to the content
✓ Subject matches Philippine Senior High School curriculum
✓ All answers are factually accurate and verifiable
✓ Plain text only - no markdown formatting

IMPORTANT: 
- Respond only with valid JSON
- Create exactly 10 UNIQUE questions with NO DUPLICATES
- Each question must have exactly 4 options and 1 correct answer (index 0-3)
- Explanations must be educational and fact-based
- Passing score is 60% (6/10 correct)
${
  credibleSources.length > 0
    ? "- YOU MUST include the provided credible sources in your response - they have been specifically selected and validated for this topic."
    : ""
}
`;

      logDebug("Sending post-test generation request to Gemini 2.5-flash with JSON mode");

      // 🎯 Use a dedicated model instance configured for JSON output
      const postTestModel = getGenAI().getGenerativeModel({
        model: "gemini-2.5-flash",
        generationConfig: {
          temperature: 0.2, // Lower temperature for more consistent JSON structure
          topP: 0.9,
          topK: 40,
          maxOutputTokens: 8192,
          responseMimeType: "application/json", // ← Force JSON response format
        },
        safetySettings: [
          {
            category: HarmCategory.HARM_CATEGORY_HARASSMENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
          {
            category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
            threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
          },
        ],
      });

      const result = await this.withTimeout(
        postTestModel.generateContent(postTestPrompt),
        90000, // ✅ INCREASED: 90 second timeout (was 60s) for slower networks/devices
        "Gemini post-test generation"
      );
      const response = result.response;

      if (response.promptFeedback?.blockReason) {
        throw new Error(`Content was blocked: ${response.promptFeedback.blockReason}`);
      }

      const text = response.text();

      if (!text || text.trim().length === 0) {
        throw new Error("Empty response received from Gemini API");
      }

      logDebug("Received response from Gemini 2.5-flash for post-test", {
        responseLength: text.length,
        firstChars: text.substring(0, 100),
      });

      // Parse JSON response with improved error handling
      try {
        let jsonText = text.trim();

        // Remove markdown code blocks
        jsonText = jsonText.replace(/```json\s*/, "").replace(/```\s*$/, "");

        // Remove any text before first { and after last }
        jsonText = jsonText.replace(/^[^{]*/, "").replace(/[^}]*$/, "");

        const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          logWarn("No JSON object found in post-test response", {
            responseLength: text.length,
            cleanedText: jsonText.substring(0, 200) + "...",
          });
          throw new Error("Invalid JSON response from Gemini API - no JSON object found");
        }

        let cleanJsonText = jsonMatch[0];

        // ✅ CRITICAL PRE-CLEANING: Fix common malformed patterns BEFORE our main cleaning
        // These patterns occur when Gemini makes JSON syntax errors

        // Fix: Remove BOM (Byte Order Mark) if present
        cleanJsonText = cleanJsonText.replace(/^\uFEFF/, "");

        // Fix: Remove zero-width characters that break JSON parsing
        cleanJsonText = cleanJsonText.replace(/[\u200B-\u200D\uFEFF]/g, "");

        // Fix: Normalize line endings (some responses have mixed \r\n and \n)
        cleanJsonText = cleanJsonText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

        // 🔧 IMPROVED JSON CLEANING PIPELINE:

        // Step 1: Remove trailing commas before closing brackets/braces
        cleanJsonText = cleanJsonText.replace(/,(\s*[\]}])/g, "$1");

        // Step 2: Quote unquoted property names (but be careful not to quote numbers)
        cleanJsonText = cleanJsonText.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');

        // Step 3: Remove control characters (but preserve structural whitespace)
        cleanJsonText = cleanJsonText.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, "");

        // Step 3.5: Fix invalid escape sequences and normalize backslashes
        // JSON only allows: \", \\, \/, \b, \f, \n, \r, \t, \uXXXX
        // ✅ CRITICAL FIX: Handle LaTeX backslashes AND escaped quotes correctly
        // ✅ NEW APPROACH: Manually parse strings character-by-character with proper escape detection

        // Helper function to check if a quote is escaped
        function isEscaped(str: string, pos: number): boolean {
          let backslashCount = 0;
          let i = pos - 1;
          while (i >= 0 && str[i] === "\\") {
            backslashCount++;
            i--;
          }
          // If odd number of backslashes, the quote is escaped
          return backslashCount % 2 === 1;
        }

        let result = "";
        let i = 0;
        let inString = false;
        let stringContent = "";

        while (i < cleanJsonText.length) {
          const char = cleanJsonText[i];

          if (char === '"' && !isEscaped(cleanJsonText, i)) {
            // Un-escaped quote - toggle string mode
            if (inString) {
              // End of string - process the content for LaTeX backslashes
              let processed = "";
              let j = 0;
              while (j < stringContent.length) {
                if (stringContent[j] === "\\") {
                  if (j + 1 < stringContent.length) {
                    const nextChar = stringContent[j + 1];
                    // Valid JSON escape characters: " \ / b f n r t u
                    if (
                      nextChar === '"' ||
                      nextChar === "\\" ||
                      nextChar === "/" ||
                      nextChar === "b" ||
                      nextChar === "f" ||
                      nextChar === "n" ||
                      nextChar === "r" ||
                      nextChar === "t"
                    ) {
                      // Already a valid escape - keep as is
                      processed += "\\" + nextChar;
                      j += 2;
                    } else if (nextChar === "u" && j + 5 < stringContent.length) {
                      // Unicode escape \uXXXX - keep as is
                      processed += stringContent.substring(j, j + 6);
                      j += 6;
                    } else {
                      // Invalid escape - this is LaTeX, double the backslash
                      processed += "\\\\";
                      j++;
                    }
                  } else {
                    // Backslash at end - double it
                    processed += "\\\\";
                    j++;
                  }
                } else {
                  // Regular character
                  processed += stringContent[j];
                  j++;
                }
              }
              // End string: add processed content and closing quote (opening quote already added)
              result += processed + '"';
              stringContent = "";
              inString = false;
            } else {
              // Start of string - add opening quote
              result += '"';
              inString = true;
            }
          } else if (inString) {
            // Inside a string - accumulate content
            stringContent += char;
          } else {
            // Outside string - copy as-is
            result += char;
          }

          i++;
        }

        cleanJsonText = result;

        // Step 4: Try to parse as-is first before aggressive cleaning
        let parsed: PostTestResult | null = null;
        let parseError: Error | null = null;

        try {
          parsed = JSON.parse(cleanJsonText) as PostTestResult;
          logSuccess("✅ JSON parsed successfully on first attempt");
        } catch (firstError: any) {
          parseError = firstError;

          logWarn("⚠️ Initial JSON parse failed, attempting aggressive cleaning", {
            error: firstError.message,
            position: firstError.message.match(/position (\d+)/)?.[1],
          });

          // Extract position from error message
          const positionMatch = firstError.message.match(/position (\d+)/);
          if (positionMatch) {
            const errorPos = parseInt(positionMatch[1]);
            logDebug("JSON error context", {
              before: cleanJsonText.substring(Math.max(0, errorPos - 100), errorPos),
              at: cleanJsonText.substring(errorPos, errorPos + 10),
              after: cleanJsonText.substring(
                errorPos + 10,
                Math.min(cleanJsonText.length, errorPos + 100)
              ),
            });
          }

          // Aggressive fix: Try to repair common JSON issues
          // 1. First, let's try to extract just the valid JSON portion
          let repairedJson = cleanJsonText;

          // 2. COMPREHENSIVE LaTeX backslash repair (second pass, more aggressive)
          // ✅ CRITICAL FIX: Use the same character-by-character logic as first pass
          repairedJson = repairedJson.replace(/"([^"]*)"/g, (match, content) => {
            // ✅ Strategy: Process character by character to handle escapes correctly
            let result = "";
            let i = 0;

            while (i < content.length) {
              if (content[i] === "\\") {
                // Check if this is already a valid JSON escape sequence
                if (i + 1 < content.length) {
                  const nextChar = content[i + 1];
                  // Valid JSON escape characters: " \ / b f n r t u
                  if (
                    nextChar === '"' ||
                    nextChar === "\\" ||
                    nextChar === "/" ||
                    nextChar === "b" ||
                    nextChar === "f" ||
                    nextChar === "n" ||
                    nextChar === "r" ||
                    nextChar === "t"
                  ) {
                    // Already a valid escape - keep as is
                    result += "\\" + nextChar;
                    i += 2;
                  } else if (nextChar === "u" && i + 5 < content.length) {
                    // Unicode escape \uXXXX - keep as is
                    result += content.substring(i, i + 6);
                    i += 6;
                  } else {
                    // Invalid escape - this is LaTeX, need to double the backslash
                    result += "\\\\";
                    i++;
                  }
                } else {
                  // Backslash at end of string - double it
                  result += "\\\\";
                  i++;
                }
              } else {
                // Regular character
                result += content[i];
                i++;
              }
            }

            return `"${result}"`;
          });

          // 3. Try parsing the repaired version
          try {
            parsed = JSON.parse(repairedJson) as PostTestResult;
            logSuccess("✅ JSON parsed after aggressive cleaning");
          } catch (secondError: any) {
            // ✅ LAST RESORT: Try to extract and fix specific problematic sections
            logWarn("⚠️ Attempting last-resort JSON repair strategies", {
              error: secondError.message,
            });

            try {
              // Strategy 1: Try to fix common patterns that break JSON
              let finalAttempt = repairedJson;

              // Fix double-escaped backslashes that might have been over-corrected
              // Example: \\\\sin becomes \\sin (correct for JSON)
              finalAttempt = finalAttempt.replace(/\\\\\\\\/g, "\\\\");

              // Fix: Ensure all property names are quoted
              finalAttempt = finalAttempt.replace(
                /([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g,
                '$1"$2":'
              );

              // Fix: Remove any trailing commas before closing brackets/braces
              finalAttempt = finalAttempt.replace(/,(\s*[}\]])/g, "$1");

              // Try parsing one more time
              parsed = JSON.parse(finalAttempt) as PostTestResult;
              logSuccess("✅ JSON parsed after final repair attempt");
            } catch (thirdError: any) {
              logError("❌ All JSON parsing attempts failed - FULL RESPONSE LOGGED", {
                originalError: firstError.message,
                secondError: secondError.message,
                thirdError: thirdError.message,
                fullResponse: text, // ✅ NEW: Log full response for debugging
                responseLength: text.length,
                responsePreview: text.substring(0, 500),
                cleanedPreview: cleanJsonText.substring(0, 500),
                repairedPreview: repairedJson.substring(0, 500),
              });

              // ✅ ENHANCED: Log to console.error for Firebase Cloud Logging
              console.error("🔴 GEMINI JSON PARSE FAILURE - FULL RESPONSE:", {
                timestamp: new Date().toISOString(),
                environment: import.meta.env.MODE,
                fullResponse: text,
                responseLength: text.length,
                errors: {
                  attempt1: firstError.message,
                  attempt2: secondError.message,
                  attempt3: thirdError.message,
                },
              });

              // Provide helpful error message to user
              throw new Error(
                `Failed to parse Gemini JSON response after 3 attempts. ` +
                  `The AI generated invalid JSON. This may be due to complex mathematical formulas. ` +
                  `Please try again or simplify your content. ` +
                  `Error: ${thirdError.message}`
              );
            }
          }
        }

        if (!parsed) {
          throw new Error("Failed to parse JSON response after multiple attempts");
        }

        // 🐛 DEBUG: Log successful parse details
        logDebug("📊 Parsed post-test structure", {
          questionCount: parsed.questions?.length || 0,
          hasTopic: !!parsed.topic,
          hasSubject: !!parsed.subject,
          sourcesCount: parsed.sources?.length || 0,
        });

        // Validate and initialize all required fields
        if (!parsed.questions) parsed.questions = [];
        if (!parsed.topic) parsed.topic = "Post-Test Assessment";
        if (!parsed.subject) parsed.subject = "General";
        if (!parsed.sources) parsed.sources = [];
        if (!parsed.correctionsApplied) parsed.correctionsApplied = [];

        // Clean and validate questions
        if (Array.isArray(parsed.questions)) {
          parsed.questions = parsed.questions.filter((q) => {
            return (
              q.question &&
              Array.isArray(q.options) &&
              q.options.length === 4 &&
              typeof q.correctAnswer === "number" &&
              q.correctAnswer >= 0 &&
              q.correctAnswer <= 3 &&
              q.explanation
            );
          });

          // Clean formatting from text fields
          parsed.questions = parsed.questions.map((q) => ({
            question: this.cleanTextFromFormatting(q.question),
            options: q.options.map((opt) => this.cleanTextFromFormatting(opt)),
            correctAnswer: q.correctAnswer,
            explanation: this.cleanTextFromFormatting(q.explanation),
          }));
        }

        // Clean other fields
        if (parsed.topic) {
          parsed.topic = this.cleanTextFromFormatting(parsed.topic);
        }

        if (parsed.subject) {
          parsed.subject = this.cleanTextFromFormatting(parsed.subject);
        }

        // ✅ CRITICAL FIX: Filter out non-string sources BEFORE cleaning (same fix as flashcards/summaries)
        // Gemini sometimes returns: sources: ["url1", {object}, null, "url2"] which crashes cleanSourceText()
        if (Array.isArray(parsed.sources)) {
          parsed.sources = parsed.sources
            .filter((source) => source && typeof source === "string")
            .map((source) => this.cleanSourceText(source));
        }

        // Validate we have exactly 10 questions
        if (parsed.questions.length !== 10) {
          logWarn(`Expected 10 questions, got ${parsed.questions.length}`, {
            questionCount: parsed.questions.length,
          });

          if (parsed.questions.length < 10) {
            throw new Error(`Only generated ${parsed.questions.length} questions, expected 10`);
          } else {
            // Trim to exactly 10 if we have more
            parsed.questions = parsed.questions.slice(0, 10);
          }
        }

        // ✅ CRITICAL FIX: Enhance with credible sources AND deduplicate (same fix as flashcards/summaries)
        if (credibleSources.length > 0 && parsed.sources.length < 3) {
          // ✅ NEW: Filter out off-topic sources before enhancing
          const validatedCredibleSources = credibleSources.slice(0, 3).filter((source) => {
            // Check for off-topic sources
            if (
              this.isObviouslyOffTopic(
                source,
                parsed.subject || hfAnalysis?.subject || "",
                keyTopics || []
              )
            ) {
              logWarn("Filtered off-topic source from post-test credible sources", {
                source,
                subject: parsed.subject,
              });
              return false;
            }
            return true;
          });

          const enhancedSources = [...parsed.sources];
          validatedCredibleSources.forEach((source) => {
            // Check for duplicates using normalized URLs
            const isDuplicate = enhancedSources.some((s) => {
              try {
                return this.normalizeUrl(s) === this.normalizeUrl(source);
              } catch {
                return s === source || s.includes(source);
              }
            });
            if (!isDuplicate) {
              enhancedSources.push(source);
            }
          });

          // Deduplicate sources before returning AND filter off-topic Gemini sources
          const deduplicatedSources = enhancedSources.filter((source, index, self) => {
            // Check for duplicates
            const isDuplicate =
              index !==
              self.findIndex((s) => {
                try {
                  return this.normalizeUrl(s) === this.normalizeUrl(source);
                } catch {
                  return s === source;
                }
              });

            if (isDuplicate) return false;

            // ✅ NEW: Check for off-topic sources
            if (
              this.isObviouslyOffTopic(
                source,
                parsed.subject || hfAnalysis?.subject || "",
                keyTopics || []
              )
            ) {
              logWarn("Filtered off-topic Gemini source from post-test", {
                source,
                subject: parsed.subject,
              });
              return false;
            }

            return true;
          });

          parsed.sources = deduplicatedSources;
          logSuccess("Enhanced sources with search results (deduplicated)", {
            totalSources: parsed.sources.length,
            duplicatesRemoved: enhancedSources.length - deduplicatedSources.length,
          });
        }

        logSuccess("Post-test generation completed successfully", {
          hasSearchSources: credibleSources.length > 0,
          totalSources: parsed.sources.length,
          questionCount: parsed.questions.length,
          topic: parsed.topic,
          subject: parsed.subject,
        });

        return parsed;
      } catch (parseError) {
        logError("Failed to parse post-test JSON response", parseError);
        throw new Error("Failed to generate valid post-test questions. Please try again.");
      }
    } catch (error: any) {
      logError("Error generating post-test", error);

      if (error.message?.includes("API_KEY_INVALID")) {
        throw new Error("Invalid API key. Please check your Gemini API key configuration.");
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        throw new Error("API quota exceeded. Please try again later or check your API limits.");
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        throw new Error("Rate limit exceeded. Please wait a moment and try again.");
      } else if (error.message?.includes("blocked")) {
        throw new Error(
          "Content was blocked by safety filters. Please try with different content."
        );
      } else {
        throw new Error(`Failed to generate post-test: ${error.message || "Unknown error"}`);
      }
    }
  }

  /**
   * Generate AI-powered pre-test questions (10 foundational/basic questions)
   * Pre-tests focus on assessing baseline knowledge BEFORE tutoring begins
   * Uses booking context (subject + learning goals) to generate relevant questions
   *
   * @param sessionSubject - Subject from booking form (e.g., "Mathematics", "Science")
   * @param learningGoals - Student's learning goals/notes from booking form
   * @param uploadedFileContent - Optional: Tutor-uploaded materials for context
   * @param fileName - Optional: Name of uploaded file
   * @param preferredLanguage - Output language (English/Filipino)
   * @returns PreTestResult with 10 basic/foundational questions
   */
  async generatePreTest(
    sessionSubject: string,
    learningGoals: string,
    uploadedFileContent?: string,
    fileName?: string,
    preferredLanguage?: "english" | "filipino",
    scrapedSources?: Array<{ url: string; content: string; title: string; word_count: number }> // ✅ NEW: Scraped educational sources for fact-checking
  ): Promise<PostTestResult> {
    // Check API key
    if (!API_KEY || API_KEY === "YOUR_GEMINI_API_KEY_HERE") {
      throw new Error(
        "Gemini API key not configured. Please add your API key to the environment variables."
      );
    }

    try {
      logInfo("Starting pre-test generation with Gemini 2.5-flash", {
        subject: sessionSubject,
        learningGoalsLength: learningGoals?.length || 0,
        hasUploadedFile: !!uploadedFileContent,
        fileName: fileName || "none",
        model: "gemini-2.5-flash",
      });

      // Build context from booking information
      const contextContent = this.buildPreTestContext(
        sessionSubject,
        learningGoals,
        uploadedFileContent
      );

      logDebug("Pre-test context built", {
        contextLength: contextContent.length,
        subject: sessionSubject,
        hasFileContent: !!uploadedFileContent,
      });

      // Determine output language
      const outputLanguage = preferredLanguage || "english";
      const languageInstruction =
        outputLanguage === "filipino"
          ? "Generate all questions and answers in Filipino language."
          : "Generate all questions and answers in English language.";

      // ✅ NEW: Use pre-scraped sources if provided (from PreTestModal pipeline)
      let sourcesContext = "";
      let credibleSources: string[] = []; // Declare here for later use

      if (scrapedSources && scrapedSources.length > 0) {
        // Use pre-scraped educational sources for fact-checking
        credibleSources = scrapedSources.map((s) => s.url); // Extract URLs for later reference

        logSuccess("✅ Using pre-scraped educational sources for pre-test fact-checking", {
          fetchedCount: scrapedSources.length,
          totalWords: scrapedSources.reduce((sum, s) => sum + s.word_count, 0),
        });

        sourcesContext = `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎓 VERIFIED EDUCATIONAL SOURCE CONTENT FOR PRE-TEST FACT-CHECKING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

The following credible educational sources have been scraped and verified:

${scrapedSources
  .map(
    (source, idx) => `
──────────────────────────────────────────────────────────────────────────────
SOURCE ${idx + 1}: ${source.title || "Educational Resource"}
URL: ${source.url}
Word Count: ${source.word_count} words
──────────────────────────────────────────────────────────────────────────────

${source.content.substring(0, 3000)}${
      source.content.length > 3000 ? "\n[...truncated for length...]" : ""
    }

`
  )
  .join("\n")}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️ CRITICAL INSTRUCTION: USE THESE SOURCES FOR FACT-CHECKING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

✅ CROSS-REFERENCE all questions with the above educational source content
✅ VERIFY all facts, definitions, and relationships match these verified sources
✅ CITE specific sources in your explanations when possible
✅ IF there are discrepancies between sources, use the most authoritative/recent one
✅ PRIORITIZE source content over general knowledge when creating questions

`;
      } else {
        // Fallback: Search for credible sources and attempt scraping
        logDebug("No pre-scraped sources provided, searching for credible source URLs...");
        const credibleSourcesRaw = await Promise.race([
          this.searchCredibleSources([sessionSubject], sessionSubject).catch(() => []),
          new Promise<string[]>((resolve) =>
            setTimeout(() => {
              logWarn("Pre-test source search timeout after 15s");
              resolve([]);
            }, 15000)
          ),
        ]);

        // ✅ NEW: Filter out off-topic sources for pre-test
        credibleSources = credibleSourcesRaw.filter((source) => {
          const isOffTopic = this.isObviouslyOffTopic(source, sessionSubject, [sessionSubject]);
          if (isOffTopic) {
            logWarn("Filtered off-topic source from pre-test sources", {
              url: source,
              subject: sessionSubject,
            });
          }
          return !isOffTopic;
        });

        // ✅ NEW: Deduplicate sources for pre-test
        credibleSources = credibleSources.filter((source, index, self) => {
          return (
            index ===
            self.findIndex((s) => {
              try {
                return this.normalizeUrl(s) === this.normalizeUrl(source);
              } catch {
                return s === source;
              }
            })
          );
        });

        logDebug("Pre-test source search completed", {
          sourceCount: credibleSources.length,
          originalCount: credibleSourcesRaw.length,
          subject: sessionSubject,
        });

        // Attempt to scrape the found sources
        if (credibleSources.length > 0) {
          logInfo("🔍 Attempting to scrape credible sources for pre-test enhancement", {
            sourcesCount: credibleSources.length,
          });

          try {
            const backendAIService = await getBackendAIService();
            const scrapedSourcesResult = await Promise.race([
              backendAIService.scrapeSources(credibleSources.slice(0, 3), 3),
              new Promise<any[]>((resolve) =>
                setTimeout(() => {
                  logWarn("Pre-test source scraping timeout after 15s");
                  resolve([]);
                }, 15000)
              ),
            ]);

            const successfulScrapes = scrapedSourcesResult.filter(
              (s: any) => s?.success && s?.content
            );

            if (successfulScrapes.length > 0) {
              logSuccess("✅ Successfully scraped content from sources for pre-test", {
                successfulScrapes: successfulScrapes.length,
                totalWords: successfulScrapes.reduce(
                  (sum: number, s: any) => sum + (s.word_count || 0),
                  0
                ),
              });

              // Update credibleSources to only include successful URLs
              credibleSources = successfulScrapes.map((s: any) => s.url);

              sourcesContext = `\n\nSCRAPED EDUCATIONAL CONTENT FROM CREDIBLE SOURCES:\n${successfulScrapes
                .map(
                  (source: any, idx: number) =>
                    `SOURCE ${idx + 1}: ${source.url}\nTITLE: ${
                      source.title
                    }\nCONTENT: ${source.content.substring(0, 2000)}...`
                )
                .join(
                  "\n\n"
                )}\n\nIMPORTANT: Use the above verified educational content to create accurate pre-test questions.`;
            } else {
              logWarn("⚠️ No content could be scraped for pre-test, using URLs only");
              sourcesContext = `\n\nAUTHORITATIVE EDUCATIONAL SOURCE URLS:\n${credibleSources
                .slice(0, 3)
                .map((url, idx) => `${idx + 1}. ${url}`)
                .join(
                  "\n"
                )}\n\nNote: Source content scraping was attempted but returned no results. These are verified authoritative educational URLs. Generate accurate pre-test questions based on established educational facts from peer-reviewed academic sources, and cite these URLs as authoritative references.\n`;
            }
          } catch (scrapingError) {
            logError("Pre-test source scraping failed", scrapingError);
            sourcesContext = `\n\nAUTHORITATIVE EDUCATIONAL SOURCE URLS:\n${credibleSources
              .slice(0, 3)
              .map((url, idx) => `${idx + 1}. ${url}`)
              .join(
                "\n"
              )}\n\nNote: Source content scraping failed due to technical issues. These are verified authoritative educational URLs. Generate accurate pre-test questions based on established educational facts from peer-reviewed sources, and cite these URLs as authoritative references.\n`;
          }
        } else {
          sourcesContext = "";
        }
      }

      // Generate pre-test questions with Gemini
      const prompt = `You are an expert K-12 educator creating a PRE-TEST assessment.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 PRE-TEST MISSION: BASELINE KNOWLEDGE ASSESSMENT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📚 SUBJECT: ${sessionSubject}
📝 STUDENT'S LEARNING GOALS: ${learningGoals || "Not specified"}
${fileName ? `📄 TUTOR'S MATERIALS: ${fileName}` : ""}
${sourcesContext}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎓 PRE-TEST PHILOSOPHY: "BEFORE LEARNING" ASSESSMENT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

This is a PRE-TEST (administered BEFORE tutoring begins), NOT a post-test.

PURPOSE: Measure student's BASELINE KNOWLEDGE to help tutor:
✅ Identify what the student already knows
✅ Discover knowledge gaps and misconceptions
✅ Tailor the tutoring session to student's starting point
✅ Establish a baseline for measuring learning progress

🔴 CRITICAL REQUIREMENTS FOR PRE-TEST QUESTIONS:

1️⃣ FOUNDATIONAL/BASIC LEVEL (NOT Advanced)
   - Focus on fundamental concepts, definitions, basic principles
   - Assess prerequisite knowledge needed for the learning goals
   - Questions should test READINESS, not mastery
   - Example: "What is photosynthesis?" NOT "Explain the biochemical pathway of photosynthesis"

2️⃣ FOCUSED ON STUDENT'S SPECIFIC LEARNING GOALS
   - **PRIORITY**: Generate questions directly related to the student's stated learning goals/topics
   - If student specified a topic (e.g., "intermolecular forces"), focus 80% of questions on that specific topic
   - If uploaded materials provided, use that content as primary source for questions
   - Only include 1-2 general prerequisite questions if needed for context
   - Questions should assess readiness for the SPECIFIC topics the student wants to learn

3️⃣ APPROPRIATE DIFFICULTY (K-12 Level)
   - Questions should be answerable by a student WHO HAS STUDIED THE BASICS
   - NOT too easy (trivial) but NOT too hard (advanced concepts)
   - Target: Questions a prepared student could answer, but gaps will be revealed

4️⃣ CLEAR & UNAMBIGUOUS
   - Each question has ONE clearly correct answer
   - Distractors (wrong answers) should be plausible but distinctly incorrect
   - Avoid trick questions or confusing phrasing

5️⃣ EDUCATIONALLY SOUND
   - Questions must be factually accurate and based on credible sources
   - Avoid outdated information or misconceptions
   - Follow K-12 curriculum standards for ${sessionSubject}

${languageInstruction}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📖 CONTEXT FROM BOOKING FORM
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${contextContent}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 GENERATE 10 PRE-TEST QUESTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🚨 **CRITICAL FORMATTING RULE - READ THIS FIRST**:
❌ NEVER USE HTML TAGS: <em>, <i>, <b>, <strong>, <sub>, <sup>, <span>, <div>, or ANY HTML
✅ ALWAYS USE LaTeX for formulas: $H_2O$, $CO_2$, $x^2$ (NOT <sub> or <sup>)
✅ For emphasis: Use **bold markdown** or plain text (NOT <em> or <i>)
⚠️ HTML tags display as broken text like "< em >" and ruin the user experience!

🎯 **PRIMARY FOCUS**: Student's Learning Goals = "${learningGoals || "General understanding"}"
${
  learningGoals
    ? `\n🔴 CRITICAL: 8-9 questions MUST be directly about the student's stated topic/goals above.\n`
    : ""
}

Generate EXACTLY 10 multiple-choice questions following these guidelines:

QUESTION TYPES (Mix of these):
✅ Definition/Concept Questions (30-40%): "What is...?", "Which of the following defines...?"
✅ Basic Application (20-30%): Simple problem-solving using fundamental concepts
✅ Relationship Questions (20-30%): "How are X and Y related?", "What happens when...?"
✅ Recognition Questions (10-20%): Identify examples, classify concepts

DIFFICULTY DISTRIBUTION:
- 3 questions: Very Basic (Core definitions, simple facts)
- 5 questions: Basic (Fundamental understanding, simple relationships)
- 2 questions: Intermediate Basic (Slightly deeper understanding, basic application)

**IMPORTANT**: Return your response as a valid JSON object matching this exact structure:

{
  "questions": [
    {
      "question": "Question text here",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": 0,
      "explanation": "Brief explanation of why this answer is correct. Focus on the concept itself rather than referencing external sources. Write complete sentences."
    }
  ],
  "topic": "Specific topic covered (e.g., 'Basic Cell Biology')",
  "subject": "${sessionSubject}",
  "sources": ${credibleSources.length > 0 ? JSON.stringify(credibleSources.slice(0, 3)) : "[]"}
}

🔴 EXPLANATION FORMATTING RULES (CRITICAL):
1. Write COMPLETE, self-contained explanations that explain the concept directly
2. DO NOT reference sources in explanations (e.g., avoid "The provided source states" or "Source 1 says")
3. DO NOT use phrases like "According to..." or "The source mentions..."
4. Focus on explaining WHY the correct answer is right and what makes it different from wrong answers
5. Keep explanations concise (2-3 sentences) but complete
6. Use active voice and direct language (e.g., "Gravity pulls objects toward Earth" instead of "According to physics, gravity...")
7. If you used scraped content for accuracy, incorporate the knowledge directly without citing

🔴 JSON FORMATTING RULES (CRITICAL - MUST FOLLOW):
1. Use ONLY double quotes (") for all strings, NEVER single quotes (')
2. When quoting words or phrases in questions/explanations:
   - First choice: Rephrase to avoid quotes (e.g., "refers to the term AND" instead of "meaning \\"AND\\"")
   - Second choice: Use single quotes for visual distinction (e.g., 'AND' - these will be processed safely)
   - Last resort: Use escaped double quotes (e.g., \\"word\\") - but avoid if possible
3. For LaTeX math formulas and formatting:
   - Use single dollar signs: $\\\\formula$ for inline, $$\\\\formula$$ for display
   - Example: "$\\\\frac{a}{b}$" or "$\\\\sin(\\\\theta)$"
   - For chemical formulas with subscripts: Use LaTeX underscore (e.g., "$H_2O$", "$NH_3$", "$CO_2$")
   - For superscripts: Use LaTeX caret (e.g., "$x^2$", "$10^{-5}$")
   - For emphasis: Use **bold text** in markdown instead of <em> or <i>
   - **ABSOLUTELY FORBIDDEN: <sub>, <sup>, <em>, <i>, <b>, <strong>, <span>, <div>, or ANY HTML tags**
   - **WHY: HTML tags break rendering and display as literal text like "< em >" instead of formatting**
   - **ONLY use LaTeX syntax ($...$) for math/science notation and plain text/markdown for everything else**
4. Safe special characters that need NO escaping:
   - Logical operators: ∧ (AND), ∨ (OR), → (implies), ↔ (if and only if), ~ (NOT)
   - Math operators: ±, ×, ÷, ≠, ≤, ≥
   - Greek letters: α, β, γ, θ, π, Σ
   - Em-dashes: – (use for ranges or emphasis)
5. Contractions and possessives:
   - Write "doesnt" instead of "doesn't", "cant" instead of "can't"
   - Write "Newtons law" instead of "Newton's law"
6. Escape special JSON characters: \\n (newline), \\t (tab), \\r (return)
7. NO trailing commas in arrays or objects
8. Ensure all brackets [ ] and braces { } are properly closed
9. Every opening quote " must have a matching closing quote "
10. Line breaks within strings: Use \\n, NOT literal newlines
11. Backslashes: Use \\\\ for literal backslash (except in LaTeX where single \\ is correct)
12. Test your JSON structure before returning - it MUST parse as valid JSON

🔴 CRITICAL VALIDATION:
- Questions MUST be foundational/basic level (NOT advanced)
- Questions MUST relate to ${sessionSubject}
- Each question MUST have exactly 4 options
- correctAnswer MUST be index (0-3) of the correct option
- All content MUST be factually accurate and educationally appropriate`;

      // ✅ ULTIMATE FIX: Force Gemini to output valid JSON using generationConfig
      // This prevents prose injection and string boundary issues
      const result = await this.withTimeout(
        this.flashcardModel.generateContent({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.1, // Low temperature for consistent, structured output
            topP: 0.95,
            topK: 40,
            maxOutputTokens: 8192,
            responseMimeType: "application/json", // ✅ FORCE JSON OUTPUT
          },
        }),
        60000, // 60 second timeout (same as post-test for 10 questions)
        "Gemini pre-test generation"
      );
      const response = result.response;

      if (response.promptFeedback?.blockReason) {
        throw new Error(`Content was blocked: ${response.promptFeedback.blockReason}`);
      }

      const text = response.text();

      if (!text || text.trim().length === 0) {
        throw new Error("Empty response received from Gemini API");
      }

      logDebug("Received pre-test generation response", {
        responseLength: text.length,
        subject: sessionSubject,
      });

      // ✅ SIMPLIFIED: With responseMimeType="application/json", Gemini outputs valid JSON
      // Only minimal cleanup needed
      try {
        let cleanJsonText = text.trim();

        // Remove markdown code blocks if present (shouldn't be with JSON mime type)
        cleanJsonText = cleanJsonText.replace(/```json\s*/, "").replace(/```\s*$/, "");

        // Extract JSON object
        const jsonMatch = cleanJsonText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          logError("No JSON found in pre-test response", {
            responseLength: text.length,
            preview: text.substring(0, 200),
          });
          throw new Error("No JSON object found in response");
        }

        cleanJsonText = jsonMatch[0];

        // ✅ SIMPLIFIED: Since we're using JSON mode with responseMimeType,
        // we only need minimal cleanup (remove markdown artifacts, normalize whitespace)

        // Remove BOM if present
        cleanJsonText = cleanJsonText.replace(/^\uFEFF/, "");

        // Normalize line endings and excessive whitespace
        cleanJsonText = cleanJsonText.replace(/\r\n/g, " ");
        cleanJsonText = cleanJsonText.replace(/\n/g, " ");
        cleanJsonText = cleanJsonText.replace(/\t/g, " ");

        // Remove control characters
        cleanJsonText = cleanJsonText.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, "");

        // Fix trailing commas (defensive)
        cleanJsonText = cleanJsonText.replace(/,(\s*[\]}])/g, "$1");

        // ✅ PARSE: With JSON mode enabled, Gemini should return valid JSON
        let parsed: PostTestResult | undefined;
        try {
          parsed = JSON.parse(cleanJsonText);
        } catch (parseErr: any) {
          logError("JSON parsing failed even with JSON mode enabled", {
            error: parseErr.message,
            jsonPreview: cleanJsonText.substring(0, 500) + "...",
          });
          throw new Error(`Failed to parse AI response: ${parseErr.message}`);
        }

        // ✅ FIX: Ensure parsed is defined before validation
        if (!parsed) {
          throw new Error("Failed to parse JSON response");
        }

        // ✅ CRITICAL FIX: Remove any HTML tags from questions/options/explanations
        // Converts common HTML formatting to LaTeX equivalents
        // HANDLES SPACED TAGS: Gemini outputs "< em >" with spaces, not "<em>"
        const cleanHTMLTags = (text: string): string => {
          if (!text) return text;

          let cleaned = text;

          // Step 1: Convert subscript/superscript HTML to LaTeX (capture group first)
          // Handles: <sub>2</sub>, < sub >2< /sub >, <sub >2</sub >, etc.
          cleaned = cleaned.replace(/<\s*sub\s*>(.*?)<\s*\/\s*sub\s*>/gis, "_$1");
          cleaned = cleaned.replace(/<\s*sup\s*>(.*?)<\s*\/\s*sup\s*>/gis, "^$1");

          // Step 2: AGGRESSIVE tag removal with multiple passes
          // Pass 1: Remove opening tags with spaces
          cleaned = cleaned.replace(/<\s*em\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*i\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*strong\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*b\s*>/gi, "");

          // Pass 2: Remove closing tags with spaces (separate pass for edge cases)
          cleaned = cleaned.replace(/<\s*\/\s*em\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/\s*i\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/\s*strong\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/\s*b\s*>/gi, "");

          // Pass 3: Handle malformed closing tags like "< /em >" or "</em >"
          cleaned = cleaned.replace(/<\s*\/em\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/i\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/strong\s*>/gi, "");
          cleaned = cleaned.replace(/<\s*\/b\s*>/gi, "");

          // Pass 4: Nuclear option - remove ANY remaining angle bracket pairs
          // This catches: "< em >", "< /em >", "< whatever >"
          cleaned = cleaned.replace(/<[^>]*>/g, "");

          // Step 3: Clean up whitespace artifacts from tag removal
          cleaned = cleaned.replace(/\s+/g, " ").trim();

          return cleaned;
        };

        // Clean all questions, options, and explanations
        if (parsed.questions) {
          // Debug: Sample first question before/after for debugging
          const sampleBefore = parsed.questions[0]?.question || "";
          const hasHTMLTags = /<[^>]*>/g.test(sampleBefore);

          parsed.questions = parsed.questions.map((q) => ({
            ...q,
            question: cleanHTMLTags(q.question),
            options: q.options.map((opt) => cleanHTMLTags(opt)),
            explanation: cleanHTMLTags(q.explanation),
          }));

          const sampleAfter = parsed.questions[0]?.question || "";

          // Log if HTML tags were detected and cleaned
          if (hasHTMLTags || sampleBefore !== sampleAfter) {
            logWarn("🧹 HTML tags detected and cleaned from pre-test questions", {
              hadHTMLTags: hasHTMLTags,
              before: sampleBefore,
              after: sampleAfter,
              tagsCleaned: sampleBefore !== sampleAfter,
            });
          } else {
            logInfo("✅ Pre-test questions clean (no HTML tags detected)");
          }
        }

        // Validation: Check that we have exactly 10 questions
        if (!parsed.questions || parsed.questions.length !== 10) {
          logWarn("Pre-test generated wrong number of questions", {
            expected: 10,
            actual: parsed.questions?.length || 0,
          });
          throw new Error("Pre-test must have exactly 10 questions");
        }

        // Validation: Each question must have exactly 4 options
        for (let i = 0; i < parsed.questions.length; i++) {
          const q = parsed.questions[i];
          if (!q.options || q.options.length !== 4) {
            logError(`Pre-test question ${i + 1} has invalid number of options`, {
              expected: 4,
              actual: q.options?.length || 0,
            });
            throw new Error(`Question ${i + 1} must have exactly 4 options`);
          }
          if (typeof q.correctAnswer !== "number" || q.correctAnswer < 0 || q.correctAnswer > 3) {
            logError(`Pre-test question ${i + 1} has invalid correctAnswer`, {
              value: q.correctAnswer,
            });
            throw new Error(`Question ${i + 1} has invalid correctAnswer (must be 0-3)`);
          }
        }

        // ✅ CRITICAL FIX: Clean and deduplicate sources (same as post-test/flashcards/summaries)
        // Filter out non-string sources from Gemini response
        if (parsed.sources && Array.isArray(parsed.sources)) {
          parsed.sources = parsed.sources
            .filter((source) => source && typeof source === "string")
            .map((source) => this.cleanSourceText(source));
        }

        // Ensure sources are included
        // Pre-test uses learning goals as topic context for validation
        const topicContext = [sessionSubject, learningGoals];

        if (!parsed.sources || parsed.sources.length === 0) {
          // ✅ NEW: Filter off-topic sources from credible sources
          const validatedCredibleSources = credibleSources.slice(0, 3).filter((source) => {
            if (this.isObviouslyOffTopic(source, sessionSubject, topicContext)) {
              logWarn("Filtered off-topic source from pre-test credible sources", {
                source,
                subject: sessionSubject,
              });
              return false;
            }
            return true;
          });
          parsed.sources = validatedCredibleSources;
        } else if (credibleSources.length > 0) {
          // ✅ NEW: Filter off-topic sources before enhancing
          const validatedCredibleSources = credibleSources.slice(0, 3).filter((source) => {
            if (this.isObviouslyOffTopic(source, sessionSubject, topicContext)) {
              logWarn("Filtered off-topic source from pre-test credible sources", {
                source,
                subject: sessionSubject,
              });
              return false;
            }
            return true;
          });

          // Enhance with credible sources and deduplicate
          const enhancedSources = [...parsed.sources];
          validatedCredibleSources.forEach((source) => {
            // Check for duplicates using normalized URLs
            const isDuplicate = enhancedSources.some((s) => {
              try {
                return this.normalizeUrl(s) === this.normalizeUrl(source);
              } catch {
                return s === source || s.includes(source);
              }
            });
            if (!isDuplicate) {
              enhancedSources.push(source);
            }
          });

          // Deduplicate AND filter off-topic Gemini sources
          parsed.sources = enhancedSources.filter((source, index, self) => {
            // Check for duplicates
            const isDuplicate =
              index !==
              self.findIndex((s) => {
                try {
                  return this.normalizeUrl(s) === this.normalizeUrl(source);
                } catch {
                  return s === source;
                }
              });

            if (isDuplicate) return false;

            // ✅ NEW: Check for off-topic sources
            if (this.isObviouslyOffTopic(source, sessionSubject, topicContext)) {
              logWarn("Filtered off-topic Gemini source from pre-test", {
                source,
                subject: sessionSubject,
              });
              return false;
            }

            return true;
          });
        }

        // Set subject and topic
        parsed.subject = sessionSubject;
        if (!parsed.topic) {
          parsed.topic = `Pre-Test: ${sessionSubject}`;
        }

        logSuccess("Pre-test generation completed successfully", {
          questionCount: parsed.questions.length,
          topic: parsed.topic,
          subject: parsed.subject,
          hasCredibleSources: parsed.sources.length > 0,
        });

        // ✅ Normalize chemical notation for safe rendering (pretest only)
        parsed.questions = parsed.questions.map((q) => ({
          question: this.normalizeChemicalNotation(q.question),
          options: q.options.map((opt) => this.normalizeChemicalNotation(opt)),
          correctAnswer: q.correctAnswer,
          explanation: this.normalizeChemicalNotation(q.explanation),
        }));

        return parsed;
      } catch (parseError) {
        // ✅ ENHANCED ERROR LOGGING: Log full Gemini response for debugging
        logError("❌ All JSON parsing attempts failed - FULL PRE-TEST RESPONSE LOGGED", {
          fullResponse: text,
          responseLength: text.length,
          firstChars: text.substring(0, 200),
          lastChars: text.substring(text.length - 200),
          parseError: parseError instanceof Error ? parseError.message : String(parseError),
          timestamp: new Date().toISOString(),
          context: {
            subject: sessionSubject,
            outputLanguage,
            hasCredibleSources: credibleSources.length > 0,
            hasUploadedContent: !!uploadedFileContent,
            learningGoalsLength: learningGoals?.length || 0,
          },
        });

        // ✅ Also log to console.error for Firebase Cloud Logging capture
        console.error("🔴 GEMINI JSON PARSE FAILURE - FULL PRE-TEST RESPONSE:", {
          timestamp: new Date().toISOString(),
          environment: import.meta.env.MODE,
          fullResponse: text,
          responseLength: text.length,
          parseError: parseError instanceof Error ? parseError.message : String(parseError),
          context: `Pre-test for ${sessionSubject}`,
        });

        logError("Failed to parse pre-test JSON response", parseError);
        throw new Error("Failed to generate valid pre-test questions. Please try again.");
      }
    } catch (error: any) {
      logError("Error generating pre-test", error);

      if (error.message?.includes("API_KEY_INVALID")) {
        throw new Error("Invalid API key. Please check your Gemini API key configuration.");
      } else if (error.message?.includes("QUOTA_EXCEEDED")) {
        throw new Error("API quota exceeded. Please try again later or check your API limits.");
      } else if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
        throw new Error("Rate limit exceeded. Please wait a moment and try again.");
      } else if (error.message?.includes("blocked")) {
        throw new Error(
          "Content was blocked by safety filters. Please try with different content."
        );
      } else {
        throw new Error(`Failed to generate pre-test: ${error.message || "Unknown error"}`);
      }
    }
  }

  /**
   * Normalize chemical notation and subscripts for safe rendering
   * Converts HTML-like patterns to Unicode subscripts (e.g., <em>2</em> → ₂)
   * Used ONLY for pretest questions to ensure deterministic, XSS-safe output
   */
  private normalizeChemicalNotation(text: string): string {
    if (!text) return text;

    // Unicode subscript map
    const subscriptMap: { [key: string]: string } = {
      "0": "₀",
      "1": "₁",
      "2": "₂",
      "3": "₃",
      "4": "₄",
      "5": "₅",
      "6": "₆",
      "7": "₇",
      "8": "₈",
      "9": "₉",
    };

    let normalized = text;

    // Convert <em>NUMBER</em> to Unicode subscripts
    normalized = normalized.replace(/<em>(\d+)<\/em>/gi, (_, num) => {
      return num
        .split("")
        .map((digit: string) => subscriptMap[digit] || digit)
        .join("");
    });

    // Convert <sub>NUMBER</sub> to Unicode subscripts
    normalized = normalized.replace(/<sub>(\d+)<\/sub>/gi, (_, num) => {
      return num
        .split("")
        .map((digit: string) => subscriptMap[digit] || digit)
        .join("");
    });

    // Convert _{NUMBER} patterns to Unicode subscripts
    normalized = normalized.replace(/_(\d+)/g, (_, num) => {
      return num
        .split("")
        .map((digit: string) => subscriptMap[digit] || digit)
        .join("");
    });

    // Strip any remaining HTML tags for safety
    normalized = normalized.replace(/<[^>]+>/g, "");

    // Decode common HTML entities
    normalized = normalized.replace(/&lt;/g, "<");
    normalized = normalized.replace(/&gt;/g, ">");
    normalized = normalized.replace(/&amp;/g, "&");
    normalized = normalized.replace(/&quot;/g, '"');
    normalized = normalized.replace(/&#39;/g, "'");

    return normalized;
  }

  /**
   * Build context for pre-test generation from booking information
   */
  private buildPreTestContext(
    subject: string,
    learningGoals: string,
    uploadedFileContent?: string
  ): string {
    let context = `SUBJECT: ${subject}\n\n`;

    if (learningGoals && learningGoals.trim().length > 0) {
      context += `STUDENT'S LEARNING GOALS/NOTES:\n${learningGoals}\n\n`;
    } else {
      context += `STUDENT'S LEARNING GOALS: Not specified (Generate general foundational questions for ${subject})\n\n`;
    }

    if (uploadedFileContent && uploadedFileContent.trim().length > 0) {
      // Truncate file content if too large
      const maxFileLength = 3000;
      const truncatedContent =
        uploadedFileContent.length > maxFileLength
          ? uploadedFileContent.substring(0, maxFileLength) + "\n... [truncated]"
          : uploadedFileContent;

      context += `TUTOR'S UPLOADED MATERIALS (for context):\n${truncatedContent}\n\n`;
    }

    context += `Generate 10 foundational/basic questions that will help assess the student's BASELINE KNOWLEDGE before tutoring begins.`;

    return context;
  }

  /**
   * 🧪 TEST METHOD: Verify complete AI pipeline with min/max enforcement
   *
   * Tests the entire pipeline flow:
   * 1. User Input → Safety Check → HF Analysis
   * 2. Google Search → Jina Rerank → URL Validation
   * 3. Off-topic Filtering → Deduplication
   * 4. Min/Max Enforcement (1-5 sources) with fallback strategies
   * 5. Gemini Generation
   *
   * @example
   * // Call this from browser console:
   * window.testAIPipeline = async () => {
   *   const { getGeminiService } = await import('./services/geminiService');
   *   const result = await getGeminiService().testCompletePipeline();
   *   console.log('Test Results:', result);
   * };
   * window.testAIPipeline();
   */
  async testCompletePipeline() {
    console.log(`\n${"=".repeat(100)}`);
    console.log(`🧪 [AI PIPELINE TEST] Starting comprehensive validation`);
    console.log(`   Testing: Min/Max enforcement, URL validation, off-topic filtering, fallbacks`);
    console.log(`   Date: ${new Date().toLocaleString()}`);
    console.log(`${"=".repeat(100)}\n`);

    const testResults: any = {
      timestamp: new Date().toISOString(),
      tests: [],
      summary: {
        total: 0,
        passed: 0,
        failed: 0,
      },
    };

    // Test Case 1: Math content (should get math sources)
    console.log(`\n📝 Test 1: Math content with subject-matched sources`);
    try {
      const mathContent =
        "Explain quadratic equations and the quadratic formula. Include examples of solving x² + 5x + 6 = 0.";
      const mathResult = await this.generateEnhancedSummaryWithHF(
        mathContent,
        "test-math.txt",
        "english"
      );

      const test1 = {
        name: "Math content with on-topic sources",
        passed:
          mathResult.sources && mathResult.sources.length >= 1 && mathResult.sources.length <= 5,
        sourceCount: mathResult.sources?.length || 0,
        sources: mathResult.sources?.slice(0, 3) || [],
        expectedBehavior: "1-5 math-related sources",
        actualBehavior: `${mathResult.sources?.length || 0} sources returned`,
      };

      testResults.tests.push(test1);
      testResults.summary.total++;
      if (test1.passed) testResults.summary.passed++;
      else testResults.summary.failed++;

      console.log(`   ✅ Result: ${test1.passed ? "PASS" : "FAIL"}`);
      console.log(`   Sources: ${test1.sourceCount} (expected: 1-5)`);
      if (test1.sources.length > 0) {
        console.log(`   Sample sources:`, test1.sources);
      }
    } catch (error) {
      console.error(`   ❌ Test 1 FAILED with error:`, error);
      testResults.tests.push({
        name: "Math content test",
        passed: false,
        error: error instanceof Error ? error.message : String(error),
      });
      testResults.summary.total++;
      testResults.summary.failed++;
    }

    // Test Case 2: Science content (should get science sources)
    console.log(`\n📝 Test 2: Science content with subject-matched sources`);
    try {
      const scienceContent =
        "Describe photosynthesis process in plants. Explain the light-dependent and light-independent reactions.";
      const scienceResult = await this.generateEnhancedSummaryWithHF(
        scienceContent,
        "test-science.txt",
        "english"
      );

      const test2 = {
        name: "Science content with on-topic sources",
        passed:
          scienceResult.sources &&
          scienceResult.sources.length >= 1 &&
          scienceResult.sources.length <= 5,
        sourceCount: scienceResult.sources?.length || 0,
        sources: scienceResult.sources?.slice(0, 3) || [],
        expectedBehavior: "1-5 science-related sources",
        actualBehavior: `${scienceResult.sources?.length || 0} sources returned`,
      };

      testResults.tests.push(test2);
      testResults.summary.total++;
      if (test2.passed) testResults.summary.passed++;
      else testResults.summary.failed++;

      console.log(`   ✅ Result: ${test2.passed ? "PASS" : "FAIL"}`);
      console.log(`   Sources: ${test2.sourceCount} (expected: 1-5)`);
      if (test2.sources.length > 0) {
        console.log(`   Sample sources:`, test2.sources);
      }
    } catch (error) {
      console.error(`   ❌ Test 2 FAILED with error:`, error);
      testResults.tests.push({
        name: "Science content test",
        passed: false,
        error: error instanceof Error ? error.message : String(error),
      });
      testResults.summary.total++;
      testResults.summary.failed++;
    }

    // Test Case 3: Flashcards (should also enforce 1-5 sources)
    console.log(`\n📝 Test 3: Flashcard generation with min/max enforcement`);
    try {
      const flashcardContent = "World War II key events, causes, and major battles.";
      const flashcardResult = await this.generateFlashcards(
        flashcardContent,
        "test-history.txt",
        5,
        "en"
      );

      const test3 = {
        name: "Flashcard generation with source limits",
        passed: true, // Flashcards work differently, just verify no crash
        flashcardCount: flashcardResult.flashcards?.length || 0,
        expectedBehavior: "Generate 5 flashcards with 0-5 sources",
        actualBehavior: `${flashcardResult.flashcards?.length || 0} flashcards generated`,
      };

      testResults.tests.push(test3);
      testResults.summary.total++;
      if (test3.passed) testResults.summary.passed++;
      else testResults.summary.failed++;

      console.log(`   ✅ Result: ${test3.passed ? "PASS" : "FAIL"}`);
      console.log(`   Flashcards: ${test3.flashcardCount} (expected: 5)`);
    } catch (error) {
      console.error(`   ❌ Test 3 FAILED with error:`, error);
      testResults.tests.push({
        name: "Flashcard generation test",
        passed: false,
        error: error instanceof Error ? error.message : String(error),
      });
      testResults.summary.total++;
      testResults.summary.failed++;
    }

    // Test Case 4: Generic content (should handle any subject)
    console.log(`\n📝 Test 4: Generic content with flexible source matching`);
    try {
      const genericContent = "Explain the basics of computer programming and software development.";
      const genericResult = await this.generateEnhancedSummaryWithHF(
        genericContent,
        "test-generic.txt",
        "english"
      );

      const test4 = {
        name: "Generic content with flexible sources",
        passed:
          genericResult.sources &&
          genericResult.sources.length >= 1 &&
          genericResult.sources.length <= 5,
        sourceCount: genericResult.sources?.length || 0,
        sources: genericResult.sources?.slice(0, 3) || [],
        expectedBehavior: "1-5 programming-related sources",
        actualBehavior: `${genericResult.sources?.length || 0} sources returned`,
      };

      testResults.tests.push(test4);
      testResults.summary.total++;
      if (test4.passed) testResults.summary.passed++;
      else testResults.summary.failed++;

      console.log(`   ✅ Result: ${test4.passed ? "PASS" : "FAIL"}`);
      console.log(`   Sources: ${test4.sourceCount} (expected: 1-5)`);
      if (test4.sources.length > 0) {
        console.log(`   Sample sources:`, test4.sources);
      }
    } catch (error) {
      console.error(`   ❌ Test 4 FAILED with error:`, error);
      testResults.tests.push({
        name: "Generic content test",
        passed: false,
        error: error instanceof Error ? error.message : String(error),
      });
      testResults.summary.total++;
      testResults.summary.failed++;
    }

    // Final Summary
    console.log(`\n${"=".repeat(100)}`);
    console.log(`🧪 [AI PIPELINE TEST] Test suite completed`);
    console.log(`   Total tests: ${testResults.summary.total}`);
    console.log(`   ✅ Passed: ${testResults.summary.passed}`);
    console.log(`   ❌ Failed: ${testResults.summary.failed}`);
    console.log(
      `   Success rate: ${((testResults.summary.passed / testResults.summary.total) * 100).toFixed(
        1
      )}%`
    );
    console.log(`${"=".repeat(100)}\n`);

    // Log detailed results
    logInfo("AI Pipeline test suite completed", {
      total: testResults.summary.total,
      passed: testResults.summary.passed,
      failed: testResults.summary.failed,
      successRate: `${((testResults.summary.passed / testResults.summary.total) * 100).toFixed(
        1
      )}%`,
      timestamp: testResults.timestamp,
    });

    return testResults;
  }
}

// Lazy singleton instance to avoid circular dependency issues
let geminiServiceInstance: GeminiService | null = null;

export function getGeminiService(): GeminiService {
  if (!geminiServiceInstance) {
    geminiServiceInstance = new GeminiService();
  }
  return geminiServiceInstance;
}

// Lazy singleton export using Proxy for backward compatibility
// ✅ FIX: Delay Proxy creation to avoid module-level evaluation
let _geminiProxyCache: GeminiService | null = null;

function getGeminiProxyInstance(): GeminiService {
  if (!_geminiProxyCache) {
    _geminiProxyCache = new Proxy({} as GeminiService, {
      get(target, prop) {
        return getGeminiService()[prop as keyof GeminiService];
      },
    }) as GeminiService;
  }
  return _geminiProxyCache;
}

export const geminiService: GeminiService = getGeminiProxyInstance();
